import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Modal from '../components/Modal';
import api from '../api';
import { CONTACT_CAT_ORDER } from '../constants';

const PdfIcon = ({ size = 24 }) => (
  <img src="/pdf-icon.png" alt="PDF" width={size} height={size} style={{ objectFit: 'contain', display: 'block' }} />
);
const EyeIcon = ({ size = 18, color = '#374151' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const DownloadIcon = ({ size = 18, color = '#374151' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12" />
    <path d="M7 10l5 5 5-5" />
    <path d="M5 20h14" />
  </svg>
);

// ── Constants ────────────────────────────────────────────────────────────────

const PROJECT_LOGOS = {
  'ד החדשה': '/logos/aura_logo.jpg',
  'אייזיקחריף': '/logos/jaffag2.png',
  'ג החדשה': '/logos/jaffag2.png',
  'סקייליין בת ים': '/logos/aura_logo.jpg',
  'ראשון החדשה': '/logos/rishon.jpg',
  'אפלטון 6': '/logos/aplaton6.jpeg',
  'שארית ישראל': '/logos/sherit_israel.jpg',
};

// "ג החדשה" is the literal project_name in the DB (used for API calls and
// matching) — this only changes what's shown to the user, e.g. in headers.
const PROJECT_DISPLAY_NAMES = { 'ג החדשה': "ג' החדשה", 'אייזיקחריף': 'אייזיק חריף' };
const displayProjectName = n => PROJECT_DISPLAY_NAMES[n] || n;

const CATEGORY_ORDER = ['תב"ע', 'תכנית עיצוב אדריכלי', 'היתר בנייה'];
const CATEGORY_COLORS = {
  'תב"ע': { header: '#7c3aed', bar: '#8b5cf6' },
  'תכנית עיצוב אדריכלי': { header: '#1d4ed8', bar: '#3b82f6' },
  'היתר בנייה': { header: '#5B4FE8', bar: '#7c6ff0' },
};
const STATUS_LABELS = { pending: 'טרם התחיל', active: 'בביצוע', completed: 'הושלם' };
const STATUS_COLORS = {
  pending: { bg: '#f3f4f6', text: '#6b7280', dot: '#d1d5db' },
  active: { bg: '#dbeafe', text: '#1d4ed8', dot: '#3b82f6' },
  completed: { bg: '#dcfce7', text: '#15803d', dot: '#22c55e' },
};

const TABS = [
  { id: 'dashboard', label: '📊 דשבורד פעילות' },
  { id: 'home', label: '🏠 דף הבית' },
  { id: 'new-comp', label: '✨ תמורה לדירה החדשה' },
  { id: 'apartment', label: '🏡 הדירה שלי' },
  { id: 'stages', label: '📋 שלבים' },
  { id: 'announcements', label: '📢 הודעות' },
  { id: 'gallery', label: '📷 גלריית תמונות' },
  { id: 'contacts', label: '📞 אנשי קשר' },
  { id: 'files', label: '📁 קבצים' },
  { id: 'tenant-lookup', label: '🔍 מעקב צפייה בדייר', adminOnly: true },
  { id: 'tasks', label: '✅ משימות' },
  { id: 'about-manhelet', label: '🏢 אודות המנהלת' },
  { id: 'about', label: 'ℹ️ אודות היזם' },
  { id: 'ai-chat', label: '🤖 עוזר AI' },
];

// Files here are hosted on S3 (cross-origin from this admin site) — the
// native <a download> attribute is silently ignored by browsers for
// cross-origin URLs, so clicking "הורד" just opened the file instead of
// downloading it. Fetching as a blob and saving via an object URL works
// regardless of origin.
async function downloadCrossOriginFile(url, filename) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename || 'file';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  } catch {
    window.open(url, '_blank');
  }
}

function getWorkerInfo() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return { role: 'project_manager', name: '' };
    const p = JSON.parse(decodeURIComponent(escape(atob(token.split('.')[1]))));
    return { role: p.role || 'project_manager', name: `${p.workerFirstName || ''} ${p.workerLastName || ''}`.trim(), userName: p.userName || '' };
  } catch { return { role: 'project_manager', name: '', userName: '' }; }
}

// ── Tenant Lookup Tab (admin) — search a resident/contact/building, view their portal ──

function normalizeSearch(s) {
  return String(s || '').replace(/[\s\-]/g, '').toLowerCase();
}

function TenantLookupTab({ projectName }) {
  const [allTenants, setAllTenants] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState(null); // { buildingAddress, buildingNumber }
  const [viewingId, setViewingId] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/portal/tenants-list/${encodeURIComponent(projectName)}`).then(r => setAllTenants(r.data || [])).catch(() => {});
  }, [projectName]);

  const buildingsList = React.useMemo(() => {
    const map = new Map();
    allTenants.forEach(t => {
      const key = `${t.buildingAddress}|${t.buildingNumber}`;
      if (!map.has(key)) map.set(key, { buildingAddress: t.buildingAddress, buildingNumber: t.buildingNumber, count: 0 });
      map.get(key).count++;
    });
    return [...map.values()].sort((a, b) => (a.buildingAddress + a.buildingNumber).localeCompare(b.buildingAddress + b.buildingNumber));
  }, [allTenants]);

  const byAptNum = (a, b) => (parseFloat(a.apartmentNumber) || 0) - (parseFloat(b.apartmentNumber) || 0);
  const q = normalizeSearch(query);
  const buildingMatches = q ? buildingsList.filter(b => normalizeSearch(b.buildingAddress + b.buildingNumber).includes(q)) : [];
  const tenantMatches = q
    ? allTenants.filter(t => t.hasPortalAccess && (normalizeSearch(`${t.firstName}${t.lastName}`).includes(q) || (t.cellularPhone && normalizeSearch(t.cellularPhone).includes(q)))).sort(byAptNum)
    : [];
  const buildingTenants = selectedBuilding
    ? allTenants.filter(t => t.hasPortalAccess && t.buildingAddress === selectedBuilding.buildingAddress && String(t.buildingNumber) === String(selectedBuilding.buildingNumber)).sort(byAptNum)
    : [];

  async function viewAsTenant(tenant) {
    setErr(''); setViewingId(tenant.id);
    // iOS Safari blocks window.open after an await; open the tab synchronously
    // on the tap, then point it at the real URL once the token arrives.
    const win = window.open('', '_blank');
    try {
      const r = await api.get('/portal/admin-view-as-token', { params: { tenantId: tenant.id } });
      const qs = new URLSearchParams({
        token: r.data.token, phone: r.data.phone, name: r.data.name, projectName: r.data.projectName,
        buildingAddress: r.data.buildingAddress, buildingNumber: r.data.buildingNumber, apartmentNumber: r.data.apartmentNumber,
      });
      const target = `/admin-view-as?${qs.toString()}`;
      if (win) win.location.href = target;
      else window.location.href = target;
    } catch (e) {
      if (win) win.close();
      setErr(e?.response?.data?.error || 'שגיאה בפתיחת תצוגת דייר');
    }
    setViewingId(null);
  }

  function TenantRow({ t }) {
    if (!t.hasPortalAccess) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 700, color: '#1f2937' }}>{t.firstName} {t.lastName}</div>
          <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>{t.cellularPhone || 'ללא טלפון'} · {t.buildingAddress} {t.buildingNumber} · דירה {t.apartmentNumber}</div>
        </div>
        <button onClick={() => viewAsTenant(t)} disabled={viewingId === t.id} style={{ background: '#1B2A4A', color: 'white', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
          {viewingId === t.id ? 'פותח...' : '👁 צפה כדייר'}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb', marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937', marginBottom: 12 }}>🔍 מעקב צפייה בדייר</div>
        <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 12 }}>חפש שם פרטי/משפחה, טלפון, או כתובת בניין — כדי לראות בדיוק מה הדייר רואה כשהוא נכנס לפורטל.</div>
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setSelectedBuilding(null); }}
          placeholder="חיפוש לפי שם, טלפון או בניין..."
          style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.95rem', boxSizing: 'border-box' }}
        />
        {err && <div style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 8 }}>{err}</div>}
      </div>

      {selectedBuilding ? (
        <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontWeight: 700, color: '#1f2937' }}>{selectedBuilding.buildingAddress} {selectedBuilding.buildingNumber} — {buildingTenants.length} דיירים</div>
            <button onClick={() => setSelectedBuilding(null)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', color: '#6b7280', cursor: 'pointer' }}>← חזרה לחיפוש</button>
          </div>
          {buildingTenants.map(t => <TenantRow key={`${t.id}-${t.apartmentNumber}`} t={t} />)}
        </div>
      ) : q ? (
        <>
          {buildingMatches.length > 0 && (
            <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: 12 }}>בניינים</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                {buildingMatches.map(b => (
                  <button key={`${b.buildingAddress}-${b.buildingNumber}`} onClick={() => setSelectedBuilding(b)}
                    style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 10px', cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, color: '#1f2937' }}>{b.buildingAddress} {b.buildingNumber}</div>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 4 }}>{b.count} דיירים</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {tenantMatches.length > 0 && (
            <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
              <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: 12 }}>דיירים</div>
              {tenantMatches.map(t => <TenantRow key={`${t.id}-${t.apartmentNumber}`} t={t} />)}
            </div>
          )}
          {buildingMatches.length === 0 && tenantMatches.length === 0 && (
            <div style={{ color: '#6b7280', textAlign: 'center', padding: 24 }}>לא נמצאו תוצאות</div>
          )}
        </>
      ) : null}
    </div>
  );
}

// ── Home Tab (admin) ─────────────────────────────────────────────────────────

const STAGE_HIERARCHY = {
  'תכנון ואישור התב"ע מול העירייה': ['תכנון התב"ע מול העירייה', 'פורום מהנדס העיר (תב"ע)', 'סבב הערות לתוכנית'],
  'תכנון ואישור התבע מול העירייה': ['תכנון התב"ע מול העירייה', 'פורום מהנדס העיר (תב"ע)', 'סבב הערות לתוכנית'],
  'דיון להפקדה בועדה המקומית ומתן תוקף': ['הפקדה להתנגדויות', 'אישור התכנית למתן תוקף'],
};
const ALL_CHILD_NAMES = new Set(Object.values(STAGE_HIERARCHY).flat());

function DraggableImage({ src, pos, onPosChange }) {
  const ref = React.useRef(null);
  const drag = React.useRef(null);
  return (
    <div
      ref={ref}
      style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 6, border: '2px solid #d1d5db',
        cursor: 'grab', touchAction: 'none', userSelect: 'none' }}
      onPointerDown={e => {
        e.preventDefault();
        const el = ref.current;
        el.setPointerCapture(e.pointerId);
        const [sx, sy] = pos.split(' ').map(v => parseFloat(v));
        const rect = el.getBoundingClientRect();
        drag.current = { sx, sy, x0: e.clientX, y0: e.clientY, rect };
        el.style.cursor = 'grabbing';
      }}
      onPointerMove={e => {
        if (!drag.current) return;
        e.preventDefault();
        const { sx, sy, x0, y0, rect } = drag.current;
        const nx = Math.max(0, Math.min(100, sx + ((e.clientX - x0) / rect.width) * -100));
        const ny = Math.max(0, Math.min(100, sy + ((e.clientY - y0) / rect.height) * -100));
        ref.current.querySelector('img').style.objectPosition = `${Math.round(nx)}% ${Math.round(ny)}%`;
      }}
      onPointerUp={e => {
        if (!drag.current) return;
        ref.current.style.cursor = 'grab';
        const img = ref.current.querySelector('img');
        onPosChange(img.style.objectPosition);
        drag.current = null;
      }}
      onPointerCancel={() => { drag.current = null; }}
    >
      <img src={src} alt="" draggable={false}
        style={{ width: '100%', height: 380, objectFit: 'cover', display: 'block',
          objectPosition: pos, pointerEvents: 'none' }}
        onError={e => e.target.style.display = 'none'}
      />
    </div>
  );
}

const HOME_IMAGES_MAX = 10;

function HomeTab({ projectName }) {
  const [images, setImages] = useState([]); // [{ url, pos }]
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState({});
  const [dragging, setDragging] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [pickerSlot, setPickerSlot] = useState(null);

  useEffect(() => {
    api.get(`/portal/home-settings/${encodeURIComponent(projectName)}`).then(r => setImages(r.data?.images || [])).catch(() => {});
    api.get(`/portal/gallery/${encodeURIComponent(projectName)}`).then(r => setGallery(r.data || [])).catch(() => {});
  }, [projectName]);

  async function persist(next) {
    await api.post(`/portal/home-settings/${encodeURIComponent(projectName)}`, { images: next }).catch(() => {});
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  }

  async function save() {
    setSaving(true);
    await persist(images);
    setSaving(false);
  }

  function imgUrl(url) { return url; }

  async function uploadFile(slot, file) {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(u => ({ ...u, [slot]: true }));
    const fd = new FormData(); fd.append('image', file);
    try {
      const r = await api.post('/portal/home-image-upload', fd);
      const next = [...images];
      if (slot < next.length) next[slot] = { ...next[slot], url: r.data.url };
      else next.push({ url: r.data.url, pos: '50% 50%' });
      setImages(next);
      await persist(next);
    } catch (err) {
      alert(err?.response?.data?.error || 'שגיאה בהעלאת התמונה');
    }
    setUploading(u => ({ ...u, [slot]: false }));
  }

  function onDrop(slot, e) {
    e.preventDefault(); setDragging(null);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(slot, file);
  }

  function removeSlot(slot) {
    const next = images.filter((_, i) => i !== slot);
    setImages(next);
    persist(next);
  }

  const slots = images.length < HOME_IMAGES_MAX ? [...images, { url: '' }] : images;

  return (
    <div>
      <h3 style={{ marginBottom: 4, fontWeight: 700 }}>עד {HOME_IMAGES_MAX} תמונות לדף הבית של הדיירים</h3>
      <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: 16 }}>גרור תמונה לאזור, לחץ לבחירה מהמחשב, או בחר מהגלריה. הדייר יראה אותן כקרוסלה שמתחלפת אוטומטית.</p>
      {/* Full-width, one per row — matches the tenant carousel's actual wide
          aspect ratio (100% width × fixed height). A grid of 3 narrow columns
          here made the crop tool show a completely different aspect ratio
          than what tenants actually see, so positioning done here didn't
          match the real result. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 20 }}>
        {slots.map((img, n) => {
          const url = img.url;
          const isDrag = dragging === n;
          const isNewSlot = n >= images.length;
          return (
            <div key={n} style={{ background: 'white', borderRadius: 12, padding: 14, border: '1px solid #e5e7eb' }}>
              <div style={{ fontWeight: 600, marginBottom: 10, color: '#374151' }}>תמונה {n + 1}</div>

              {/* Image with drag-to-reposition */}
              {url ? (
                <DraggableImage
                  src={imgUrl(url)}
                  pos={img.pos || '50% 50%'}
                  onPosChange={p => { const next = images.map((im, i) => i === n ? { ...im, pos: p } : im); setImages(next); persist(next); }}
                />
              ) : null}

              {/* Drop zone — only shown when no image */}
              {!url && (
                <label
                  onDragOver={e => { e.preventDefault(); setDragging(n); }}
                  onDragLeave={() => setDragging(null)}
                  onDrop={e => onDrop(n, e)}
                  style={{
                    display: 'block', cursor: 'pointer', borderRadius: 10, overflow: 'hidden',
                    border: isDrag ? '2px dashed #7c3aed' : '2px dashed #d1d5db',
                    background: isDrag ? '#f5f3ff' : '#fafafa',
                    marginBottom: 10, position: 'relative',
                  }}>
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) uploadFile(n, e.target.files[0]); }} />
                  {uploading[n] ? (
                    <div style={{ height: isNewSlot ? 140 : 380, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed', fontSize: '0.9rem' }}>מעלה...</div>
                  ) : (
                    <div style={{ height: isNewSlot ? 140 : 380, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#9ca3af' }}>
                      <span style={{ fontSize: '2rem' }}>{isNewSlot ? '➕' : '📷'}</span>
                      <span style={{ fontSize: '0.78rem' }}>{isNewSlot ? 'הוסף תמונה' : 'גרור או לחץ לבחירה'}</span>
                    </div>
                  )}
                </label>
              )}
              {!url && (
                <button onClick={() => setPickerSlot(n)} style={{ width: '100%', marginTop: 6, padding: '6px 10px', background: '#f3f4f6', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', color: '#374151' }}>
                  🖼 בחר מהגלריה
                </button>
              )}

              {url && <div style={{ fontSize: '0.72rem', color: '#9ca3af', textAlign: 'center', marginBottom: 6 }}>✋ גרור לשינוי מיקום</div>}
              {url && (
                <div style={{ display: 'flex', gap: 6, marginBottom: 0 }}>
                  <label style={{ flex: 1, padding: '6px 10px', background: '#f3f4f6', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', color: '#374151', textAlign: 'center' }}>
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) uploadFile(n, e.target.files[0]); }} />
                    🔄 החלף
                  </label>
                  <button onClick={() => removeSlot(n)} style={{ flex: 1, padding: '6px 10px', background: '#fee2e2', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', color: '#dc2626' }}>✕ הסר</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button onClick={save} disabled={saving} style={{ background: '#1B2A4A', color: 'white', border: 'none', padding: '10px 28px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem' }}>
        {saved ? '✓ נשמר' : saving ? 'שומר...' : 'שמור'}
      </button>

      {pickerSlot !== null && (
        <div onClick={() => setPickerSlot(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, padding: 24, maxWidth: 720, width: '90%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>בחר תמונה {pickerSlot + 1}</h3>
              <button onClick={() => setPickerSlot(null)} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>
            {gallery.length === 0 && <p style={{ color: '#9ca3af', textAlign: 'center' }}>אין תמונות בגלריה</p>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
              {gallery.map(g => (
                <img key={g.id} src={imgUrl(g.url)} alt={g.caption || ''} onClick={() => {
                  const next = [...images];
                  if (pickerSlot < next.length) next[pickerSlot] = { ...next[pickerSlot], url: g.url };
                  else next.push({ url: g.url, pos: '50% 50%' });
                  setImages(next); persist(next); setPickerSlot(null);
                }}
                  style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: '2px solid transparent', transition: 'border .1s' }}
                  onMouseOver={e => e.target.style.border = '2px solid #7c3aed'}
                  onMouseOut={e => e.target.style.border = '2px solid transparent'}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Stages Tab ───────────────────────────────────────────────────────────────

function StagesTab({ projectName, logo }) {
  const [stages, setStages] = useState([]);
  const [overall, setOverall] = useState(0);
  const [expanded, setExpanded] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [editStage, setEditStage] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'תב"ע', status: 'pending', completion_pct: 0, target_date: '', order: 1, description: '' });

  const load = useCallback(async () => {
    const [s, o] = await Promise.all([
      api.get(`/portal/stages/${encodeURIComponent(projectName)}`),
      api.get(`/portal/overall/${encodeURIComponent(projectName)}`),
    ]);
    setStages(s.data);
    setOverall(o.data.overall_pct);
  }, [projectName]);

  useEffect(() => { load(); }, [load]);

  function openAdd() {
    setForm({ name: '', category: 'תב"ע', status: 'pending', completion_pct: 0, target_date: '', order: 1, description: '' });
    setEditStage(null);
    setShowAdd(true);
  }

  function openEdit(stage) {
    setForm({
      name: stage.name, category: stage.category || 'תב"ע', status: stage.status,
      completion_pct: stage.completion_pct, target_date: stage.target_date || '',
      order: stage.order, description: stage.description || '',
    });
    setEditStage(stage);
    setShowAdd(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (editStage) {
      await api.patch(`/portal/stages/${editStage.id}`, form);
    } else {
      await api.post(`/portal/stages/${encodeURIComponent(projectName)}`, form);
    }
    setShowAdd(false);
    load();
  }

  async function handleDelete(id) {
    if (!confirm('למחוק שלב זה?')) return;
    await api.delete(`/portal/stages/${id}`);
    load();
  }

  const grouped = CATEGORY_ORDER.map(cat => ({
    cat, items: stages.filter(s => s.category === cat).sort((a, b) => a.order - b.order),
  })).filter(g => g.items.length > 0);
  const uncategorized = stages.filter(s => !s.category || !CATEGORY_ORDER.includes(s.category));

  return (
    <div>
      {/* Overall progress */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontWeight: 700, fontSize: '1rem' }}>התקדמות כללית</span>
          <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1B2A4A' }}>{overall}%</span>
        </div>
        <div style={{ position: 'relative', background: '#e5e7eb', borderRadius: 99, height: 12, margin: '36px 0 14px', overflow: 'visible' }}>
          <div style={{ position: 'absolute', right: 0, width: `${overall}%`, height: '100%', background: 'linear-gradient(90deg,#3b82f6,#1d4ed8)', borderRadius: 99, transition: 'width .4s' }} />
          <div style={{ position: 'absolute', top: '50%', left: `clamp(32px, ${100 - overall}%, calc(100% - 32px))`, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
            {logo
              ? <img src={logo} alt="" style={{ width: 100, height: 100, borderRadius: '50%', border: '4px solid white', objectFit: 'contain', background: 'white', boxShadow: '0 3px 12px rgba(0,0,0,0.18)' }} />
              : <div style={{ width: 64, height: 64, borderRadius: '50%', border: '4px solid white', background: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '1rem', boxShadow: '0 3px 12px rgba(0,0,0,0.18)' }}>{overall}%</div>
            }
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button className="btn btn-primary" onClick={openAdd}>+ הוסף שלב</button>
      </div>

      {[...grouped.map(({ cat, items }) => {
        const col = CATEGORY_COLORS[cat] || { header: '#374151', bar: '#6b7280' };
        const pct = items.length ? Math.round((items.filter(s => s.status === 'completed').length / items.length) * 100) : 0;
        return (
          <div key={cat} style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <div style={{ background: col.header, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#fff', fontWeight: 700 }}>{cat}</span>
            </div>
            <div style={{ height: 4, background: '#e5e7eb' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: col.bar }} />
            </div>
            <div style={{ padding: 12 }}>
              {(() => {
                // ד' החדשה wants a flat 5-step תב"ע list, no parent/child grouping —
                // the STAGE_HIERARCHY name-matching applies globally otherwise.
                const useHierarchy = projectName !== 'ד החדשה';
                let visIdx = 0;
                return items.map(stage => {
                  if (useHierarchy && ALL_CHILD_NAMES.has(stage.name) && items.some(s => (STAGE_HIERARCHY[s.name] || []).includes(stage.name))) return null;
                  const childNames = useHierarchy ? (STAGE_HIERARCHY[stage.name] || []) : [];
                  const childStages = childNames.map(n => items.find(s => s.name === n)).filter(Boolean);
                  const isExp = !!expanded[stage.id];
                  const idx = visIdx++;
                  return (
                    <div key={stage.id}>
                      {renderStageRow(stage, idx, openEdit, handleDelete, {
                        childCount: childStages.length,
                        isExpanded: isExp,
                        toggleExpand: () => setExpanded(e => ({ ...e, [stage.id]: !e[stage.id] })),
                      })}
                      {isExp && childStages.map((child, ci) => (
                        <div key={child.id} style={{ paddingRight: 20, borderRight: '2px solid #e5e7eb', marginRight: 14 }}>
                          {renderStageRow(child, ci, openEdit, handleDelete, { isChild: true })}
                        </div>
                      ))}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        );
      }),
      uncategorized.length > 0 && (
        <div key="uncat" style={{ background: '#fff', borderRadius: 16, padding: 12, marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <div style={{ fontWeight: 600, color: '#6b7280', marginBottom: 8, fontSize: '0.85rem' }}>שלבים נוספים</div>
          {uncategorized.map((stage, idx) => renderStageRow(stage, idx, openEdit, handleDelete))}
        </div>
      )]}

      {showAdd && (
        <Modal title={editStage ? 'עריכת שלב' : 'הוספת שלב'} onClose={() => setShowAdd(false)}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>שם השלב *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required autoFocus />
            </div>
            <div className="form-group">
              <label>קטגוריה</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {CATEGORY_ORDER.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>סטטוס</label>
              <select value={form.status} onChange={e => {
                const s = e.target.value;
                setForm(f => ({ ...f, status: s, completion_pct: s === 'completed' ? 100 : f.completion_pct }));
              }}>
                <option value="pending">טרם התחיל</option>
                <option value="active">בביצוע</option>
                <option value="completed">הושלם</option>
              </select>
            </div>
            <div className="form-group">
              <label>אחוז השלמה ({form.completion_pct}%)</label>
              <input type="range" min={0} max={100} value={form.completion_pct} onChange={e => {
                const pct = +e.target.value;
                setForm(f => ({
                  ...f,
                  completion_pct: pct,
                  status: pct === 100 ? 'completed' : pct > 0 && f.status === 'pending' ? 'active' : pct === 0 && f.status === 'active' ? 'pending' : f.status,
                }));
              }} />
            </div>
            <div className="form-group">
              <label>הערות</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" className="btn btn-primary">{editStage ? 'שמור' : 'הוסף'}</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>ביטול</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function renderStageRow(stage, idx, onEdit, onDelete, opts = {}) {
  const { childCount = 0, isExpanded = false, toggleExpand, isChild = false } = opts;
  const cfg = STATUS_COLORS[stage.status] || STATUS_COLORS.pending;
  return (
    <div key={stage.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: cfg.dot, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: stage.status === 'completed' ? '0.8rem' : isChild ? '1.2rem' : '0.95rem', fontWeight: 700, flexShrink: 0 }}>
        {stage.status === 'completed' ? '✓' : isChild ? '•' : idx + 1}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{stage.name}</span>
            {childCount > 0 && (
              <button onClick={toggleExpand} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '1.4rem', padding: 0, lineHeight: 1, flexShrink: 0, display: 'inline-block' }}>
                {isExpanded ? '▲' : '▼'}
              </button>
            )}
          </div>
          <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 99, background: cfg.bg, color: cfg.text }}>{STATUS_LABELS[stage.status]}</span>
        </div>
        {stage.description && <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2 }}>{stage.description}</div>}
        {stage.target_date && <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 2 }}>📅 יעד: {new Date(stage.target_date).toLocaleDateString('he-IL')}</div>}
        <div style={{ marginTop: 4 }}>
          <div style={{ background: '#e5e7eb', borderRadius: 99, height: 6, width: '100%' }}>
            <div style={{ width: `${stage.completion_pct}%`, height: '100%', background: cfg.dot, borderRadius: 99 }} />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        <button onClick={() => onEdit(stage)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '0.8rem' }}>✏️</button>
        <button onClick={() => onDelete(stage.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '0.8rem' }}>🗑️</button>
      </div>
    </div>
  );
}

// ── Tenant Search (for specific recipient) ───────────────────────────────────

function TenantMultiSearch({ projectName, selectedTenants, onAdd, onRemove }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!query) { setResults([]); return; }
    const timer = setTimeout(() => {
      api.get(`/portal/tenants-by-project/${encodeURIComponent(projectName)}?q=${encodeURIComponent(query)}`)
        .then(r => setResults(r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [query, projectName]);

  const selectedPhones = new Set(selectedTenants.map(t => t.phone));

  return (
    <div className="form-group">
      <label>חפש דייר</label>
      {selectedTenants.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {selectedTenants.map(t => (
            <div key={t.phone} style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#dbeafe', borderRadius: 20, padding: '4px 10px 4px 6px', fontSize: '0.82rem', color: '#1d4ed8' }}>
              <button type="button" onClick={() => onRemove(t.phone)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: '0.9rem', padding: 0, lineHeight: 1 }}>✕</button>
              <span style={{ fontWeight: 600 }}>{t.name}</span>
              <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>{t.address}</span>
            </div>
          ))}
        </div>
      )}
      <input
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder={selectedTenants.length > 0 ? 'הוסף דייר נוסף...' : 'הקלד שם דייר...'}
        autoComplete="off"
      />
      {results.length > 0 && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 4, background: '#fff', maxHeight: 200, overflowY: 'auto' }}>
          {results.map((t, i) => {
            const phone = (t.cellularPhone || '').replace(/[\s\-]/g, '');
            const already = selectedPhones.has(phone);
            const addr = t.buildingAddress ? `${t.buildingAddress}${t.buildingNumber ? ` ${t.buildingNumber}` : ''}${t.apartmentNumber ? ` דירה ${t.apartmentNumber}` : ''}` : '';
            return (
              <div key={i}
                onClick={() => {
                  if (already) return;
                  onAdd({ name: `${t.firstName} ${t.lastName}`, phone, address: addr });
                  setQuery(''); setResults([]);
                }}
                style={{ padding: '8px 12px', cursor: already ? 'default' : 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '0.9rem', opacity: already ? 0.45 : 1 }}
                onMouseOver={e => { if (!already) e.currentTarget.style.background = '#f0f9ff'; }}
                onMouseOut={e => e.currentTarget.style.background = '#fff'}
              >
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {t.firstName} {t.lastName}
                  {already && <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>✓ נבחר</span>}
                </div>
                <div style={{ color: '#6b7280', fontSize: '0.78rem', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span>📞 {t.cellularPhone}</span>
                  {addr && <span>📍 {addr}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TenantSearch({ projectName, onSelect, selectedLabel }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!query) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/portal/tenants-by-project/${encodeURIComponent(projectName)}?q=${encodeURIComponent(query)}`)
        .then(r => setResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query, projectName]);
  return (
    <div className="form-group">
      <label>חפש דייר</label>
      {selectedLabel && <div style={{ marginBottom: 6, padding: '4px 10px', background: '#dbeafe', borderRadius: 8, color: '#1d4ed8', fontSize: '0.85rem', display: 'inline-block' }}>✓ נבחר: {selectedLabel}</div>}
      <input value={query} onChange={e => setQuery(e.target.value)} placeholder="הקלד שם דייר..." autoComplete="off" />
      {results.length > 0 && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 4, background: '#fff', maxHeight: 180, overflowY: 'auto' }}>
          {results.map((t, i) => (
            <div key={i} onClick={() => { onSelect(t); setQuery(''); setResults([]); }}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '0.9rem' }}
              onMouseOver={e => e.currentTarget.style.background = '#f0f9ff'}
              onMouseOut={e => e.currentTarget.style.background = '#fff'}>
              <div style={{ fontWeight: 600 }}>{t.firstName} {t.lastName}</div>
              <div style={{ color: '#6b7280', fontSize: '0.78rem', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span>📞 {t.cellularPhone}</span>
                {t.buildingAddress && <span>📍 {t.buildingAddress}{t.buildingNumber ? ` ${t.buildingNumber}` : ''}{t.apartmentNumber ? ` דירה ${t.apartmentNumber}` : ''}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Announcements Tab ────────────────────────────────────────────────────────

function AnnouncementForm({ form, setForm, projectName, onSubmit, onClose, submitLabel }) {
  const [managers, setManagers] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  useEffect(() => {
    api.get('/portal/workers').then(r => setManagers(r.data)).catch(() => {});
  }, []);

  async function handleAttachFile(file) {
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/portal/announcement-file', fd);
      setForm(f => ({ ...f, file_url: r.data.url, file_name: r.data.name }));
    } catch { alert('שגיאה בהעלאת הקובץ'); }
    setUploadingFile(false);
  }

  const selectedManagers = form.selectedManagers || [];
  function toggleManager(userName) {
    const cur = form.selectedManagers || [];
    const next = cur.includes(userName) ? cur.filter(u => u !== userName) : [...cur, userName];
    setForm(f => ({ ...f, selectedManagers: next }));
  }

  return (
    <form onSubmit={onSubmit}>
      <div className="form-group">
        <label>כותרת *</label>
        <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required autoFocus />
      </div>
      <div className="form-group">
        <label>תוכן</label>
        <textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={4} />
      </div>
      <div className="form-group">
        <label>קובץ מצורף (למשל המכתב עצמו)</label>
        {form.file_url ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px' }}>
            <a href={form.file_url} target="_blank" rel="noreferrer" style={{ color: '#15803d', fontWeight: 600, fontSize: '0.88rem' }}>📎 {form.file_name}</a>
            <button type="button" onClick={() => setForm(f => ({ ...f, file_url: null, file_name: null }))} style={{ marginRight: 'auto', background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.82rem' }}>הסר</button>
          </div>
        ) : (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: uploadingFile ? 'wait' : 'pointer', background: uploadingFile ? '#9ca3af' : '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe', padding: '7px 14px', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600 }}>
            {uploadingFile ? '⏳ מעלה...' : '📎 צרף קובץ'}
            <input type="file" disabled={uploadingFile} style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) handleAttachFile(e.target.files[0]); e.target.value = ''; }} />
          </label>
        )}
      </div>
      <div className="form-group">
        <label>קטגוריה</label>
        {(form.recipient === 'specific' || form.recipient.startsWith('specific:')) ? (
          <div style={{ padding: '8px 12px', background: '#fef3c7', borderRadius: 8, fontSize: '0.9rem', color: '#92400e' }}>👤 הודעה אישית</div>
        ) : (
          <select value={form.category} onChange={e => {
            const cat = e.target.value;
            setForm(f => ({
              ...f,
              category: cat,
              recipient: cat === 'אישי' && (f.recipient === 'all' || f.recipient === 'tenants') ? 'specific' : f.recipient
            }));
          }}>
            <option value="כללי">📢 הודעה כללית</option>
            <option value="אישי">👤 הודעה אישית</option>
          </select>
        )}
      </div>
      <div className="form-group">
        <label>עדיפות</label>
        <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
          <option value="normal">רגיל</option>
          <option value="urgent">דחוף</option>
        </select>
      </div>
      <div className="form-group">
        <label>תאריך ושעת פרסום</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', direction: 'ltr' }}>
          <input type="number" min="0" max="23" placeholder="HH" style={{ width: 56, textAlign: 'center', padding: '6px 4px', border: '1px solid #d1d5db', borderRadius: 6 }}
            value={form.custom_date && form.custom_date.length >= 16 ? parseInt(form.custom_date.slice(11, 13), 10) : ''}
            onChange={e => {
              const h = String(Math.min(23, Math.max(0, parseInt(e.target.value) || 0))).padStart(2, '0');
              const date = form.custom_date ? form.custom_date.slice(0, 10) : new Date().toISOString().slice(0, 10);
              const m = form.custom_date && form.custom_date.length >= 16 ? form.custom_date.slice(14, 16) : '00';
              setForm(f => ({ ...f, custom_date: date + 'T' + h + ':' + m }));
            }} />
          <span>:</span>
          <input type="number" min="0" max="59" placeholder="MM" style={{ width: 56, textAlign: 'center', padding: '6px 4px', border: '1px solid #d1d5db', borderRadius: 6 }}
            value={form.custom_date && form.custom_date.length >= 16 ? parseInt(form.custom_date.slice(14, 16), 10) : ''}
            onChange={e => {
              const m = String(Math.min(59, Math.max(0, parseInt(e.target.value) || 0))).padStart(2, '0');
              const date = form.custom_date ? form.custom_date.slice(0, 10) : new Date().toISOString().slice(0, 10);
              const h = form.custom_date && form.custom_date.length >= 16 ? form.custom_date.slice(11, 13) : '00';
              setForm(f => ({ ...f, custom_date: date + 'T' + h + ':' + m }));
            }} />
          <input type="date" style={{ flex: 1 }}
            value={form.custom_date ? form.custom_date.slice(0, 10) : ''}
            onChange={e => setForm(f => ({ ...f, custom_date: e.target.value + 'T' + (f.custom_date && f.custom_date.length >= 16 ? f.custom_date.slice(11, 16) : '00:00') }))} />
        </div>
      </div>
      <div className="form-group">
        <label>שלח ל</label>
        <select value={form.recipient.startsWith('specific:') ? 'specific' : form.recipient}
          onChange={e => {
            const r = e.target.value;
            setForm(f => ({ ...f, recipient: r, specificSearch: '', specificPhone: '', category: r === 'specific' ? 'אישי' : f.category }));
          }}>
          {form.category !== 'אישי' && <option value="tenants">בעלי דירות בלבד</option>}
          <option value="manager">מנהלי פרויקטים</option>
          <option value="specific">דייר ספציפי</option>
        </select>
      </div>
      {form.recipient === 'manager' && (
        <div className="form-group">
          <label>בחר מנהלי פרויקטים</label>
          {managers.length === 0
            ? <div style={{ fontSize: '0.85rem', color: '#9ca3af', padding: 8 }}>טוען רשימה...</div>
            : <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', maxHeight: 180, overflowY: 'auto', background: '#fafafa' }}>
                {managers.map(m => (
                  <label key={m.userName} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: '0.9rem' }}>
                    <input type="checkbox" checked={selectedManagers.includes(m.userName)} onChange={() => toggleManager(m.userName)} />
                    {m.workerFirstName} {m.workerLastName}
                    <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>({m.role === 'admin' ? 'אדמין' : 'מנהל פרויקט'})</span>
                  </label>
                ))}
              </div>
          }
          {selectedManagers.length === 0 && <div style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4 }}>יש לבחור לפחות מנהל פרויקט אחד</div>}
        </div>
      )}
      {(form.recipient === 'specific' || form.recipient.startsWith('specific:')) && (
        <TenantMultiSearch
          projectName={projectName}
          selectedTenants={form.selectedTenants || []}
          onAdd={t => setForm(f => ({ ...f, selectedTenants: [...(f.selectedTenants || []), t] }))}
          onRemove={phone => setForm(f => ({ ...f, selectedTenants: (f.selectedTenants || []).filter(t => t.phone !== phone) }))}
        />
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary">{submitLabel}</button>
        <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
      </div>
    </form>
  );
}

function AnnouncementsTab({ projectName }) {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const emptyForm = { title: '', body: '', priority: 'normal', recipient: 'tenants', custom_date: today, selectedTenants: [], category: 'כללי', selectedManagers: [] };
  const [form, setForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [translating, setTranslating] = useState(false);
  const [translateMsg, setTranslateMsg] = useState('');

  async function translateExisting() {
    setTranslating(true);
    setTranslateMsg('מתרגם...');
    try {
      const r = await api.post(`/portal/translate-existing/${encodeURIComponent(projectName)}`);
      const c = r.data.translated;
      setTranslateMsg(`✅ תורגם: ${c.tasks} משימות, ${c.announcements} הודעות, ${c.stages} שלבים`);
      load();
    } catch (e) {
      setTranslateMsg('❌ שגיאה: ' + (e.response?.data?.error || e.message));
    }
    setTranslating(false);
  }

  function announcementMatchesSearch(item, q) {
    if (!q) return true;
    const words = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (!words.length) return true;
    const text = `${item.title || ''} ${item.body || ''}`.toLowerCase();
    const dateStr = item.published_at ? new Date(item.published_at.replace(' ', 'T')).toLocaleDateString('he-IL') : '';
    // Tenant-name matching is trim-tolerant: names in the DB sometimes carry stray
    // extra spaces (e.g. "אור " + "לוי" → "אור  לוי"), so collapse runs of whitespace
    // before comparing — otherwise a normally-spaced search like "אור ל" misses it.
    const nameNormalized = (item.recipient_names || '').toLowerCase().replace(/\s+/g, ' ').trim();
    return words.every(w => text.includes(w) || dateStr.includes(w) || nameNormalized.includes(w));
  }

  const load = useCallback(async () => {
    const res = await api.get(`/portal/announcements/${encodeURIComponent(projectName)}?admin=1`);
    setItems(res.data);
  }, [projectName]);

  useEffect(() => { load(); }, [load]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.recipient === 'manager' && (!form.selectedManagers || form.selectedManagers.length === 0)) {
      alert('יש לבחור לפחות מנהל פרויקט אחד');
      return;
    }
    if ((form.recipient === 'specific' || form.recipient.startsWith('specific:')) && (!form.selectedTenants || form.selectedTenants.length === 0)) {
      alert('יש לבחור לפחות דייר אחד');
      return;
    }
    const payload = { ...form };
    if (form.recipient === 'manager' && form.selectedManagers?.length > 0) {
      payload.recipient = `managers:${form.selectedManagers.join(',')}`;
    } else if ((form.recipient === 'specific' || form.recipient.startsWith('specific:')) && form.selectedTenants?.length > 0) {
      payload.recipient = `specific:${form.selectedTenants.map(t => t.phone).join(',')}`;
      payload.recipient_names = form.selectedTenants.map(t => t.name || t.phone).join(', ');
    }
    try {
      await api.post(`/portal/announcements/${encodeURIComponent(projectName)}`, payload);
      setShowAdd(false);
      setForm(emptyForm);
      load();
    } catch(err) { alert('שגיאה בשמירה: ' + (err.response?.data?.error || err.message)); }
  }

  function openEdit(item) {
    const dateStr = item.published_at ? item.published_at.slice(0, 16).replace(' ', 'T') : today;
    const rec = item.recipient || 'all';
    let selectedTenants = [];
    if (rec.startsWith('specific:')) {
      const phones = rec.slice(9).split(',');
      selectedTenants = phones.map(p => ({ name: p, phone: p, address: '' }));
    }
    setEditForm({
      title: item.title || '',
      body: item.body || '',
      priority: item.priority || 'normal',
      recipient: rec.startsWith('specific:') ? 'specific' : rec,
      custom_date: dateStr,
      selectedTenants,
      category: item.category || 'כללי',
      selectedManagers: [],
      file_url: item.file_url || null,
      file_name: item.file_name || null,
    });
    setEditingItem(item);
  }

  async function handleEdit(e) {
    e.preventDefault();
    const payload = { ...editForm };
    if ((editForm.recipient === 'specific' || editForm.recipient.startsWith('specific:')) && editForm.selectedTenants?.length > 0) {
      payload.recipient = `specific:${editForm.selectedTenants.map(t => t.phone).join(',')}`;
      payload.recipient_names = editForm.selectedTenants.map(t => t.name || t.phone).join(', ');
    }
    try {
      await api.put(`/portal/announcements/${editingItem.id}`, payload);
      setEditingItem(null);
      load();
    } catch(err) { alert('שגיאה בעריכה: ' + (err.response?.data?.error || err.message)); }
  }

  async function handleDelete(id) {
    if (!confirm('האם אתה בטוח שאתה רוצה למחוק את ההודעה?')) return;
    await api.delete(`/portal/announcements/${id}`);
    load();
  }

  return (
    <div style={{ background: '#f6efe0', borderRadius: 14, padding: '20px 22px', minHeight: 200 }}>
      <div style={{ display: 'flex', direction: 'rtl', justifyContent: 'flex-start', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => setShowAdd(true)} style={{ background: '#8a6a2a', color: '#fff', border: 'none', borderRadius: 7, padding: '9px 16px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>+ הוסף הודעה</button>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 חפש לפי מילה, תאריך או שם דייר"
          style={{ flex: 1, minWidth: 220, padding: '9px 14px', borderRadius: 7, border: '1px solid #e3d5ac', fontSize: '0.85rem', outline: 'none', direction: 'rtl' }}
        />
        <button onClick={translateExisting} disabled={translating} style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 7, padding: '9px 14px', fontWeight: 600, fontSize: '0.82rem', cursor: translating ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
          {translating ? '⏳ מתרגם...' : '🌐 תרגם תוכן קיים'}
        </button>
        {translateMsg && <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>{translateMsg}</span>}
      </div>
      {(() => {
        const filteredItems = items.filter(item => announcementMatchesSearch(item, search));
        return <>
      {filteredItems.length === 0 && <div style={{ color: '#8a7454', textAlign: 'center', padding: 40 }}>{items.length === 0 ? 'אין הודעות עדיין' : 'לא נמצאו הודעות תואמות'}</div>}
      {filteredItems.map(item => (
        <div key={item.id} style={{
          background: item.priority === 'urgent' ? '#fff8ee' : '#fff',
          border: `1px solid ${item.priority === 'urgent' ? '#e3c57a' : '#e3d5ac'}`,
          borderRadius: 10, padding: '14px 16px', marginBottom: 10,
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <div style={{ display: 'flex', gap: 10, color: '#a4802f', fontSize: '1rem', paddingTop: 2 }}>
            <button onClick={() => handleDelete(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a4802f', fontSize: '1rem', padding: 0 }}>🗑️</button>
            <button onClick={() => openEdit(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a4802f', fontSize: '1rem', padding: 0 }}>✏️</button>
          </div>
          <div style={{ textAlign: 'right', flex: 1, marginRight: 14 }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a1a', marginBottom: 4 }}>
              {item.priority === 'urgent' ? '⚠️ ' : ''}{item.title}
            </div>
            {item.body && <div style={{ fontSize: '0.8rem', color: '#6b5a3a', marginBottom: 10 }}>{item.body}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '0.72rem', color: '#8a7454' }}>{item.published_at ? new Date(item.published_at.replace(' ', 'T')).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : ''}</span>
              {item.recipient && item.recipient.startsWith('specific:') ? (
                <span style={{ background: '#f0e0b8', color: '#8a6a2a', borderRadius: 999, padding: '4px 10px', fontWeight: 700, fontSize: '0.68rem' }}>👤 {item.recipient_names || 'דייר ספציפי'}</span>
              ) : (
                <>
                  <span style={{ background: '#f0e0b8', color: '#8a6a2a', borderRadius: 999, padding: '4px 10px', fontWeight: 700, fontSize: '0.68rem' }}>
                    {item.category === 'אישי' ? '👤 אישי' : '📣 כללי'}
                  </span>
                  {item.recipient && item.recipient !== 'all' && (
                    <span style={{ background: '#e8dcc8', color: '#6b5a3a', borderRadius: 999, padding: '4px 10px', fontSize: '0.68rem' }}>
                      {item.recipient === 'manager' ? 'מנהלי פרויקטים' : item.recipient === 'tenants' ? 'בעלי דירות בלבד' : item.recipient.startsWith('managers:') ? `👤 ${item.recipient.slice(9).split(',').join(', ')}` : item.recipient}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ))}
      </>;
      })()}
      {showAdd && (
        <Modal title="הוספת הודעה" onClose={() => { setShowAdd(false); setForm(emptyForm); }}>
          <AnnouncementForm form={form} setForm={setForm} projectName={projectName} onSubmit={handleSubmit} onClose={() => { setShowAdd(false); setForm(emptyForm); }} submitLabel="פרסם" />
        </Modal>
      )}
      {editingItem && (
        <Modal title="עריכת הודעה" onClose={() => setEditingItem(null)}>
          <AnnouncementForm form={editForm} setForm={setEditForm} projectName={projectName} onSubmit={handleEdit} onClose={() => setEditingItem(null)} submitLabel="שמור שינויים" />
        </Modal>
      )}
    </div>
  );
}

// ── Gallery Tab ──────────────────────────────────────────────────────────────

const GALLERY_CATEGORIES = ['הדמיות פרויקטים', 'כנסים ומפגשים', 'אירועי חתימות'];

const btnStyle = { padding: '10px 0', borderRadius: 10, border: '1px solid #e5e7eb', background: '#f9fafb', fontSize: '1rem', cursor: 'pointer', fontWeight: 700 };

function parsePos(pos) {
  // Parse "X% Y%" or named values to numbers 0-100
  const named = { left: 0, center: 50, right: 100, top: 0, bottom: 100 };
  const parts = (pos || 'center center').split(' ');
  const x = parts[0]?.endsWith('%') ? parseFloat(parts[0]) : (named[parts[0]] ?? 50);
  const y = parts[1]?.endsWith('%') ? parseFloat(parts[1]) : (named[parts[1]] ?? 50);
  return { x, y };
}
function fmtPos(x, y) { return `${Math.round(x)}% ${Math.round(y)}%`; }
function clamp(v) { return Math.max(0, Math.min(100, v)); }

function PositionEditorModal({ item, onSave, onClose, imgUrl }) {
  const init = parsePos(item.object_position);
  const [x, setX] = useState(init.x);
  const [y, setY] = useState(init.y);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const containerRef = React.useRef(null);
  const dragState = React.useRef(null);
  const pos = fmtPos(x, y);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const prevent = e => { if (dragState.current) e.preventDefault(); };
    el.addEventListener('touchmove', prevent, { passive: false });
    el.addEventListener('pointermove', prevent, { passive: false });
    return () => { el.removeEventListener('touchmove', prevent); el.removeEventListener('pointermove', prevent); };
  }, []);

  async function handleSave() {
    setSaving(true);
    await onSave(item.id, pos);
    setSaving(false);
    onClose();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, padding: 24, width: 320, maxWidth: '95vw', direction: 'rtl' }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 14 }}>✂️ עריכת מיקום תמונה</div>

        {/* Preview — drag to reposition */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 6 }}>גרור את התמונה לשינוי מיקום:</div>
          <div
            ref={containerRef}
            style={{ width: '100%', height: 180, borderRadius: 10, overflow: 'hidden', border: `2px solid ${dragging ? '#1a4a8a' : '#e5e7eb'}`, cursor: dragging ? 'grabbing' : 'grab', userSelect: 'none', touchAction: 'none' }}
            onPointerDown={e => {
              e.preventDefault();
              const el = containerRef.current;
              el.setPointerCapture(e.pointerId);
              const rect = el.getBoundingClientRect();
              dragState.current = { startClientX: e.clientX, startClientY: e.clientY, startX: x, startY: y, rect };
              setDragging(true);
            }}
            onPointerMove={e => {
              e.preventDefault();
              if (!dragState.current) return;
              const { startClientX, startClientY, startX: sx, startY: sy, rect } = dragState.current;
              const dx = ((e.clientX - startClientX) / rect.width) * -100;
              const dy = ((e.clientY - startClientY) / rect.height) * -100;
              setX(clamp(sx + dx));
              setY(clamp(sy + dy));
            }}
            onPointerUp={() => { dragState.current = null; setDragging(false); }}
            onPointerCancel={() => { dragState.current = null; setDragging(false); }}
          >
            <img src={imgUrl(item.url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: pos, display: 'block', pointerEvents: 'none' }} />
          </div>
        </div>

        {/* 4 direction buttons — LTR so arrows match visual direction */}

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleSave} disabled={saving} className="btn btn-primary" style={{ flex: 1 }}>
            {saving ? 'שומר...' : 'שמור'}
          </button>
          <button onClick={onClose} className="btn btn-secondary">ביטול</button>
        </div>
      </div>
    </div>
  );
}

function stripExt(name) {
  if (!name) return '';
  return name.replace(/\.[^/.]+$/, '');
}

function imgUrl(url) {
  if (!url) return url;
  const tok = localStorage.getItem('token') || '';
  const base = url.startsWith('/uploads') ? `https://mabat.communityltd.co.il${url}` : url;
  return base.includes('/uploads') && tok ? `${base}?auth=${tok}` : base;
}

function GalleryTab({ projectName }) {
  const [items, setItems] = useState([]);
  const [filterCat, setFilterCat] = useState('הכל');
  const [uploadCat, setUploadCat] = useState('הדמיות פרויקטים');
  const [addingCat, setAddingCat] = useState(false);
  const [newCatInput, setNewCatInput] = useState('');
  const [manualCats, setManualCats] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [dropZoneDragging, setDropZoneDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [lightbox, setLightbox] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragIdxRef = React.useRef(null);
  const [editingCaption, setEditingCaption] = useState(null); // { id, value }
  const [catEditorId, setCatEditorId] = useState(null);
  const [posEditor, setPosEditor] = useState(null); // { item }
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileInputRef = React.useRef();

  const load = useCallback(async () => {
    const res = await api.get(`/portal/gallery/${encodeURIComponent(projectName)}`);
    setItems(res.data);
    setSelected(new Set());
  }, [projectName]);

  useEffect(() => { load(); }, [load]);

  // Close lightbox on Escape
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Prevent browser from navigating when a gallery card is dropped outside a valid target
  useEffect(() => {
    const preventNav = e => { if (dragIdxRef.current !== null) e.preventDefault(); };
    document.addEventListener('dragover', preventNav);
    document.addEventListener('drop', preventNav);
    return () => { document.removeEventListener('dragover', preventNav); document.removeEventListener('drop', preventNav); };
  }, []);

  async function uploadFiles(files) {
    if (!files || !files.length) return;
    const invalid = Array.from(files).filter(f => !f.type.startsWith('image/') || f.size > 100 * 1024 * 1024);
    if (invalid.length) alert(`${invalid.length} קבצים לא תקינים (רק תמונות עד 100MB)`);
    const valid = Array.from(files).filter(f => f.type.startsWith('image/') && f.size <= 100 * 1024 * 1024);
    if (!valid.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: valid.length });
    let failed = 0, done = 0;
    const CONCURRENCY = 2;

    function resizeToBlob(file, maxDim, quality) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            if (width > height) { height = Math.round(height * maxDim / width); width = maxDim; }
            else { width = Math.round(width * maxDim / height); height = maxDim; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          canvas.toBlob(resolve, 'image/webp', quality);
        };
        img.onerror = reject;
        img.src = url;
      });
    }

    try {
      const uploadOne = async (f) => {
        try {
          const [webpBlob, thumbBlob] = await Promise.all([
            resizeToBlob(f, 1800, 0.82),
            resizeToBlob(f, 400, 0.70),
          ]);
          const { data: presign } = await api.post('/portal/presign-gallery', { projectName });
          await Promise.all([
            fetch(presign.webpUploadUrl, { method: 'PUT', headers: { 'Content-Type': 'image/webp' }, body: webpBlob }),
            fetch(presign.thumbUploadUrl, { method: 'PUT', headers: { 'Content-Type': 'image/webp' }, body: thumbBlob }),
          ]);
          await api.post(`/portal/gallery/${encodeURIComponent(projectName)}/confirm`, {
            webpS3Url: presign.webpS3Url, thumbS3Url: presign.thumbS3Url,
            category: uploadCat, workerName: getWorkerInfo().name,
          });
        } catch { failed++; }
        setUploadProgress({ done: ++done, total: valid.length });
      };
      for (let i = 0; i < valid.length; i += CONCURRENCY) {
        await Promise.all(valid.slice(i, i + CONCURRENCY).map(uploadOne));
      }
      await load();
      setFilterCat('הכל');
      if (failed) alert(`${failed} תמונות לא הועלו`);
    } finally { setUploading(false); setUploadProgress({ done: 0, total: 0 }); }
  }

  function onDropZone(e) {
    e.preventDefault();
    setDropZoneDragging(false);
    // only accept actual file drops, not card reorder drags
    if (!e.dataTransfer.files || !e.dataTransfer.files.length) return;
    uploadFiles(e.dataTransfer.files);
  }

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAll() {
    const visible = filtered.map(i => i.id);
    if (selected.size === visible.length) setSelected(new Set());
    else setSelected(new Set(visible));
  }

  async function deleteSelected() {
    if (!selected.size) return;
    setConfirmDelete(true);
  }

  async function doDeleteSelected() {
    setConfirmDelete(false);
    try {
      await api.post('/portal/gallery/delete-many', { ids: Array.from(selected) });
      setSelected(new Set());
      load();
    } catch (e) { alert('שגיאה במחיקה: ' + e.message); }
  }

  async function deleteSingle(id) {
    if (!window.confirm('למחוק את התמונה?')) return;
    try {
      await api.delete(`/portal/gallery/${id}`);
      load();
    } catch (e) { alert('שגיאה במחיקה: ' + e.message); }
  }

  async function saveCategory(id, category) {
    await api.patch(`/portal/gallery/${id}/category`, { category });
    setItems(prev => prev.map(i => i.id === id ? { ...i, category } : i));
    setCatEditorId(null);
  }

  async function saveCaption(id, value) {
    await api.patch(`/portal/gallery/${id}/caption`, { caption: value });
    setItems(prev => prev.map(i => i.id === id ? { ...i, caption: value } : i));
    setEditingCaption(null);
  }

  async function savePosition(id, pos) {
    await api.patch(`/portal/gallery/${id}/position`, { object_position: pos });
    setItems(prev => prev.map(i => i.id === id ? { ...i, object_position: pos } : i));
    if (posEditor && posEditor.item.id === id) setPosEditor(pe => ({ ...pe, item: { ...pe.item, object_position: pos } }));
  }

  // Drag-to-reorder handlers
  function onDragStart(e, idx) {
    dragIdxRef.current = idx;
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/x-gallery-idx', String(idx));
  }
  function onDragOver(e, idx) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIdx(idx);
  }
  async function onDrop(e, idx) {
    e.preventDefault();
    e.stopPropagation();
    const from = dragIdxRef.current;
    dragIdxRef.current = null;
    setDragIdx(null);
    setDragOverIdx(null);
    if (from === null || from === idx) return;
    const reordered = [...filtered];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(idx, 0, moved);
    await api.post('/portal/gallery/reorder', { ids: reordered.map(i => i.id) });
    load(); // reload fresh from server
  }

  // Lightbox navigation
  function openLightbox(item) {
    const idx = filtered.findIndex(i => i.id === item.id);
    setLightbox({ item, idx });
  }
  const lbTouchRef = useRef(null);
  function lbTouchStart(e) { lbTouchRef.current = e.touches[0].clientX; }
  function lbTouchEnd(e) {
    if (lbTouchRef.current == null) return;
    const dx = e.changedTouches[0].clientX - lbTouchRef.current;
    lbTouchRef.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) lbNext(); else lbPrev();
  }
  function lbPrev() {
    setLightbox(lb => {
      const idx = (lb.idx - 1 + filtered.length) % filtered.length;
      return { item: filtered[idx], idx };
    });
  }
  function lbNext() {
    setLightbox(lb => {
      const idx = (lb.idx + 1) % filtered.length;
      return { item: filtered[idx], idx };
    });
  }

  const dynamicCats = [...new Set([...GALLERY_CATEGORIES, ...manualCats, ...items.map(i => i.category).filter(Boolean)])];
  // Signing-event photos are excluded from the aggregate "הכל" view here too
  // (matching the tenant portal) — still fully visible/manageable under their
  // own category tab.
  const filtered = filterCat === 'הכל' ? items.filter(i => i.category !== 'אירועי חתימות') : items.filter(i => i.category === filterCat);
  const allVisibleSelected = filtered.length > 0 && filtered.every(i => selected.has(i.id));

  const [renamingCat, setRenamingCat] = useState(null); // { old, value }

  async function confirmRename() {
    const newName = renamingCat.value.trim();
    if (!newName || newName === renamingCat.old) { setRenamingCat(null); return; }
    await fetch(`/api/portal/gallery/rename-category/${encodeURIComponent(projectName)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ oldName: renamingCat.old, newName })
    });
    setManualCats(prev => prev.map(c => c === renamingCat.old ? newName : c));
    if (uploadCat === renamingCat.old) setUploadCat(newName);
    if (filterCat === renamingCat.old) setFilterCat(newName);
    setItems(prev => prev.map(i => i.category === renamingCat.old ? { ...i, category: newName } : i));
    setRenamingCat(null);
  }

  function confirmNewCat() {
    const name = newCatInput.trim();
    if (!name || dynamicCats.includes(name)) { setAddingCat(false); setNewCatInput(''); return; }
    setManualCats(prev => [...prev, name]);
    setUploadCat(name);
    setAddingCat(false);
    setNewCatInput('');
  }

  return (
    <div>
      {/* Category for upload */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>קטגוריה לתמונות חדשות:</span>
        {dynamicCats.map(c => (
          renamingCat?.old === c ? (
            <div key={c} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input autoFocus value={renamingCat.value} onChange={e => setRenamingCat(r => ({ ...r, value: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') confirmRename(); if (e.key === 'Escape') setRenamingCat(null); }}
                style={{ padding: '4px 10px', borderRadius: 99, border: '1px solid #1B2A4A', fontSize: '0.82rem', width: 130, outline: 'none' }} />
              <button onClick={confirmRename} style={{ padding: '4px 10px', borderRadius: 99, border: 'none', background: '#1B2A4A', color: '#fff', cursor: 'pointer', fontSize: '0.82rem' }}>✓</button>
              <button onClick={() => setRenamingCat(null)} style={{ padding: '4px 8px', borderRadius: 99, border: 'none', background: '#f3f4f6', cursor: 'pointer', fontSize: '0.82rem' }}>✕</button>
            </div>
          ) : (
            <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <button onClick={() => setUploadCat(c)} style={{
                padding: '4px 14px', borderRadius: 99, border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                background: uploadCat === c ? '#1B2A4A' : '#f3f4f6', color: uploadCat === c ? '#fff' : '#374151',
              }}>{c}</button>
              <button onClick={() => setRenamingCat({ old: c, value: c })} title="שנה שם" style={{ padding: '2px 5px', borderRadius: 99, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.75rem', color: '#9ca3af' }}>✎</button>
            </div>
          )
        ))}
        {addingCat ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input
              autoFocus
              value={newCatInput}
              onChange={e => setNewCatInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') confirmNewCat(); if (e.key === 'Escape') { setAddingCat(false); setNewCatInput(''); } }}
              placeholder="שם קטגוריה..."
              style={{ padding: '4px 10px', borderRadius: 99, border: '1px solid #d1d5db', fontSize: '0.82rem', width: 130, outline: 'none' }}
            />
            <button onClick={confirmNewCat} style={{ padding: '4px 10px', borderRadius: 99, border: 'none', background: '#1B2A4A', color: '#fff', cursor: 'pointer', fontSize: '0.82rem' }}>✓</button>
            <button onClick={() => { setAddingCat(false); setNewCatInput(''); }} style={{ padding: '4px 8px', borderRadius: 99, border: 'none', background: '#f3f4f6', cursor: 'pointer', fontSize: '0.82rem' }}>✕</button>
          </div>
        ) : (
          <button onClick={() => setAddingCat(true)} style={{ padding: '4px 12px', borderRadius: 99, border: '1px dashed #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '0.82rem', color: '#6b7280' }}>+ קטגוריה</button>
        )}
      </div>

      {/* Upload zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDropZoneDragging(true); }}
        onDragLeave={() => setDropZoneDragging(false)}
        onDrop={onDropZone}
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
        style={{
          border: `2px dashed ${dropZoneDragging ? '#3b82f6' : '#d1d5db'}`,
          borderRadius: 16, padding: '28px 20px', textAlign: 'center',
          background: dropZoneDragging ? '#eff6ff' : '#fafafa', cursor: 'pointer', marginBottom: 16,
          transition: 'all .2s',
        }}
      >
        <div style={{ fontSize: '2rem', marginBottom: 6 }}>📷</div>
        <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
          {uploading ? `מעלה ${uploadProgress.done}/${uploadProgress.total} תמונות...` : 'גרור תמונות לכאן או לחץ לבחירה'}
        </div>
        {uploading && uploadProgress.total > 0 && (
          <div style={{ width: '80%', background: '#e5e7eb', borderRadius: 8, height: 10, margin: '8px auto 4px', overflow: 'hidden' }}>
            <div style={{ width: `${Math.round(uploadProgress.done / uploadProgress.total * 100)}%`, height: '100%', background: '#7c3aed', borderRadius: 8, transition: 'width 0.3s' }} />
          </div>
        )}
        {uploading && uploadProgress.total > 0 && (
          <div style={{ fontSize: '0.8rem', color: '#7c3aed', fontWeight: 600 }}>
            {Math.round(uploadProgress.done / uploadProgress.total * 100)}%
          </div>
        )}
        <div style={{ fontSize: '0.8rem', color: '#9ca3af' }}>כל סוגי התמונות • עד 100MB לתמונה • מומלץ עד 10 תמונות בכל העלאה</div>
        <input ref={fileInputRef} type="file" multiple accept="image/*" style={{ display: 'none' }}
          onChange={e => uploadFiles(e.target.files)} />
      </div>

      {/* Filter + actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['הכל', ...dynamicCats].map(c => (
            <button key={c} onClick={() => setFilterCat(c)} style={{
              padding: '4px 14px', borderRadius: 99, border: 'none', cursor: 'pointer', fontSize: '0.82rem',
              background: filterCat === c ? '#3b82f6' : '#f3f4f6', color: filterCat === c ? '#fff' : '#374151',
            }}>{c} {c === 'הכל' ? `(${items.filter(i => i.category !== 'אירועי חתימות').length})` : `(${items.filter(i => i.category === c).length})`}</button>
          ))}
        </div>
        {filtered.length > 0 && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={selectAll} style={{ padding: '4px 14px', borderRadius: 99, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '0.82rem' }}>
              {allVisibleSelected ? 'בטל בחירה' : 'בחר הכל'}
            </button>
            {selected.size > 0 && (
              <>
                <select onChange={async e => {
                  const cat = e.target.value;
                  if (!cat) return;
                  await Promise.all(Array.from(selected).map(id => api.patch(`/portal/gallery/${id}/category`, { category: cat })));
                  setItems(prev => prev.map(i => selected.has(i.id) ? { ...i, category: cat } : i));
                  setSelected(new Set());
                  e.target.value = '';
                }} defaultValue="" style={{ padding: '4px 12px', borderRadius: 99, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '0.82rem' }}>
                  <option value="">שנה קטגוריה ({selected.size})</option>
                  {dynamicCats.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button onClick={deleteSelected} style={{ padding: '4px 14px', borderRadius: 99, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                  מחק נבחרים ({selected.size})
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginBottom: 10, textAlign: 'center' }}>
          💡 גרור תמונה לשינוי הסדר • לחץ על תמונה לתצוגה מלאה
        </div>
      )}

      {filtered.length === 0 && <div style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>אין תמונות עדיין</div>}

      {(() => {
        const [featured, ...rest] = filtered;
        function GalleryCard({ item, idx, imgH, useFull }) {
          return (
            <div
              key={item.id}
              draggable
              onDragStart={e => onDragStart(e, idx)}
              onDragOver={e => onDragOver(e, idx)}
              onDrop={e => onDrop(e, idx)}
              onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
              style={{
                borderRadius: 10, overflow: 'hidden', position: 'relative',
                outline: selected.has(item.id) ? '3px solid #3b82f6' : dragOverIdx === idx ? '3px solid #f59e0b' : 'none',
                opacity: dragIdx === idx ? 0.5 : 1, cursor: 'grab', transition: 'opacity .15s',
              }}
            >
              <div style={{ position: 'relative' }}>
                <img src={imgUrl(useFull ? item.url : (item.thumb_url || item.url))} alt={stripExt(item.caption) || ''} loading="lazy" decoding="async"
                  style={{ width: '100%', height: imgH, objectFit: 'cover', objectPosition: item.object_position || 'center', display: 'block', cursor: 'pointer' }}
                  onClick={() => openLightbox(item)} draggable={false} />
                <div style={{ position: 'absolute', top: 6, right: 6 }}>
                  <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggleSelect(item.id)} style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#3b82f6' }} />
                </div>
                <button onClick={e => { e.stopPropagation(); deleteSingle(item.id); }} style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', padding: '4px 7px', fontSize: '1rem', lineHeight: 1 }} title="מחק">🗑️</button>
                <button onClick={e => { e.stopPropagation(); setPosEditor({ item }); }} style={{ position: 'absolute', bottom: 32, left: 6, background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center' }} title="מיקום">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="3" stroke="#ef4444"/><circle cx="6" cy="18" r="3" stroke="#ef4444"/><line x1="8.46" y1="7.54" x2="22" y2="21" stroke="white"/><line x1="22" y1="3" x2="8.46" y2="16.46" stroke="white"/></svg>
                </button>
                <div style={{ position: 'absolute', bottom: 6, left: 6 }}>
                  <button onClick={e => { e.stopPropagation(); setCatEditorId(catEditorId === item.id ? null : item.id); }} style={{ background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', padding: '3px 6px', fontSize: '0.78rem' }}>✏️</button>
                  {catEditorId === item.id && (
                    <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: 28, left: 0, background: '#fff', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.18)', padding: 8, zIndex: 99, minWidth: 160 }}>
                      {dynamicCats.map(c => (
                        <div key={c} onClick={() => saveCategory(item.id, c)} style={{ padding: '6px 12px', cursor: 'pointer', borderRadius: 6, fontSize: '0.82rem', fontWeight: item.category === c ? 700 : 400, background: item.category === c ? '#eff6ff' : 'transparent', color: item.category === c ? '#1d4ed8' : '#374151' }}>{c}</div>
                      ))}
                    </div>
                  )}
                </div>
                {item.caption && (
                  <div style={{ position: 'absolute', bottom: 0, right: 0, left: 0, background: 'linear-gradient(0deg,rgba(0,0,0,.55),transparent)', padding: '10px 12px', pointerEvents: 'none' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.78rem', color: '#fff' }}>{stripExt(item.caption)}</div>
                  </div>
                )}
              </div>
              {editingCaption && editingCaption.id === item.id ? (
                <div style={{ background: '#fff', padding: '4px 6px', display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                  <input autoFocus value={editingCaption.value} onChange={e => setEditingCaption(ec => ({ ...ec, value: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') saveCaption(item.id, editingCaption.value); if (e.key === 'Escape') setEditingCaption(null); }}
                    style={{ flex: 1, fontSize: '0.75rem', padding: '2px 6px', borderRadius: 6, border: '1px solid #3b82f6', outline: 'none' }} />
                  <button onClick={() => saveCaption(item.id, editingCaption.value)} style={{ background: '#3b82f6', border: 'none', color: '#fff', borderRadius: 6, padding: '2px 7px', cursor: 'pointer', fontSize: '0.75rem' }}>✓</button>
                  <button onClick={() => setEditingCaption(null)} style={{ background: '#e5e7eb', border: 'none', borderRadius: 6, padding: '2px 6px', cursor: 'pointer', fontSize: '0.75rem' }}>✕</button>
                </div>
              ) : (
                <div onClick={e => { e.stopPropagation(); setEditingCaption({ id: item.id, value: item.caption || '' }); }} title="לחץ לעריכת שם"
                  style={{ padding: '4px 8px', fontSize: '0.72rem', background: '#fff', color: item.caption ? '#374151' : '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'text' }}>
                  {item.caption ? stripExt(item.caption) : '✏️ הוסף שם...'}
                </div>
              )}
              {item.category && <div style={{ padding: '2px 8px 5px', fontSize: '0.68rem', color: '#6b7280', background: '#fff' }}>{item.category}</div>}
            </div>
          );
        }

        return (
          <>
            {/* Featured row: first image large + next 2 stacked */}
            {filtered.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: rest.length >= 2 ? '2fr 1fr' : '1fr', gap: 10, marginBottom: 10 }}>
                <GalleryCard item={featured} idx={0} imgH={260} useFull />
                {rest.length >= 2 && (
                  <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: 10 }}>
                    <GalleryCard item={rest[0]} idx={1} imgH={125} />
                    <GalleryCard item={rest[1]} idx={2} imgH={125} />
                  </div>
                )}
              </div>
            )}
            {/* Rest: small grid — includes rest[0] when there's exactly 1 leftover */}
            {rest.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                {(rest.length === 1 ? rest : rest.slice(2)).map((item, i) => (
                  <GalleryCard key={item.id} item={item} idx={i + (rest.length === 1 ? 1 : 3)} imgH={130} />
                ))}
              </div>
            )}
          </>
        );
      })()}

      {/* Position editor modal */}
      {posEditor && <PositionEditorModal item={posEditor.item} onSave={savePosition} onClose={() => setPosEditor(null)} imgUrl={imgUrl} />}

      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: 320, direction: 'rtl', textAlign: 'center' }}>
            <div style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 10 }}>מחיקת תמונות</div>
            <div style={{ color: '#6b7280', marginBottom: 20 }}>למחוק {selected.size} תמונות נבחרות?</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={doDeleteSelected} style={{ flex: 1, background: '#ef4444', color: 'white', border: 'none', padding: '9px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>מחק</button>
              <button onClick={() => setConfirmDelete(false)} style={{ flex: 1, background: '#f3f4f6', color: '#374151', border: 'none', padding: '9px 0', borderRadius: 8, cursor: 'pointer' }}>ביטול</button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={e => { if (e.target === e.currentTarget) { if (e.clientX > window.innerWidth / 2) lbPrev(); else lbNext(); } }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            onTouchStart={lbTouchStart}
            onTouchEnd={lbTouchEnd}
            style={{ position: 'relative', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center', touchAction: 'pan-y' }}
          >
          {filtered.length > 1 && (
            <button onClick={e => { e.stopPropagation(); lbPrev(); }} style={{
              position: 'absolute', right: 6, top: '40%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.25)', border: 'none', borderRadius: '50%',
              color: '#fff', fontSize: '1.6rem', width: 44, height: 44, cursor: 'pointer', zIndex: 2,
            }}>‹</button>
          )}
          {filtered.length > 1 && (
            <button onClick={e => { e.stopPropagation(); lbNext(); }} style={{
              position: 'absolute', left: 6, top: '40%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.25)', border: 'none', borderRadius: '50%',
              color: '#fff', fontSize: '1.6rem', width: 44, height: 44, cursor: 'pointer', zIndex: 2,
            }}>›</button>
          )}
            <img
              src={imgUrl(lightbox.item.url)}
              alt={stripExt(lightbox.item.caption) || ''}
              style={{ maxWidth: '95vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 }}
            />
            {lightbox.item.caption && (
              <div style={{ color: '#e5e7eb', marginTop: 12, fontSize: '0.95rem', textAlign: 'center' }}>
                {stripExt(lightbox.item.caption)}
              </div>
            )}
            <div style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: 4 }}>
              {lightbox.idx + 1} / {filtered.length}
            </div>
          </div>
          <button onClick={() => setLightbox(null)} style={{
            position: 'absolute', top: 16, left: 16, background: 'rgba(255,255,255,0.15)',
            border: 'none', color: '#fff', fontSize: '1.4rem', width: 40, height: 40,
            borderRadius: '50%', cursor: 'pointer',
          }}>✕</button>
        </div>
      )}
    </div>
  );
}

// ── Contacts Tab ─────────────────────────────────────────────────────────────

const DEFAULT_CONTACT_CATS = CONTACT_CAT_ORDER;
const CAT_ORDER = CONTACT_CAT_ORDER;
const EMPTY_CONTACT = { name: '', category: 'מנהלת הדיירים', role: '', phone: '', mobile: '', website: '', facebook: '', instagram: '', customCat: '' };

function ContactForm({ form, setForm, allCategories, buildings, onSubmit, onCancel, submitLabel }) {
  const isCustom = !DEFAULT_CONTACT_CATS.includes(form.category);
  return (
    <form onSubmit={onSubmit}>
      <div className="form-group">
        <label>שם / שם חברה *</label>
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required autoFocus placeholder="למשל: נורית כהן או משרד עו&quot;ד לוי" />
      </div>
      <div className="form-group">
        <label>קטגוריה</label>
        <select value={isCustom ? '__custom__' : form.category}
          onChange={e => setForm(f => ({ ...f, category: e.target.value === '__custom__' ? (f.customCat || '') : e.target.value, customCat: e.target.value === '__custom__' ? f.customCat : '' }))}>
          {DEFAULT_CONTACT_CATS.map(c => <option key={c} value={c}>{c}</option>)}
          {allCategories.filter(c => !DEFAULT_CONTACT_CATS.includes(c)).map(c => <option key={c} value={c}>{c}</option>)}
          <option value="__custom__">+ קטגוריה חדשה</option>
        </select>
        {(isCustom || form.category === '') && (
          <input placeholder="שם קטגוריה חדשה" value={form.customCat || form.category}
            onChange={e => setForm(f => ({ ...f, category: e.target.value, customCat: e.target.value }))}
            style={{ marginTop: 6 }} />
        )}
      </div>
      <div className="form-group">
        <label>תפקיד</label>
        <input value={form.role || ''} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} placeholder="למשל: מנהל פרויקט, עו&quot;ד" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div className="form-group">
          <label>טלפון 📞</label>
          <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} type="tel" placeholder="03-1234567" />
        </div>
        <div className="form-group">
          <label>פלאפון 💬</label>
          <input value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} type="tel" placeholder="050-1234567" />
        </div>
      </div>
      <div className="form-group">
        <label>אתר אינטרנט</label>
        <input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://..." />
      </div>
      <div className="form-group">
        <label>פייסבוק</label>
        <input value={form.facebook} onChange={e => setForm(f => ({ ...f, facebook: e.target.value }))} placeholder="https://facebook.com/..." />
      </div>
      <div className="form-group">
        <label>אינסטגרם</label>
        <input value={form.instagram} onChange={e => setForm(f => ({ ...f, instagram: e.target.value }))} placeholder="https://instagram.com/..." />
      </div>
      {buildings && buildings.length > 0 && (
        <div className="form-group">
          <label>הצג לבניין</label>
          <select value={form.building || ''} onChange={e => setForm(f => ({ ...f, building: e.target.value || null }))}>
            <option value="">כל הבניינים בפרויקט</option>
            {buildings.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary">{submitLabel}</button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>ביטול</button>
      </div>
    </form>
  );
}

// Drag-to-reposition, like moving a photo behind a circular mask (Facebook/
// LinkedIn profile-photo style) — click and drag the preview itself instead
// of fiddling with sliders.
// avatar_pos stores "X% Y%" as a translate() offset (0,0 = centered) — a
// leftover "50% 50%" from before this rework meant "centered" too under the
// old object-position semantics, so it also maps to (0,0) here.
function avatarTransform(pos, zoom) {
  const [x, y] = (!pos || pos === '50% 50%' ? '0% 0%' : pos).replace(/%/g, '').split(' ').map(Number);
  return `translate(${isNaN(x) ? 0 : x}%, ${isNaN(y) ? 0 : y}%) scale(${zoom || 1})`;
}

function DragReposition({ repositioning, setRepositioning, onSave, onCancel }) {
  const boxRef = React.useRef(null);
  const dragState = React.useRef(null); // { startX, startY, posX, posY }

  function onPointerDown(e) {
    e.preventDefault();
    const box = boxRef.current;
    if (!box) return;
    box.setPointerCapture(e.pointerId);
    // Dragging fast enough that the pointer leaves the circle would otherwise
    // let the browser fall back to its native text/image drag-selection on
    // whatever is underneath — suppress that globally for the drag's duration.
    document.body.style.userSelect = 'none';
    dragState.current = {
      startX: e.clientX, startY: e.clientY,
      posX: repositioning.x, posY: repositioning.y,
      w: box.clientWidth, h: box.clientHeight,
    };
  }
  function onPointerMove(e) {
    const d = dragState.current;
    if (!d) return;
    e.preventDefault();
    const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
    // dx/dy are converted to a percentage OF THE IMAGE'S OWN BOX (the img is
    // width:100%/height:100% of the circle in every place this renders), so
    // translate(x%, y%) means the same relative shift everywhere regardless
    // of the box's actual pixel size (28px in the list, 160px in this editor,
    // 42px in the tenant portal).
    const newX = Math.max(-50, Math.min(50, d.posX + (dx / d.w) * 100));
    const newY = Math.max(-50, Math.min(50, d.posY + (dy / d.h) * 100));
    setRepositioning(r => ({ ...r, x: newX, y: newY }));
  }
  function onPointerUp(e) {
    dragState.current = null;
    document.body.style.userSelect = '';
    boxRef.current?.releasePointerCapture(e.pointerId);
  }
  function onWheel(e) {
    e.preventDefault();
    const zoom = repositioning.zoom || 1;
    const next = Math.max(0.4, Math.min(3, zoom - e.deltaY * 0.001));
    setRepositioning(r => ({ ...r, zoom: next }));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '8px 0' }}>
      <div
        ref={boxRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onWheel={onWheel}
        style={{
          width: 160, height: 160, borderRadius: '50%', overflow: 'hidden',
          border: '2px solid #e5e7eb', background: '#f9fafb',
          cursor: 'grab', touchAction: 'none', userSelect: 'none',
        }}
      >
        <img
          src={repositioning.avatar_url}
          alt=""
          draggable={false}
          onDragStart={e => e.preventDefault()}
          style={{
            width: '100%', height: '100%', objectFit: 'contain',
            transform: `translate(${repositioning.x}%, ${repositioning.y}%) scale(${repositioning.zoom || 1})`,
            pointerEvents: 'none', WebkitUserDrag: 'none',
          }}
        />
      </div>
      <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>גררי כדי למקם, גלגלת עכבר כדי להגדיל/להקטין</div>
      <div style={{ display: 'flex', gap: 8, width: '100%' }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer' }}>ביטול</button>
        <button onClick={onSave} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: 'none', background: '#1d4ed8', color: 'white', fontWeight: 600, cursor: 'pointer' }}>שמור</button>
      </div>
    </div>
  );
}

function FloorplanMarkerEditor({ plan, onSave, onCancel }) {
  const [markers, setMarkers] = useState((plan.markers || []).map((m, i) => {
    let polygon_pct = null;
    try { polygon_pct = typeof m.polygon_pct === 'string' ? JSON.parse(m.polygon_pct) : (m.polygon_pct || null); } catch {}
    return {
      letter: m.letter, top_pct: Number(m.top_pct), left_pct: Number(m.left_pct),
      rect_left_pct: m.rect_left_pct != null ? Number(m.rect_left_pct) : null,
      rect_top_pct: m.rect_top_pct != null ? Number(m.rect_top_pct) : null,
      rect_width_pct: m.rect_width_pct != null ? Number(m.rect_width_pct) : null,
      rect_height_pct: m.rect_height_pct != null ? Number(m.rect_height_pct) : null,
      polygon_pct,
      // Default (before the admin ever drags it) mirrors the tenant-side
      // auto-placement so the handle starts near where the label currently
      // renders, instead of jumping to a surprising spot on first open.
      label_top_pct: m.label_top_pct != null ? Number(m.label_top_pct) : Math.min(Math.max(Number(m.top_pct) + 16, 14), 92),
      label_left_pct: m.label_left_pct != null ? Number(m.label_left_pct) : (Number(m.left_pct) > 80 ? 6 : 94),
      _key: i,
    };
  }));
  const [letterInput, setLetterInput] = useState('');
  const [drawingKey, setDrawingKey] = useState(null); // marker._key currently being outlined
  const imgRef = React.useRef(null);
  const drag = React.useRef(null); // { key, mode: 'dot'|'vertex', vertexIdx?, startX, startY, start }
  const clamp = v => Math.max(0, Math.min(100, v));

  function pctFromEvent(e) {
    const rect = imgRef.current.getBoundingClientRect();
    return {
      x: clamp(((e.clientX - rect.left) / rect.width) * 100),
      y: clamp(((e.clientY - rect.top) / rect.height) * 100),
    };
  }

  function onImgClick(e) {
    if (drawingKey != null) {
      const p = pctFromEvent(e);
      setMarkers(ms => ms.map(m => m._key === drawingKey ? { ...m, polygon_pct: [...(m.polygon_pct || []), p] } : m));
      return;
    }
    if (!letterInput.trim()) { alert('הזן אות דירה לפני הקלקה על התמונה'); return; }
    const letter = letterInput.trim();
    if (markers.some(m => m.letter === letter)) {
      alert(`הדירה ${letter} כבר מסומנת — גרור את הנקודה שלה, או לחץ על "+ קווים" ליד הנקודה כדי לצייר לה שטח`);
      return;
    }
    const { x: left_pct, y: top_pct } = pctFromEvent(e);
    const label_top_pct = Math.min(Math.max(top_pct + 16, 14), 92);
    const label_left_pct = left_pct > 80 ? 6 : 94;
    setMarkers(m => [...m, { letter, top_pct, left_pct, rect_left_pct: null, rect_top_pct: null, rect_width_pct: null, rect_height_pct: null, polygon_pct: null, label_top_pct, label_left_pct, _key: Date.now() }]);
    setLetterInput('');
  }
  function startDrag(key, mode, vertexIdx, e) {
    e.stopPropagation(); e.preventDefault();
    const m = markers.find(mm => mm._key === key);
    drag.current = { key, mode, vertexIdx, startX: e.clientX, startY: e.clientY, start: { ...m } };
    e.target.setPointerCapture(e.pointerId);
  }
  function onImgPointerMove(e) {
    if (!drag.current) return;
    const rectEl = imgRef.current.getBoundingClientRect();
    const dxPct = ((e.clientX - drag.current.startX) / rectEl.width) * 100;
    const dyPct = ((e.clientY - drag.current.startY) / rectEl.height) * 100;
    const { key, mode, vertexIdx, start } = drag.current;
    setMarkers(ms => ms.map(m => {
      if (m._key !== key) return m;
      if (mode === 'dot') return { ...m, top_pct: clamp(start.top_pct + dyPct), left_pct: clamp(start.left_pct + dxPct) };
      if (mode === 'label') return { ...m, label_top_pct: clamp(start.label_top_pct + dyPct), label_left_pct: clamp(start.label_left_pct + dxPct) };
      if (mode === 'vertex') {
        const pts = start.polygon_pct.map((pt, i) => i === vertexIdx
          ? { x: clamp(pt.x + dxPct), y: clamp(pt.y + dyPct) }
          : pt);
        return { ...m, polygon_pct: pts };
      }
      return m;
    }));
  }
  function onImgPointerUp() { drag.current = null; }
  function removeMarker(key) { setMarkers(ms => ms.filter(m => m._key !== key)); if (drawingKey === key) setDrawingKey(null); }
  function startDrawing(key) { setMarkers(ms => ms.map(m => m._key === key ? { ...m, polygon_pct: [] } : m)); setDrawingKey(key); }
  function removeVertex(key, idx) {
    setMarkers(ms => ms.map(m => {
      if (m._key !== key) return m;
      const pts = m.polygon_pct.filter((_, i) => i !== idx);
      return { ...m, polygon_pct: pts.length >= 3 ? pts : null };
    }));
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={letterInput} onChange={e => setLetterInput(e.target.value)} placeholder="אות דירה (למשל A)" disabled={drawingKey != null} style={{ width: 140, padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db' }} />
        {drawingKey != null ? (
          <>
            <span style={{ fontSize: '0.78rem', color: '#2563eb', fontWeight: 600 }}>מצב ציור: לחץ על התמונה כדי להוסיף נקודות לאורך קווי הדירה (בסדר היקפי). {(markers.find(m => m._key === drawingKey)?.polygon_pct || []).length} נקודות עד כה.</span>
            <button onClick={() => setDrawingKey(null)} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>✓ סיים ציור</button>
          </>
        ) : (
          <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>הזן אות ואז לחץ על מיקום היחידה בתמונה כדי להוסיף אותה. אחר כך לחץ "+ קווים" ליד הנקודה כדי לשרטט את קווי הדירה.</span>
        )}
      </div>
      <div
        style={{ position: 'relative', width: '100%', maxWidth: 700, margin: '0 auto', userSelect: 'none' }}
        onPointerMove={onImgPointerMove}
        onPointerUp={onImgPointerUp}
      >
        <img ref={imgRef} src={plan.image_url} alt="" draggable={false} onDragStart={e => e.preventDefault()}
          onClick={onImgClick}
          style={{ width: '100%', display: 'block', borderRadius: 8, cursor: 'crosshair' }} />
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} viewBox="0 0 100 100" preserveAspectRatio="none">
          {markers.map(m => (m.polygon_pct && m.polygon_pct.length >= 2) ? (
            <polygon key={m._key} points={m.polygon_pct.map(p => `${p.x},${p.y}`).join(' ')}
              fill="rgba(96,165,250,0.45)" stroke="#2563eb" strokeWidth="0.4" vectorEffect="non-scaling-stroke" />
          ) : null)}
          {/* Old-style rect markers (calibrated before line-drawing existed) */}
          {markers.map(m => (m.rect_width_pct != null && !m.polygon_pct) ? (
            <rect key={`r-${m._key}`} x={m.rect_left_pct} y={m.rect_top_pct} width={m.rect_width_pct} height={m.rect_height_pct}
              fill="rgba(96,165,250,0.45)" stroke="#2563eb" strokeWidth="0.4" vectorEffect="non-scaling-stroke" />
          ) : null)}
          {/* Dot-to-label connector — matches the line the tenant actually
              sees, so dragging the label here shows exactly what they'll get. */}
          {markers.map(m => (
            <line key={`l-${m._key}`} x1={m.left_pct} y1={m.top_pct} x2={m.label_left_pct} y2={m.label_top_pct}
              stroke="#7B1E2B" strokeWidth="0.35" vectorEffect="non-scaling-stroke" />
          ))}
        </svg>
        {markers.map(m => (
          <React.Fragment key={m._key}>
            {(m.polygon_pct || []).map((p, i) => (
              <div key={i}
                onPointerDown={e => startDrag(m._key, 'vertex', i, e)}
                onDoubleClick={() => removeVertex(m._key, i)}
                title="גרור להזזת הנקודה, לחיצה כפולה למחיקתה"
                style={{ position: 'absolute', top: `${p.y}%`, left: `${p.x}%`, transform: 'translate(-50%,-50%)', width: 12, height: 12, borderRadius: '50%', background: '#2563eb', border: '2px solid white', boxShadow: '0 0 0 1px #2563eb', cursor: 'grab', touchAction: 'none', zIndex: 3 }} />
            ))}
            <div
              onPointerDown={e => startDrag(m._key, 'dot', null, e)}
              onDoubleClick={() => removeMarker(m._key)}
              title="גרור למיקום החץ, לחיצה כפולה למחיקת הדירה"
              style={{ position: 'absolute', top: `${m.top_pct}%`, left: `${m.left_pct}%`, transform: 'translate(-50%,-50%)', width: 28, height: 28, borderRadius: '50%', background: '#dc2626', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.78rem', border: '2px solid white', boxShadow: '0 0 0 2px rgba(220,38,38,0.4)', cursor: 'grab', touchAction: 'none', zIndex: 2 }}>
              {m.letter}
            </div>
            {/* Draggable label pill — separate from the dot, so the admin can
                move the "זו הדירה שלך" text to an empty/white spot on the
                floorplan instead of leaving it wherever it auto-computes to. */}
            <div
              onPointerDown={e => startDrag(m._key, 'label', null, e)}
              title="גרור להזזת הכיתוב למקום פנוי בתמונה"
              style={{ position: 'absolute', top: `${m.label_top_pct}%`, left: `${m.label_left_pct}%`, transform: 'translate(-50%,-50%)', background: '#7B1E2B', color: 'white', fontWeight: 700, fontSize: '0.68rem', padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap', cursor: 'grab', touchAction: 'none', zIndex: 2, boxShadow: '0 2px 6px rgba(123,30,43,0.35)' }}>
              זו הדירה שלך ({m.letter})
            </div>
            {!m.polygon_pct && drawingKey == null && (
              <button
                onClick={() => startDrawing(m._key)}
                title="צייר את קווי שטח הדירה"
                style={{ position: 'absolute', top: `${m.top_pct}%`, left: `${m.left_pct}%`, transform: 'translate(6px, -22px)', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, padding: '2px 8px', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', zIndex: 3, whiteSpace: 'nowrap' }}>
                + קווים
              </button>
            )}
          </React.Fragment>
        ))}
      </div>
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 8 }}>🔵 השטח הכחול = קווי הדירה שיוצגו לדייר כרקע צבוע (לחץ "+ קווים" ליד הנקודה, לחץ על התמונה לאורך הקירות, "סיים ציור" בסוף — גרור נקודה לתקן, לחיצה כפולה עליה למחוק אותה). 🔴 הנקודה האדומה = מיקום קצה החץ "אתה כאן".</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer' }}>ביטול</button>
        <button onClick={() => onSave(markers.map(({ _key, ...m }) => m))} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: 'none', background: '#1d4ed8', color: 'white', fontWeight: 600, cursor: 'pointer' }}>שמור</button>
      </div>
    </div>
  );
}

function ContactsTab({ projectName }) {
  const [items, setItems] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_CONTACT });
  const [editForm, setEditForm] = useState({ ...EMPTY_CONTACT });
  const [editingCat, setEditingCat] = useState(null);
  const [repositioning, setRepositioning] = useState(null); // { id, avatar_url, x, y }
  const avatarInputRefs = React.useRef({});

  async function uploadContactAvatar(itemId, file) {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      await api.post(`/portal/contacts/${itemId}/avatar`, fd);
      load();
    } catch (err) { alert('שגיאה בהעלאת לוגו: ' + (err.response?.data?.error || err.message)); }
  }

  function openReposition(item) {
    // avatar_pos now stores a translate() offset in % (0,0 = centered), not the
    // old object-position semantics — a leftover "50% 50%" from before this
    // rework meant "centered" too, so it maps cleanly to the new (0,0).
    const raw = item.avatar_pos && item.avatar_pos !== '50% 50%' ? item.avatar_pos : '0% 0%';
    const [x, y] = raw.replace(/%/g, '').split(' ').map(Number);
    setRepositioning({ id: item.id, avatar_url: item.avatar_url, x: isNaN(x) ? 0 : x, y: isNaN(y) ? 0 : y, zoom: item.avatar_zoom ? Number(item.avatar_zoom) : 1 });
  }

  async function saveReposition() {
    const { id, x, y, zoom } = repositioning;
    // Drag produces fractional %s, but the server's pos regex only accepts
    // whole numbers — round before sending or the PATCH 400s silently.
    await api.patch(`/portal/contacts/${id}/avatar-pos`, { pos: `${Math.round(x)}% ${Math.round(y)}%`, zoom }).catch(() => {});
    setRepositioning(null);
    load();
  }
  const dragIdxRef = React.useRef(null);
  const cardDragRef = React.useRef(null);
  const [dragOverId, setDragOverId] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get(`/portal/contacts/${encodeURIComponent(projectName)}`);
    setItems(res.data);
  }, [projectName]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/portal/buildings/${encodeURIComponent(projectName)}`).then(r => setBuildings(r.data)).catch(() => {});
  }, [projectName]);

  const allCategories = [...new Set(items.map(i => i.category).filter(Boolean))];

  async function handleAdd(e) {
    e.preventDefault();
    await api.post(`/portal/contacts/${encodeURIComponent(projectName)}`, form);
    setShowAdd(false);
    setForm({ ...EMPTY_CONTACT });
    load();
  }

  async function handleEdit(e) {
    e.preventDefault();
    await api.patch(`/portal/contacts/${editing}`, { ...editForm });
    setEditing(null);
    load();
  }

  async function handleDelete(id) {
    if (!confirm('למחוק איש קשר זה?')) return;
    await api.delete(`/portal/contacts/${id}`);
    load();
  }

  async function saveCategoryRename(oldName, newName) {
    if (!newName.trim() || newName === oldName) { setEditingCat(null); return; }
    const toUpdate = items.filter(i => i.category === oldName);
    await Promise.all(toUpdate.map(item => api.patch(`/portal/contacts/${item.id}`, { ...item, category: newName.trim() })));
    setEditingCat(null);
    load();
  }

  // Group contacts by category (no sort — order comes from catOrder state)
  const groupedRaw = items.reduce((acc, item) => {
    const cat = item.category || 'כללי';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const allCats = Object.keys(groupedRaw);
  // catOrder: user-defined order. Start from DEFAULT order, then any extras appended.
  const [catOrder, setCatOrder] = React.useState([]);
  React.useEffect(() => {
    if (allCats.length === 0) return;
    setCatOrder(prev => {
      const existing = prev.filter(c => allCats.includes(c));
      const newCats = allCats.filter(c => !existing.includes(c));
      // sort new cats by CAT_ORDER default, then append
      newCats.sort((a, b) => {
        const ai = CAT_ORDER.indexOf(a), bi = CAT_ORDER.indexOf(b);
        if (ai === -1 && bi === -1) return a.localeCompare(b, 'he');
        if (ai === -1) return 1; if (bi === -1) return -1;
        return ai - bi;
      });
      return [...existing, ...newCats];
    });
  }, [items]); // eslint-disable-line

  const orderedCats = catOrder.filter(c => groupedRaw[c]);

  function onCatDragStart(e, cat) {
    dragIdxRef.current = cat;
    e.dataTransfer.effectAllowed = 'move';
  }

  async function onCardDrop(targetId, cat) {
    const fromId = cardDragRef.current;
    cardDragRef.current = null;
    setDragOverId(null);
    if (!fromId || fromId === targetId) return;
    const newItems = [...items];
    const fi = newItems.findIndex(i => i.id === fromId);
    const ti = newItems.findIndex(i => i.id === targetId);
    const [moved] = newItems.splice(fi, 1);
    newItems.splice(ti, 0, moved);
    setItems(newItems);
    await api.post('/portal/contacts/reorder', { ids: newItems.map(i => i.id) });
  }

  async function onCatDrop(e, targetCat) {
    e.preventDefault();
    const fromCat = dragIdxRef.current;
    dragIdxRef.current = null;
    setDragOverId(null);
    if (!fromCat || fromCat === targetCat) return;
    setCatOrder(prev => {
      const next = [...prev];
      const fi = next.indexOf(fromCat), ti = next.indexOf(targetCat);
      next.splice(fi, 1);
      next.splice(ti, 0, fromCat);
      // save new order to DB: flatten all contacts in new cat order
      const newItemOrder = next.flatMap(cat => groupedRaw[cat] || []);
      api.post('/portal/contacts/reorder', { ids: newItemOrder.map(i => i.id) }).catch(() => {});
      return next;
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ הוסף איש קשר</button>
      </div>
      {items.length === 0 && <div style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>אין אנשי קשר עדיין</div>}

      {orderedCats.map(cat => (
        <div key={cat}
          draggable
          onDragStart={e => onCatDragStart(e, cat)}
          onDragOver={e => { e.preventDefault(); setDragOverId(cat); }}
          onDragLeave={() => setDragOverId(null)}
          onDrop={e => onCatDrop(e, cat)}
          style={{ marginBottom: 24, opacity: dragIdxRef.current === cat ? 0.5 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: `2px solid ${dragOverId === cat ? '#3b82f6' : '#e5e7eb'}`, paddingBottom: 6, marginBottom: 10, cursor: 'grab' }}>
            <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>⠿</span>
            {editingCat && editingCat.oldName === cat ? (
              <>
                <input autoFocus value={editingCat.newName} onChange={e => setEditingCat(ec => ({ ...ec, newName: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') saveCategoryRename(cat, editingCat.newName); if (e.key === 'Escape') setEditingCat(null); }}
                  onClick={e => e.stopPropagation()}
                  style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1B2A4A', border: '1px solid #3b82f6', borderRadius: 6, padding: '2px 8px', outline: 'none' }} />
                <button onClick={e => { e.stopPropagation(); saveCategoryRename(cat, editingCat.newName); }} style={{ background: '#3b82f6', border: 'none', color: '#fff', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: '0.8rem' }}>✓</button>
                <button onClick={e => { e.stopPropagation(); setEditingCat(null); }} style={{ background: '#e5e7eb', border: 'none', borderRadius: 6, padding: '2px 6px', cursor: 'pointer', fontSize: '0.8rem' }}>✕</button>
              </>
            ) : (
              <>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1B2A4A' }}>{cat}</span>
                <button onClick={e => { e.stopPropagation(); setEditingCat({ oldName: cat, newName: cat }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', padding: 0 }} title="שנה שם קטגוריה">✏️</button>
              </>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
            {(groupedRaw[cat] || []).map(item => (
              <div key={item.id}
                draggable
                onDragStart={e => { e.stopPropagation(); cardDragRef.current = item.id; e.dataTransfer.effectAllowed = 'move'; }}
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverId('card-' + item.id); }}
                onDragLeave={e => { e.stopPropagation(); setDragOverId(null); }}
                onDrop={e => { e.preventDefault(); e.stopPropagation(); onCardDrop(item.id, cat); }}
                style={{ background: '#fff', borderRadius: 12, padding: 14, boxShadow: dragOverId === 'card-' + item.id ? '0 0 0 2px #3b82f6' : '0 1px 4px rgba(0,0,0,0.08)', cursor: 'grab', transition: 'box-shadow 0.15s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ position: 'relative', width: 28, height: 28, flexShrink: 0 }}>
                      <div
                        onClick={() => avatarInputRefs.current[item.id]?.click()}
                        title="העלה לוגו"
                        style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', background: '#f3f4f6', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                      >
                        {item.avatar_url
                          ? <img src={item.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: avatarTransform(item.avatar_pos, item.avatar_zoom) }} />
                          : item.website
                          ? <img src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(item.website)}&sz=64`}
                              alt="" style={{ width: '70%', height: '70%', objectFit: 'contain' }}
                              onError={e => { e.target.style.display = 'none'; }} />
                          : <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>+</span>}
                      </div>
                      <input
                        ref={el => { avatarInputRefs.current[item.id] = el; }}
                        type="file" accept="image/*" hidden
                        onChange={e => { uploadContactAvatar(item.id, e.target.files[0]); e.target.value = ''; }}
                      />
                    </div>
                    {item.name}
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {item.avatar_url && <button onClick={() => openReposition(item)} title="כוון מיקום לוגו" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}>🎯</button>}
                    <button onClick={() => { setEditing(item.id); setEditForm({ name: item.name || '', category: item.category || 'יזם', role: item.role || '', phone: item.phone || '', mobile: item.mobile || '', website: item.website || '', facebook: item.facebook || '', instagram: item.instagram || '', customCat: '', building: item.building || null }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}>✏️</button>
                    <button onClick={() => handleDelete(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '0.85rem' }}>🗑️</button>
                  </div>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {item.role && <span style={{ color: '#374151', fontWeight: 500 }}>{item.role}</span>}
                  {item.website && <a href={item.website} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>🌐 אתר אינטרנט</a>}
                  {item.phone && <span>📞 {item.phone}</span>}
                  {item.mobile && <span>💬 {item.mobile}</span>}
                  {item.facebook && <a href={item.facebook} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>📘 פייסבוק</a>}
                  {item.instagram && <a href={item.instagram} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>📸 אינסטגרם</a>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {showAdd && (
        <Modal title="הוספת איש קשר" onClose={() => setShowAdd(false)}>
          <ContactForm form={form} setForm={setForm} allCategories={allCategories} buildings={buildings} onSubmit={handleAdd} onCancel={() => setShowAdd(false)} submitLabel="הוסף" />
        </Modal>
      )}
      {editing && (
        <Modal title="עריכת איש קשר" onClose={() => setEditing(null)}>
          <ContactForm form={editForm} setForm={setEditForm} allCategories={allCategories} buildings={buildings} onSubmit={handleEdit} onCancel={() => setEditing(null)} submitLabel="שמור" />
        </Modal>
      )}
      {repositioning && (
        <Modal title="כיוון מיקום לוגו" onClose={() => setRepositioning(null)}>
          <DragReposition repositioning={repositioning} setRepositioning={setRepositioning} onSave={saveReposition} onCancel={() => setRepositioning(null)} />
        </Modal>
      )}
    </div>
  );
}

// ── Files Tab ────────────────────────────────────────────────────────────────

function FilesTab({ projectName, initialFilterCat }) {
  const [files, setFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [showCatForm, setShowCatForm] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [category, setCategory] = useState('');
  const [recipient, setRecipient] = useState('all');
  const [specificTenant, setSpecificTenant] = useState(null);
  const [selectedBuildings, setSelectedBuildings] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [buildingSearch, setBuildingSearch] = useState('');
  const [fileDate, setFileDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [editingFile, setEditingFile] = useState(null); // { id, name, category, display_date, recipientType, buildings }
  const [editBuildingSearch, setEditBuildingSearch] = useState('');
  const [filterCat, setFilterCat] = useState(initialFilterCat || 'all');
  const [fileSearch, setFileSearch] = useState('');
  const fileInputRef = useRef(null);

  function fileMatchesSearch(f, q) {
    if (!q) return true;
    const query = q.trim().toLowerCase();
    if (!query) return true;
    const name = (f.original_name || f.name || '').toLowerCase();
    if (name.includes(query)) return true;
    const d = f.file_date || f.display_date || f.created_at;
    if (!d) return false;
    return new Date(d).toLocaleDateString('he-IL').includes(query);
  }

  const loadFiles = useCallback(async () => {
    const res = await api.get(`/portal/files/${encodeURIComponent(projectName)}`);
    setFiles(res.data);
  }, [projectName]);

  const loadCats = useCallback(async () => {
    const res = await api.get(`/portal/file-categories/${encodeURIComponent(projectName)}`);
    setCategories(res.data);
  }, [projectName]);

  useEffect(() => {
    loadFiles(); loadCats();
    api.get(`/portal/buildings/${encodeURIComponent(projectName)}`).then(r => setBuildings(r.data || [])).catch(() => {});
  }, [loadFiles, loadCats, projectName]);

  function onFileSelect(e) { setSelectedFiles(Array.from(e.target.files)); }
  function onDrop(e) {
    e.preventDefault(); setDragging(false);
    setSelectedFiles(Array.from(e.dataTransfer.files));
  }

  async function upload() {
    if (!selectedFiles.length) return alert('יש לבחור קבצים');
    if (recipient === 'specific' && !specificTenant) return alert('יש לבחור דייר');
    if (recipient === 'building' && !selectedBuildings.length) return alert('יש לבחור לפחות בניין אחד');
    const tooBig = selectedFiles.filter(f => f.size > 150 * 1024 * 1024);
    if (tooBig.length) return alert(`קבצים גדולים מדי (מעל 150MB):\n${tooBig.map(f => f.name).join('\n')}`);
    setUploading(true);
    setUploadPct(0);
    const recipientList = recipient === 'building' ? selectedBuildings : ['all'];
    try {
      const total = selectedFiles.reduce((s, f) => s + f.size, 0);
      let uploaded = 0;
      for (const file of selectedFiles) {
        const { data: presign } = await api.post('/portal/presign-file', {
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          projectName,
        });
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', presign.uploadUrl);
          xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
          xhr.upload.onprogress = e => {
            if (e.lengthComputable) setUploadPct(Math.round((uploaded + e.loaded) / total * 100));
          };
          xhr.onload = () => { uploaded += file.size; resolve(); };
          xhr.onerror = reject;
          xhr.send(file);
        });
        let rec, recName;
        if (recipient === 'specific') {
          rec = `specific:${(specificTenant.phone || specificTenant.cellularPhone || '').trim()}`;
          recName = `${specificTenant.firstName || ''} ${specificTenant.lastName || ''}`.trim();
        } else if (recipient === 'building') {
          rec = selectedBuildings.map(b => `building:${b}`).join(',');
          recName = selectedBuildings.join(', ');
        } else {
          rec = 'all'; recName = null;
        }
        await api.post(`/portal/files/${encodeURIComponent(projectName)}/confirm`, {
          key: presign.key, s3Url: presign.s3Url,
          originalName: file.name, mimeType: file.type, fileSize: file.size,
          category: category || null, recipient: rec, recipientName: recName,
          workerName: getWorkerInfo().name, fileDate: fileDate || null,
        });
      }
      setSelectedFiles([]); setShowUpload(false); setRecipient('all'); setSpecificTenant(null); setSelectedBuildings([]); setCategory('');
      loadFiles();
    } catch { alert('שגיאה בהעלאה'); }
    setUploading(false); setUploadPct(0);
  }

  async function addCategory() {
    if (!newCat.trim()) return;
    await api.post(`/portal/file-categories/${encodeURIComponent(projectName)}`, { name: newCat.trim() });
    setNewCat(''); setShowCatForm(false); loadCats();
  }

  async function deleteCategory(id) {
    if (!confirm('למחוק קטגוריה זו?')) return;
    await api.delete(`/portal/file-categories/${id}`); loadCats();
  }

  async function deleteFile(id) {
    if (!confirm('למחוק קובץ זה?')) return;
    await api.delete(`/portal/files/${id}`); loadFiles();
  }

  function stripExt(name) { return name ? name.replace(/\.[^/.]+$/, '') : ''; }

  async function saveFileEdit(id, name, category, display_date, recipient) {
    await api.patch(`/portal/files/${id}`, { name, category, display_date, recipient });
    setEditingFile(null); loadFiles();
  }

  function fileIcon(mime) {
    if (!mime) return '📄';
    if (mime.startsWith('image/')) return '🖼️';
    if (mime === 'application/pdf') return <PdfIcon size={22} />;
    if (mime.includes('word')) return '📝';
    if (mime.includes('excel') || mime.includes('spreadsheet')) return '📊';
    return '📄';
  }

  function formatSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  }

  const allCats = [...new Set(files.map(f => f.category || 'ללא קטגוריה'))];
  const filteredFiles = (filterCat === 'all' ? files : files.filter(f => (f.category || 'ללא קטגוריה') === filterCat)).filter(f => fileMatchesSearch(f, fileSearch));
  const grouped = filteredFiles.reduce((acc, f) => {
    const cat = f.category || 'ללא קטגוריה';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(f);
    return acc;
  }, {});

  const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', boxSizing: 'border-box', fontSize: '0.9rem' };
  const sectionStyle = { background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e5e7eb' };

  return (
    <div>
      {/* Action bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <button className="btn btn-primary" onClick={() => setShowUpload(v => !v)}>📤 העלאת קבצים</button>
        <input
          value={fileSearch}
          onChange={e => setFileSearch(e.target.value)}
          placeholder="🔍 חפש קובץ לפי שם או תאריך"
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.85rem', outline: 'none' }}
        />
      </div>


      {/* Filter chips */}
      {allCats.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {['all', ...allCats].map(cat => (
            <button key={cat} onClick={() => setFilterCat(cat)} style={{
              background: filterCat === cat ? '#1a4a8a' : '#f3f4f6',
              color: filterCat === cat ? '#fff' : '#374151',
              border: 'none', borderRadius: 20, padding: '6px 16px',
              fontSize: '0.82rem', fontWeight: filterCat === cat ? 700 : 500,
              cursor: 'pointer', transition: 'all 0.15s',
            }}>
              {cat === 'all' ? `הכל (${files.length})` : `${cat} (${files.filter(f => (f.category || 'ללא קטגוריה') === cat).length})`}
            </button>
          ))}
        </div>
      )}

      {/* Upload panel */}
      {showUpload && (
        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, marginBottom: 14, color: '#1f2937' }}>העלאת קבצים</div>

          {/* Category */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>קטגוריה</label>
            <select value={category} onChange={e => { if (e.target.value === '__new__') { setShowCatForm(true); setCategory(''); } else { setShowCatForm(false); setCategory(e.target.value); } }} style={{ ...inputStyle }}>
              <option value="">ללא קטגוריה</option>
              {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              <option value="__new__">+ קטגוריה חדשה</option>
            </select>
            {showCatForm && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input autoFocus placeholder="שם קטגוריה חדשה" value={newCat} onChange={e => setNewCat(e.target.value)}
                  onKeyDown={async e => { if (e.key === 'Enter') { await addCategory(); setCategory(newCat.trim()); } }}
                  style={{ ...inputStyle, marginBottom: 0, flex: 1 }} />
                <button className="btn btn-primary" onClick={async () => { await addCategory(); setCategory(newCat.trim()); }}>הוסף</button>
                <button className="btn btn-secondary" onClick={() => { setShowCatForm(false); setNewCat(''); }}>ביטול</button>
              </div>
            )}
          </div>

          {/* Drop zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            style={{
              border: `2px dashed ${dragging ? '#4f46e5' : '#d1d5db'}`,
              borderRadius: 10, padding: '28px 16px', textAlign: 'center',
              cursor: 'pointer', marginBottom: 14, background: dragging ? '#eef2ff' : '#fafafa',
              transition: 'all 0.15s'
            }}>
            <div style={{ fontSize: '2rem', marginBottom: 6 }}>📁</div>
            <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              {selectedFiles.length ? `נבחרו ${selectedFiles.length} קבצים` : 'גרור קבצים לכאן או לחץ לבחירה'}
            </div>
            {!selectedFiles.length && <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: 4 }}>מקסימום 150 מ"ב לקובץ</div>}
            {selectedFiles.length > 0 && (
              <div style={{ marginTop: 8, fontSize: '0.78rem', color: '#4f46e5' }}>
                {selectedFiles.map(f => f.name).join(', ')}
              </div>
            )}
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={onFileSelect} />
          </div>

          {/* Recipient */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>נמען</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: (recipient === 'specific' || recipient === 'building') ? 10 : 0, flexWrap: 'wrap' }}>
              {[['all','כל הדיירים'],['building','בניין'],['specific','דייר ספציפי']].map(([v,lbl]) => (
                <button key={v} onClick={() => setRecipient(v)}
                  style={{ flex: 1, minWidth: 90, padding: '8px', borderRadius: 8, border: `2px solid ${recipient === v ? '#4f46e5' : '#e5e7eb'}`, background: recipient === v ? '#eef2ff' : 'white', cursor: 'pointer', fontWeight: 600, color: recipient === v ? '#4f46e5' : '#374151' }}>
                  {lbl}
                </button>
              ))}
            </div>
            {recipient === 'building' && (
              <div style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', maxHeight: 160, overflowY: 'auto', background: 'white' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => setSelectedBuildings(buildings.filter(b => b.toLowerCase().includes(buildingSearch.toLowerCase())))} style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer' }}>בחר הכל</button>
                  <button onClick={() => setSelectedBuildings([])} style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer' }}>נקה</button>
                  <input value={buildingSearch} onChange={e => setBuildingSearch(e.target.value)} placeholder="🔍 חפש בניין..." style={{ flex: 1, minWidth: 100, padding: '2px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.8rem', outline: 'none' }} />
                </div>
                {buildings.filter(b => b.toLowerCase().includes(buildingSearch.toLowerCase())).map(b => (
                  <label key={b} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: '0.9rem' }}>
                    <input type="checkbox" checked={selectedBuildings.includes(b)}
                      onChange={e => setSelectedBuildings(prev => e.target.checked ? [...prev, b] : prev.filter(x => x !== b))}
                      style={{ width: 16, height: 16 }} />
                    {b}
                  </label>
                ))}
              </div>
            )}
            {recipient === 'specific' && (
              <TenantSearch projectName={projectName}
                onSelect={t => setSpecificTenant(t)}
                selectedLabel={specificTenant ? `${specificTenant.firstName} ${specificTenant.lastName} (${specificTenant.phone || specificTenant.cellularPhone || ''})` : ''} />
            )}
          </div>

          {uploading && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#6b7280', marginBottom: 4 }}>
                <span>מעלה לשרת...</span><span>{uploadPct}%</span>
              </div>
              <div style={{ background: '#e5e7eb', borderRadius: 99, height: 8 }}>
                <div style={{ width: `${uploadPct}%`, height: '100%', background: '#4f46e5', borderRadius: 99, transition: 'width 0.3s' }} />
              </div>
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>תאריך הקובץ</label>
            <input type="date" value={fileDate} onChange={e => setFileDate(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.9rem', width: '100%', direction: 'ltr' }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={upload} disabled={uploading}>
              {uploading ? `מעלה... ${uploadPct}%` : `העלה ${selectedFiles.length ? `(${selectedFiles.length})` : ''}`}
            </button>
            <button className="btn btn-secondary" onClick={() => { setShowUpload(false); setSelectedFiles([]); }}>ביטול</button>
          </div>
        </div>
      )}

      {/* Files list grouped by category */}
      {files.length === 0 && <div style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>אין קבצים עדיין</div>}
      {Object.entries(grouped).map(([cat, catFiles]) => (
        <div key={cat} style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, borderBottom: '1px solid #e5e7eb', paddingBottom: 5 }}>{cat}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[...catFiles].sort((a, b) => {
                              const da = a.file_date || a.display_date;
                              const db = b.file_date || b.display_date;
                              if (da && db) return new Date(da) - new Date(db);
                              if (da) return -1; if (db) return 1;
                              return a.id - b.id;
                            }).map(f => {
              const displayDate = f.file_date
                ? new Date(f.file_date).toLocaleDateString('he-IL')
                : f.display_date
                  ? new Date(f.display_date).toLocaleDateString('he-IL')
                  : new Date(f.created_at).toLocaleDateString('he-IL');
              return (
              <div key={f.id} style={{ background: 'white', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                {editingFile?.id === f.id ? (
                  <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input value={editingFile.name}
                      onChange={e => setEditingFile(p => ({ ...p, name: e.target.value }))}
                      style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.9rem' }}
                      placeholder="שם קובץ" />
                    <select value={editingFile.category || ''}
                      onChange={e => setEditingFile(p => ({ ...p, category: e.target.value }))}
                      style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.9rem' }}>
                      <option value="">ללא קטגוריה</option>
                      {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                    <div>
                      <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>תאריך</label>
                      <input type="date" value={editingFile.display_date || ''}
                        onChange={e => setEditingFile(p => ({ ...p, display_date: e.target.value }))}
                        style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.9rem', direction: 'ltr' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>למי מוצג</label>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                        {[['all','כל הדיירים'],['building','בניין ספציפי']].map(([v,lbl]) => (
                          <button key={v} onClick={() => setEditingFile(p => ({ ...p, recipientType: v }))}
                            style={{ flex: 1, padding: '6px', borderRadius: 6, border: `2px solid ${editingFile.recipientType === v ? '#4f46e5' : '#e5e7eb'}`, background: editingFile.recipientType === v ? '#eef2ff' : 'white', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', color: editingFile.recipientType === v ? '#4f46e5' : '#374151' }}>
                            {lbl}
                          </button>
                        ))}
                      </div>
                      {editingFile.recipientType === 'building' && (
                        <div style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', maxHeight: 140, overflowY: 'auto', background: 'white' }}>
                          <input value={editBuildingSearch} onChange={e => setEditBuildingSearch(e.target.value)} placeholder="🔍 חפש בניין..." style={{ width: '100%', padding: '2px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.8rem', outline: 'none', marginBottom: 6 }} />
                          {buildings.filter(b => b.toLowerCase().includes(editBuildingSearch.toLowerCase())).map(b => (
                            <label key={b} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', cursor: 'pointer', fontSize: '0.85rem' }}>
                              <input type="checkbox" checked={editingFile.buildings.includes(b)}
                                onChange={e => setEditingFile(p => ({ ...p, buildings: e.target.checked ? [...p.buildings, b] : p.buildings.filter(x => x !== b) }))}
                                style={{ width: 14, height: 14 }} />
                              {b}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '5px 14px' }}
                        onClick={() => saveFileEdit(f.id, editingFile.name, editingFile.category, editingFile.display_date, editingFile.recipientType === 'building' ? editingFile.buildings.map(b => `building:${b}`).join(',') : 'all')}>שמור</button>
                      <button className="btn btn-secondary" style={{ fontSize: '0.82rem', padding: '5px 14px' }}
                        onClick={() => setEditingFile(null)}>ביטול</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{fileIcon(f.mime_type)}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: '#1f2937', fontSize: '0.9rem', overflowWrap: 'break-word' }}>{stripExt(f.original_name || f.name)}</div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 2 }}>
                          {displayDate}
                          {f.recipient && f.recipient !== 'all' && (
                            <span style={{ marginRight: 6, color: '#7c3aed' }}>· 👤 {f.recipient_name || 'דייר ספציפי'}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <a href={`${f.url}?auth=${localStorage.getItem('token') || ''}`} target="_blank" rel="noreferrer" title="צפה" style={{ display: 'flex', alignItems: 'center', padding: '5px' }}><EyeIcon /></a>
                      <a href={`${f.url}?auth=${localStorage.getItem('token') || ''}`} target="_blank" rel="noreferrer" download title="הורד" style={{ display: 'flex', alignItems: 'center', padding: '5px' }}><DownloadIcon /></a>
                      <button onClick={() => {
                        const rec = f.recipient || 'all';
                        const isBuilding = rec.startsWith('building:') || (rec.includes(',') && rec.split(',').every(r => r.startsWith('building:')));
                        const buildingsFromRecipient = isBuilding ? rec.split(',').map(r => r.replace('building:', '')) : [];
                        setEditBuildingSearch('');
                        setEditingFile({ id: f.id, name: stripExt(f.original_name || f.name), category: f.category || '', display_date: (f.file_date || f.display_date || '').toString().slice(0,10) || new Date(f.created_at).toISOString().slice(0,10), recipientType: isBuilding ? 'building' : 'all', buildings: buildingsFromRecipient });
                      }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>✏️</button>
                      <button onClick={() => deleteFile(f.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '1rem' }}>🗑</button>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── About Manhelet Tab (managing company — separate from the developer) ──────
// Deliberately a lighter, single light card look (photo + logo stacked, then
// text) instead of AboutTab's dark hero band, so the two "About" tabs read as
// visually distinct sections to the tenant.

// ── Worker Digital Business Card (trial — "my card" self-service tab) ────────
function AboutManheletTab({ projectName }) {
  const [manhelet, setManhelet] = useState({});
  const [projects, setProjects] = useState([]);
  const [press, setPress] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [editingProject, setEditingProject] = useState(null);
  const [newProject, setNewProject] = useState(null);
  const [editingPress, setEditingPress] = useState(null);
  const [newPress, setNewPress] = useState(null);
  const [photoRepositioning, setPhotoRepositioning] = useState(null);
  // The reposition modal zooms via mouse wheel — without locking body scroll,
  // that same wheel event also scrolls the page behind the modal.
  useEffect(() => {
    if (!photoRepositioning) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [photoRepositioning]);

  const load = useCallback(async () => {
    api.get(`/portal/about/manhelet/${encodeURIComponent(projectName)}`).then(r => setManhelet(r.data || {})).catch(() => {});
    api.get(`/portal/about/manhelet-projects/${encodeURIComponent(projectName)}`).then(r => setProjects(r.data)).catch(() => {});
    api.get(`/portal/about/manhelet-press/${encodeURIComponent(projectName)}`).then(r => setPress(r.data)).catch(() => {});
  }, [projectName]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    await api.post(`/portal/about/manhelet/${encodeURIComponent(projectName)}`, form);
    setEditing(false); load();
  }

  async function uploadPhoto(file) {
    if (!file) return;
    const fd = new FormData(); fd.append('photo', file);
    const r = await api.post('/portal/about/upload-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setForm(f => ({ ...f, photo_url: r.data.url }));
  }
  async function uploadLogo(file) {
    if (!file) return;
    const fd = new FormData(); fd.append('photo', file);
    const r = await api.post('/portal/about/upload-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setForm(f => ({ ...f, logo_url: r.data.url }));
  }

  async function saveProject(id, data) {
    if (id) await api.patch(`/portal/about/manhelet-projects/${id}`, data);
    else await api.post(`/portal/about/manhelet-projects/${encodeURIComponent(projectName)}`, data);
    setEditingProject(null); setNewProject(null); load();
  }
  async function deleteProject(id) {
    if (!confirm('למחוק פרויקט זה?')) return;
    await api.delete(`/portal/about/manhelet-projects/${id}`); load();
  }
  async function savePress(id, data) {
    if (id) await api.patch(`/portal/about/manhelet-press/${id}`, data);
    else await api.post(`/portal/about/manhelet-press/${encodeURIComponent(projectName)}`, data);
    setEditingPress(null); setNewPress(null); load();
  }
  async function deletePress(id) {
    if (!confirm('למחוק כתבה זו?')) return;
    await api.delete(`/portal/about/manhelet-press/${id}`); load();
  }

  // Drag-to-reorder — same pattern as the contacts list: drag a card, drop it
  // onto another to swap positions, then persist the new order to the server.
  const dragProjectRef = React.useRef(null);
  const dragPressRef = React.useRef(null);
  const [dragOverProjectId, setDragOverProjectId] = useState(null);
  const [dragOverPressId, setDragOverPressId] = useState(null);

  async function onProjectDrop(targetId) {
    const fromId = dragProjectRef.current;
    dragProjectRef.current = null; setDragOverProjectId(null);
    if (!fromId || fromId === targetId) return;
    const ids = projects.map(p => p.id);
    const fromIdx = ids.indexOf(fromId), toIdx = ids.indexOf(targetId);
    ids.splice(toIdx, 0, ids.splice(fromIdx, 1)[0]);
    setProjects(ids.map(id => projects.find(p => p.id === id)));
    await api.post('/portal/about/manhelet-projects/reorder', { ids }).catch(() => {});
  }
  async function onPressDrop(targetId) {
    const fromId = dragPressRef.current;
    dragPressRef.current = null; setDragOverPressId(null);
    if (!fromId || fromId === targetId) return;
    const ids = press.map(p => p.id);
    const fromIdx = ids.indexOf(fromId), toIdx = ids.indexOf(targetId);
    ids.splice(toIdx, 0, ids.splice(fromIdx, 1)[0]);
    setPress(ids.map(id => press.find(p => p.id === id)));
    await api.post('/portal/about/manhelet-press/reorder', { ids }).catch(() => {});
  }

  const cardStyle = { background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb', marginBottom: 16 };
  const headStyle = { fontWeight: 700, fontSize: '1rem', color: '#1f2937', marginBottom: 14, borderBottom: '1px solid #e5e7eb', paddingBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
  const inp = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.9rem', marginBottom: 8, boxSizing: 'border-box' };
  const editBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', padding: '2px 6px', color: '#6b7280' };
  const lightTA = { background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: 8, color: '#374151', fontFamily: 'inherit', fontSize: '0.9rem', lineHeight: 1.8, outline: 'none', width: '100%', padding: '8px 10px', resize: 'vertical', boxSizing: 'border-box' };

  return (
    <div>
      {/* Manhelet — single light card: photo, logo below it, then text */}
      <div style={cardStyle}>
        <div style={headStyle}>
          <span>🏢 אודות המנהלת</span>
          {!editing
            ? <button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '5px 14px' }} onClick={() => { setForm({ ...manhelet }); setEditing(true); }}>✏️ עריכה</button>
            : <div style={{ display: 'flex', gap: 8 }}><button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '5px 14px' }} onClick={save}>שמור</button><button className="btn btn-secondary" style={{ fontSize: '0.82rem', padding: '5px 14px' }} onClick={() => setEditing(false)}>ביטול</button></div>
          }
        </div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {/* Logo — displayed above the photo, deliberately smaller & square */}
            <div style={{ width: 72, height: 72, borderRadius: 10, overflow: 'hidden', background: '#f9fafb', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {(editing ? form.logo_url : manhelet.logo_url)
                ? <img src={editing ? form.logo_url : manhelet.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                : <span style={{ fontSize: '0.6rem', color: '#9ca3af' }}>לוגו</span>}
            </div>
            {editing && <label style={{ cursor: 'pointer', fontSize: '0.75rem', color: '#1d4ed8' }}>
              {form.logo_url ? 'החלף לוגו' : '+ לוגו'}
              <input type="file" accept="image/*" hidden onChange={e => uploadLogo(e.target.files[0])} />
            </label>}
            <div style={{ width: 140, height: 140, borderRadius: '50%', overflow: 'hidden', background: '#f3f4f6', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 4 }}>
              {(editing ? form.photo_url : manhelet.photo_url)
                ? <img src={editing ? form.photo_url : manhelet.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: avatarTransform(editing ? form.photo_position : manhelet.photo_position, editing ? form.photo_zoom : manhelet.photo_zoom) }} />
                : <span style={{ fontSize: '2.5rem' }}>🏢</span>}
            </div>
            {editing && form.photo_url && (
              <button type="button" onClick={() => setPhotoRepositioning({ avatar_url: form.photo_url, x: (parseFloat((form.photo_position || '0% 0%').split(' ')[0]) || 0), y: (parseFloat((form.photo_position || '0% 0%').split(' ')[1]) || 0), zoom: form.photo_zoom || 1 })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: '#6b7280', padding: 0 }}>
                ✏️ ערוך תמונה (זום/מיקום)
              </button>
            )}
            {editing && <label style={{ cursor: 'pointer', fontSize: '0.75rem', color: '#1d4ed8' }}>
              {form.photo_url ? 'החלף תמונה' : '+ תמונה'}
              <input type="file" accept="image/*" hidden onChange={e => uploadPhoto(e.target.files[0])} />
            </label>}
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            {editing ? (
              <>
                <input style={inp} placeholder="שם המנהלת" value={form.manhelet_name || ''} onChange={e => setForm(f => ({ ...f, manhelet_name: e.target.value }))} />
                <input style={inp} placeholder="תפקיד / תיאור קצר" value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                <textarea style={lightTA} rows={5} placeholder="תיאור" value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </>
            ) : (
              <>
                {manhelet.manhelet_name && <div style={{ fontWeight: 700, fontSize: '1.2rem', color: '#1f2937' }}>{manhelet.manhelet_name}</div>}
                {manhelet.title && <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 4 }}>{manhelet.title}</div>}
                {manhelet.description && <div style={{ fontSize: '0.9rem', color: '#374151', marginTop: 10, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{manhelet.description}</div>}
                {!manhelet.manhelet_name && !manhelet.description && <div style={{ color: '#9ca3af' }}>אין תוכן עדיין</div>}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Projects */}
      <div style={cardStyle}>
        <div style={headStyle}>
          <span>📌 פרויקטים</span>
          <button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '5px 12px' }} onClick={() => setNewProject({ name: '', city: '', url: '' })}>+ הוסף</button>
        </div>
        {newProject && (
          <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, marginBottom: 10 }}>
            <input style={inp} placeholder="שם פרויקט" value={newProject.name} onChange={e => setNewProject(p => ({ ...p, name: e.target.value }))} />
            <input style={inp} placeholder="עיר" value={newProject.city} onChange={e => setNewProject(p => ({ ...p, city: e.target.value }))} />
            <input style={inp} placeholder="קישור לאתר" value={newProject.url} onChange={e => setNewProject(p => ({ ...p, url: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={() => saveProject(null, newProject)}>הוסף</button>
              <button className="btn btn-secondary" onClick={() => setNewProject(null)}>ביטול</button>
            </div>
          </div>
        )}
        {projects.map(p => (
          <div key={p.id}
            draggable={editingProject !== p.id}
            onDragStart={() => { dragProjectRef.current = p.id; }}
            onDragOver={e => { e.preventDefault(); setDragOverProjectId(p.id); }}
            onDragLeave={() => setDragOverProjectId(null)}
            onDrop={() => onProjectDrop(p.id)}
            style={{ borderBottom: '1px solid #f3f4f6', padding: '10px 0', cursor: editingProject === p.id ? 'default' : 'grab', boxShadow: dragOverProjectId === p.id ? '0 0 0 2px #3b82f6' : 'none' }}>
            {editingProject === p.id ? (
              <ProjectEditForm item={p} onSave={data => saveProject(p.id, data)} onCancel={() => setEditingProject(null)} inp={inp} />
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#d1d5db', fontSize: '0.9rem' }}>⠿</span>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{p.name}</div>
                    {p.city && <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>📍 {p.city}</div>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {p.url && <a href={p.url} target="_blank" rel="noreferrer" style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: '5px 12px', fontSize: '0.82rem', color: '#1a4a8a', textDecoration: 'none' }}>קישור לאתר</a>}
                  <button style={editBtn} onClick={() => setEditingProject(p.id)}>✏️</button>
                  <button style={{ ...editBtn, color: '#ef4444' }} onClick={() => deleteProject(p.id)}>🗑️</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {projects.length === 0 && !newProject && <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>אין פרויקטים עדיין</div>}
        {projects.length > 1 && <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 6 }}>ניתן לגרור ⠿ כדי לשנות סדר</div>}
      </div>

      {/* Press */}
      <div style={cardStyle}>
        <div style={headStyle}>
          <span>📰 מן העיתונות</span>
          <button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '5px 12px' }} onClick={() => setNewPress({ title: '', source: '', date_str: '', url: '' })}>+ הוסף</button>
        </div>
        {newPress && (
          <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, marginBottom: 10 }}>
            <input style={inp} placeholder="כותרת" value={newPress.title} onChange={e => setNewPress(p => ({ ...p, title: e.target.value }))} />
            <input style={inp} placeholder="מקור (למשל: ynet)" value={newPress.source} onChange={e => setNewPress(p => ({ ...p, source: e.target.value }))} />
            <input style={inp} placeholder="תאריך (למשל: ינואר 2024)" value={newPress.date_str} onChange={e => setNewPress(p => ({ ...p, date_str: e.target.value }))} />
            <input style={inp} placeholder="קישור לכתבה" value={newPress.url} onChange={e => setNewPress(p => ({ ...p, url: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={() => savePress(null, newPress)}>הוסף</button>
              <button className="btn btn-secondary" onClick={() => setNewPress(null)}>ביטול</button>
            </div>
          </div>
        )}
        {press.map(item => (
          <div key={item.id}
            draggable={editingPress !== item.id}
            onDragStart={() => { dragPressRef.current = item.id; }}
            onDragOver={e => { e.preventDefault(); setDragOverPressId(item.id); }}
            onDragLeave={() => setDragOverPressId(null)}
            onDrop={() => onPressDrop(item.id)}
            style={{ borderBottom: '1px solid #f3f4f6', padding: '10px 0', cursor: editingPress === item.id ? 'default' : 'grab', boxShadow: dragOverPressId === item.id ? '0 0 0 2px #3b82f6' : 'none' }}>
            {editingPress === item.id ? (
              <PressEditForm item={item} onSave={data => savePress(item.id, data)} onCancel={() => setEditingPress(null)} inp={inp} />
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span style={{ color: '#d1d5db', fontSize: '0.9rem' }}>⠿</span>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: 2 }}>{item.title}</div>
                    <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>{item.source}{item.source && item.date_str && ' · '}{item.date_str}</div>
                    {item.url && <a href={item.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem', color: '#1a4a8a' }}>לכתבה המלאה 🌐</a>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button style={editBtn} onClick={() => setEditingPress(item.id)}>✏️</button>
                  <button style={{ ...editBtn, color: '#ef4444' }} onClick={() => deletePress(item.id)}>🗑️</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {press.length === 0 && !newPress && <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>אין כתבות עדיין</div>}
        {press.length > 1 && <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 6 }}>ניתן לגרור ⠿ כדי לשנות סדר</div>}
      </div>

      {photoRepositioning && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setPhotoRepositioning(null)} onWheel={e => e.preventDefault()}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, padding: 20, width: 260 }}>
            <DragReposition
              repositioning={photoRepositioning}
              setRepositioning={setPhotoRepositioning}
              onSave={() => {
                setForm(f => ({ ...f, photo_position: `${photoRepositioning.x}% ${photoRepositioning.y}%`, photo_zoom: photoRepositioning.zoom }));
                setPhotoRepositioning(null);
              }}
              onCancel={() => setPhotoRepositioning(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── About Tab ─────────────────────────────────────────────────────────────────

function AboutTab({ projectName }) {
  const [founder, setFounder] = useState({});
  const [projects, setProjects] = useState([]);
  const [press, setPress] = useState([]);
  const [editingFounder, setEditingFounder] = useState(false);
  const [founderForm, setFounderForm] = useState({});
  const [editingProject, setEditingProject] = useState(null);
  const [newProject, setNewProject] = useState(null);
  const [editingPress, setEditingPress] = useState(null);
  const [newPress, setNewPress] = useState(null);
  const [photoDragOver, setPhotoDragOver] = useState(false);
  const photoInputRef = React.useRef();
  const photoPosDragRef = React.useRef(null);

  const load = useCallback(async () => {
    api.get(`/portal/about/founder/${encodeURIComponent(projectName)}`).then(r => setFounder(r.data || {})).catch(() => {});
    api.get(`/portal/about/projects/${encodeURIComponent(projectName)}`).then(r => setProjects(r.data)).catch(() => {});
    api.get(`/portal/about/press/${encodeURIComponent(projectName)}`).then(r => setPress(r.data)).catch(() => {});
  }, [projectName]);

  useEffect(() => { load(); }, [load]);

  async function saveFounder() {
    await api.post(`/portal/about/founder/${encodeURIComponent(projectName)}`, founderForm);
    setEditingFounder(false); load();
  }

  async function uploadPhoto(file) {
    if (!file) return;
    const fd = new FormData(); fd.append('photo', file);
    const r = await api.post('/portal/about/upload-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setFounderForm(f => ({ ...f, photo_url: r.data.url }));
  }

  async function uploadFounderLogo(file) {
    if (!file) return;
    const fd = new FormData(); fd.append('photo', file);
    const r = await api.post('/portal/about/upload-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setFounderForm(f => ({ ...f, logo_url: r.data.url }));
  }

  async function saveProject(id, data) {
    if (id) await api.patch(`/portal/about/projects/${id}`, data);
    else await api.post(`/portal/about/projects/${encodeURIComponent(projectName)}`, data);
    setEditingProject(null); setNewProject(null); load();
  }

  async function deleteProject(id) {
    if (!confirm('למחוק פרויקט זה?')) return;
    await api.delete(`/portal/about/projects/${id}`); load();
  }

  async function savePress(id, data) {
    if (id) await api.patch(`/portal/about/press/${id}`, data);
    else await api.post(`/portal/about/press/${encodeURIComponent(projectName)}`, data);
    setEditingPress(null); setNewPress(null); load();
  }

  async function deletePress(id) {
    if (!confirm('למחוק כתבה זו?')) return;
    await api.delete(`/portal/about/press/${id}`); load();
  }

  const cardStyle = { background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb', marginBottom: 16 };
  const headStyle = { fontWeight: 700, fontSize: '1rem', color: '#1f2937', marginBottom: 14, borderBottom: '1px solid #e5e7eb', paddingBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
  const inp = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.9rem', marginBottom: 8, boxSizing: 'border-box' };
  const editBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', padding: '2px 6px', color: '#6b7280' };

  // Split description into 3 paragraphs for the 3 boxes
  const displayParas = (founder.description || '').split('\n---\n').filter(Boolean);
  const PARA_SEP = '\n---\n';
  const formParas = (founderForm.description || '').split(PARA_SEP);
  const setFormPara = (idx, val) => {
    const parts = [...formParas];
    while (parts.length <= idx) parts.push('');
    parts[idx] = val;
    setFounderForm(f => ({ ...f, description: parts.join(PARA_SEP) }));
  };
  const darkInp = { background: 'transparent', border: 'none', borderBottom: '1px dashed rgba(255,255,255,0.3)', color: 'inherit', fontFamily: 'inherit', outline: 'none', width: '100%', padding: '2px 0', marginBottom: 6, boxSizing: 'border-box' };
  const darkTA = { background: 'rgba(255,255,255,0.07)', border: '1px dashed rgba(255,255,255,0.25)', borderRadius: 8, color: '#c7c3b8', fontFamily: 'inherit', fontSize: '0.9rem', lineHeight: 1.8, outline: 'none', width: '100%', padding: '8px 10px', resize: 'vertical', marginTop: 10, boxSizing: 'border-box' };
  const lightTA = { background: 'rgba(0,0,0,0.04)', border: '1px dashed #c98a3f', borderRadius: 8, color: '#5a4c33', fontFamily: 'inherit', fontSize: '0.9rem', lineHeight: 1.8, outline: 'none', width: '100%', padding: '8px 10px', resize: 'vertical', boxSizing: 'border-box' };

  return (
    <div>
      {/* Founder / Company — 3 WYSIWYG boxes */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#6b7280', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>👑 תיאור החברה</span>
          {!editingFounder
            ? <button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '5px 14px' }} onClick={() => { setFounderForm({ ...founder }); setEditingFounder(true); }}>✏️ עריכה</button>
            : <div style={{ display: 'flex', gap: 8 }}><button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '5px 14px' }} onClick={saveFounder}>שמור</button><button className="btn btn-secondary" style={{ fontSize: '0.82rem', padding: '5px 14px' }} onClick={() => setEditingFounder(false)}>ביטול</button></div>
          }
        </div>

        {/* BOX 1 — Hero band, matching the tenant-facing desktop hero exactly */}
        <div style={{ background: '#161d2e', borderRadius: 20, overflow: 'hidden', display: 'flex', marginBottom: 12 }}>
          <div style={{ width: 180, flexShrink: 0, background: 'linear-gradient(160deg,#1e2740,#161d2e)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20, gap: 8 }}>
            {((editingFounder ? founderForm.logo_url : founder.logo_url) || editingFounder) && (
              <div style={{ width: 72, height: 72, borderRadius: 10, overflow: 'hidden', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 4 }}>
                {(editingFounder ? founderForm.logo_url : founder.logo_url)
                  ? <img src={editingFounder ? founderForm.logo_url : founder.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  : <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)' }}>לוגו</span>}
              </div>
            )}
            {editingFounder && <label style={{ cursor: 'pointer', fontSize: '0.68rem', color: '#e8c477', padding: '2px 8px', border: '1px solid rgba(232,196,119,0.4)', borderRadius: 8, marginBottom: 4 }}>
              {founderForm.logo_url ? 'החלף לוגו' : '+ לוגו'}
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if (f) uploadFounderLogo(f); }} />
            </label>}
            <div
              style={{ width: 120, height: 120, borderRadius: '50%', overflow: 'hidden', border: '3px solid rgba(232,196,119,0.4)', cursor: (editingFounder && founderForm.photo_url) ? 'grab' : 'default', userSelect: 'none' }}
              onMouseDown={editingFounder && founderForm.photo_url ? e => {
                e.preventDefault();
                const [sx, sy] = (founderForm.photo_position || '50% 50%').split(' ').map(v => parseFloat(v));
                const startMX = e.clientX, startMY = e.clientY;
                function onMove(ev) {
                  const dx = (ev.clientX - startMX) * 0.3; const dy = (ev.clientY - startMY) * 0.3;
                  setFounderForm(f => ({ ...f, photo_position: `${Math.round(Math.max(0,Math.min(100,sx-dx)))}% ${Math.round(Math.max(0,Math.min(100,sy-dy)))}%` }));
                }
                function onUp() { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); }
                window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
              } : undefined}
            >
              {(editingFounder ? founderForm.photo_url : founder.photo_url)
                ? <img src={`${editingFounder ? founderForm.photo_url : founder.photo_url}?auth=${localStorage.getItem('token') || ''}`} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: (editingFounder ? founderForm.photo_position : founder.photo_position) || '50% 50%', pointerEvents: 'none' }} />
                : <div style={{ width: '100%', height: '100%', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' }}>👤</div>
              }
            </div>
            {editingFounder && <label style={{ cursor: 'pointer', fontSize: '0.72rem', color: '#e8c477', padding: '3px 10px', border: '1px solid rgba(232,196,119,0.4)', borderRadius: 10 }}>
              {founderForm.photo_url ? 'החלף' : '+ תמונה'}
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if (f) uploadPhoto(f); }} />
            </label>}
            {editingFounder && founderForm.photo_url && <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)' }}>גרור להזזה</span>}
          </div>
          <div style={{ flex: 1, padding: '24px 26px', color: '#fff' }}>
            {editingFounder ? (
              <>
                <input style={{ ...darkInp, fontSize: '0.8rem', color: '#e8c477', letterSpacing: '0.5px' }} placeholder="תפקיד / תואר" value={founderForm.title || ''} onChange={e => setFounderForm(f => ({ ...f, title: e.target.value }))} />
                <input style={{ ...darkInp, fontWeight: 700, fontSize: '1.5rem', color: '#fff', marginBottom: 0 }} placeholder="שם מלא" value={founderForm.founder_name || ''} onChange={e => setFounderForm(f => ({ ...f, founder_name: e.target.value }))} />
                <textarea style={darkTA} rows={4} placeholder="ביוגרפיה (פסקה ראשונה)" value={formParas[0] || ''} onChange={e => setFormPara(0, e.target.value)} />
              </>
            ) : (
              <>
                {founder.title && <div style={{ fontSize: '0.8rem', color: '#e8c477', marginBottom: 6 }}>{founder.title}</div>}
                {founder.founder_name && <div style={{ fontWeight: 700, fontSize: '1.5rem' }}>{founder.founder_name}</div>}
                {displayParas[0] && <div style={{ fontSize: '0.9rem', color: '#c7c3b8', marginTop: 12, lineHeight: 1.8, whiteSpace: 'pre-line' }}>{displayParas[0]}</div>}
              </>
            )}
          </div>
        </div>

        {/* BOX 2 & 3 — side by side */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/* BOX 2 — קצת אישי */}
          <div style={{ background: '#fff', borderRadius: 16, padding: '18px 20px', borderRight: '4px solid #c98a3f' }}>
            <div style={{ fontWeight: 700, fontSize: '0.75rem', color: '#c98a3f', marginBottom: 8 }}>קצת אישי</div>
            {editingFounder
              ? <textarea style={lightTA} rows={4} placeholder="טקסט אישי (פסקה שנייה)" value={formParas[1] || ''} onChange={e => setFormPara(1, e.target.value)} />
              : <div style={{ fontSize: '0.88rem', color: '#5a4c33', lineHeight: 1.8, whiteSpace: 'pre-line' }}>{displayParas[1] || <span style={{ color: '#d1d5db' }}>ריק</span>}</div>
            }
          </div>
          {/* BOX 3 — ציטוט */}
          <div style={{ background: '#f3e3c8', borderRadius: 16, padding: '18px 20px' }}>
            <div style={{ fontWeight: 700, fontSize: '1.5rem', color: '#c98a3f', lineHeight: 0.5, marginBottom: 6 }}>״</div>
            {editingFounder
              ? <textarea style={{ ...lightTA, background: 'rgba(0,0,0,0.04)' }} rows={4} placeholder="ציטוט (פסקה שלישית)" value={formParas[2] || ''} onChange={e => setFormPara(2, e.target.value)} />
              : <div style={{ fontSize: '0.88rem', color: '#5a4c33', lineHeight: 1.8, fontStyle: 'italic', whiteSpace: 'pre-line' }}>{displayParas[2] || <span style={{ color: '#b8a07a' }}>ריק</span>}</div>
            }
          </div>
        </div>
      </div>

      {/* Projects */}
      <div style={cardStyle}>
        <div style={headStyle}>
          <span>📌 פרויקטים</span>
          <button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '5px 12px' }} onClick={() => setNewProject({ name: '', city: '', url: '' })}>+ הוסף</button>
        </div>
        {newProject && (
          <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, marginBottom: 10 }}>
            <input style={inp} placeholder="שם פרויקט" value={newProject.name} onChange={e => setNewProject(p => ({ ...p, name: e.target.value }))} />
            <input style={inp} placeholder="עיר" value={newProject.city} onChange={e => setNewProject(p => ({ ...p, city: e.target.value }))} />
            <input style={inp} placeholder="קישור לאתר" value={newProject.url} onChange={e => setNewProject(p => ({ ...p, url: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={() => saveProject(null, newProject)}>הוסף</button>
              <button className="btn btn-secondary" onClick={() => setNewProject(null)}>ביטול</button>
            </div>
          </div>
        )}
        {projects.map(p => (
          <div key={p.id} style={{ borderBottom: '1px solid #f3f4f6', padding: '10px 0' }}>
            {editingProject === p.id ? (
              <ProjectEditForm item={p} onSave={data => saveProject(p.id, data)} onCancel={() => setEditingProject(null)} inp={inp} />
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#1f2937' }}>{p.name}</div>
                  {p.city && <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>📍 {p.city}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {p.url && <a href={p.url} target="_blank" rel="noreferrer" style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: '5px 12px', fontSize: '0.82rem', color: '#1a4a8a', textDecoration: 'none' }}>קישור לאתר</a>}
                  <button style={editBtn} onClick={() => setEditingProject(p.id)}>✏️</button>
                  <button style={{ ...editBtn, color: '#ef4444' }} onClick={() => deleteProject(p.id)}>🗑️</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {projects.length === 0 && !newProject && <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>אין פרויקטים עדיין</div>}
      </div>

      {/* Press */}
      <div style={cardStyle}>
        <div style={headStyle}>
          <span>📰 מן העיתונות</span>
          <button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '5px 12px' }} onClick={() => setNewPress({ title: '', source: '', date_str: '', url: '' })}>+ הוסף</button>
        </div>
        {newPress && (
          <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, marginBottom: 10 }}>
            <input style={inp} placeholder="כותרת" value={newPress.title} onChange={e => setNewPress(p => ({ ...p, title: e.target.value }))} />
            <input style={inp} placeholder="מקור (למשל: ynet)" value={newPress.source} onChange={e => setNewPress(p => ({ ...p, source: e.target.value }))} />
            <input style={inp} placeholder="תאריך (למשל: ינואר 2024)" value={newPress.date_str} onChange={e => setNewPress(p => ({ ...p, date_str: e.target.value }))} />
            <input style={inp} placeholder="קישור לכתבה" value={newPress.url} onChange={e => setNewPress(p => ({ ...p, url: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={() => savePress(null, newPress)}>הוסף</button>
              <button className="btn btn-secondary" onClick={() => setNewPress(null)}>ביטול</button>
            </div>
          </div>
        )}
        {press.map(item => (
          <div key={item.id} style={{ borderBottom: '1px solid #f3f4f6', padding: '10px 0' }}>
            {editingPress === item.id ? (
              <PressEditForm item={item} onSave={data => savePress(item.id, data)} onCancel={() => setEditingPress(null)} inp={inp} />
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: 2 }}>{item.title}</div>
                  <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>{item.source}{item.source && item.date_str && ' · '}{item.date_str}</div>
                  {item.url && <a href={item.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem', color: '#1a4a8a' }}>לכתבה המלאה 🌐</a>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button style={editBtn} onClick={() => setEditingPress(item.id)}>✏️</button>
                  <button style={{ ...editBtn, color: '#ef4444' }} onClick={() => deletePress(item.id)}>🗑️</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {press.length === 0 && !newPress && <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>אין כתבות עדיין</div>}
      </div>
    </div>
  );
}

function ProjectEditForm({ item, onSave, onCancel, inp }) {
  const [f, setF] = useState({ name: item.name || '', city: item.city || '', url: item.url || '' });
  return (
    <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12 }}>
      <input style={inp} placeholder="שם פרויקט" value={f.name} onChange={e => setF(p => ({ ...p, name: e.target.value }))} />
      <input style={inp} placeholder="עיר" value={f.city} onChange={e => setF(p => ({ ...p, city: e.target.value }))} />
      <input style={inp} placeholder="קישור לאתר" value={f.url} onChange={e => setF(p => ({ ...p, url: e.target.value }))} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" onClick={() => onSave(f)}>שמור</button>
        <button className="btn btn-secondary" onClick={onCancel}>ביטול</button>
      </div>
    </div>
  );
}

function PressEditForm({ item, onSave, onCancel, inp }) {
  const [f, setF] = useState({ title: item.title || '', source: item.source || '', date_str: item.date_str || '', url: item.url || '' });
  return (
    <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12 }}>
      <input style={inp} placeholder="כותרת" value={f.title} onChange={e => setF(p => ({ ...p, title: e.target.value }))} />
      <input style={inp} placeholder="מקור" value={f.source} onChange={e => setF(p => ({ ...p, source: e.target.value }))} />
      <input style={inp} placeholder="תאריך" value={f.date_str} onChange={e => setF(p => ({ ...p, date_str: e.target.value }))} />
      <input style={inp} placeholder="קישור" value={f.url} onChange={e => setF(p => ({ ...p, url: e.target.value }))} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" onClick={() => onSave(f)}>שמור</button>
        <button className="btn btn-secondary" onClick={onCancel}>ביטול</button>
      </div>
    </div>
  );
}

// ── Apartment Tab (admin) ────────────────────────────────────────────────────

function compPlaceholder(key, form, comp) {
  const sizeAdd = parseFloat(comp?.size_addition) || 0;
  const floorAdd = parseFloat(comp?.new_floor) || 0;
  if (key === 'new_size') {
    const cur = parseFloat(form?.current_size) || 0;
    if (cur && sizeAdd) return `${(cur + sizeAdd).toFixed(1).replace(/\.0$/, '')} מ"ר`;
    return 'מ"ר';
  }
  if (key === 'new_floor') {
    const ft = comp?.floor_type;
    if (ft === 'middle_third') return 'שליש אמצעי של הבניין';
    if (ft === 'pending') return 'טרם נקבע';
    if (ft === 'addition') {
      const cur = parseFloat(form?.current_floor);
      return !isNaN(cur) && floorAdd ? `${cur + floorAdd}` : `קומה נוכחית + ${floorAdd}`;
    }
    return 'בהתאם לתוכנית שתאושר';
  }
  if (key === 'balcony_size') return comp?.balcony_addition ? `${comp.balcony_addition} מ"ר` : 'מ"ר';
  if (key === 'parking_tabu') return comp?.parking_tabu ? `${comp.parking_tabu}` : 'פרטי חנייה';
  if (key === 'storage_tabu') return comp?.storage_addition ? `${comp.storage_addition}` : 'שטח / מספר';
  const f = APT_FIELDS.find(x => x.key === key);
  return f?.placeholder || '';
}

const APT_FIELDS = [
  { key: 'current_size',   label: 'שטח דירה נוכחית', placeholder: 'מ"ר', col: 'current' },
  { key: 'new_size',       label: 'שטח דירה חדשה',   placeholder: 'מ"ר', col: 'new' },
  { key: 'current_floor',  label: 'קומה נוכחית',      placeholder: 'מספר קומה', col: 'current' },
  { key: 'new_floor',      label: 'קומה חדשה',        placeholder: 'בהתאם לתוכנית שתאושר', col: 'new' },
  { key: 'current_balcony', label: 'שטח מרפסת נוכחית', placeholder: 'מ"ר (0 אם אין)', col: 'current' },
  { key: 'balcony_size',   label: 'שטח מרפסת שמש חדשה', placeholder: 'מ"ר', col: 'new' },
  { key: 'current_parking', label: 'חנייה נוכחית',     placeholder: '0 אם אין', col: 'current' },
  { key: 'parking_tabu',   label: 'חנייה בטאבו (חדשה)', placeholder: 'פרטי חנייה', col: 'new' },
  { key: 'current_storage', label: 'מחסן נוכחי',       placeholder: '0 אם אין', col: 'current' },
  { key: 'storage_tabu',   label: 'שטח מחסן בטאבו (חדש)', placeholder: 'שטח / מספר', col: 'new' },
];

const SPEC_FIELDS = [
  { key: 'flooring',          label: 'ריצוף' },
  { key: 'bathroom_flooring', label: 'ריצוף אמבטיה' },
  { key: 'interior_doors',    label: 'דלתות פנים' },
  { key: 'ac',                label: 'מיזוג' },
  { key: 'sanitary',          label: 'סניטריה' },
  { key: 'ceiling_height',    label: 'גובה תקרה' },
  { key: 'elevator',          label: 'מעלית' },
  { key: 'entrance_door',     label: 'דלת כניסה' },
  { key: 'windows_shutters',  label: 'חלונות ותריסים' },
  { key: 'kitchen',           label: 'מטבח' },
];

// TAMA buildings (11,20,22,24) reuse the same DB columns as the general spec,
// just with different real-world meanings — no schema change needed.
const SPEC_FIELDS_TAMA = [
  { key: 'flooring',          label: 'ריצוף מרפסת' },
  { key: 'bathroom_flooring', label: 'לובי כניסה ראשי' },
  { key: 'interior_doors',    label: 'ריצוף מבואות קומתיות' },
  { key: 'ac',                label: 'מעטפת וחיפוי הבניין' },
  { key: 'sanitary',          label: 'חדר ממ"ד' },
  { key: 'ceiling_height',    label: 'עבודות פיתוח' },
  { key: 'elevator',          label: 'מעלית' },
  { key: 'entrance_door',     label: 'דלת כניסה' },
  { key: 'windows_shutters',  label: 'חלונות ותריסים' },
  { key: 'kitchen',           label: 'מסתור כביסה' },
];

function ApartmentTab({ projectName, onGoToFiles }) {
  const [specForm, setSpecForm] = useState({});
  const [specTarget, setSpecTarget] = useState('_project_'); // '_project_' | '_tama_'
  const [specSaved, setSpecSaved] = useState(false);
  const [specSaving, setSpecSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractMsg, setExtractMsg] = useState('');
  const [agreementPdfUrl, setAgreementPdfUrl] = useState('');
  const [uploadingAgreement, setUploadingAgreement] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [buildingsStructured, setBuildingsStructured] = useState([]);
  const [bulkAptOptions, setBulkAptOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [aptForm, setAptForm] = useState({});
  const [aptSaved, setAptSaved] = useState(false);
  const [aptSaving, setAptSaving] = useState(false);
  const [aptPdfs, setAptPdfs] = useState([]);
  const [bulkPdfs, setBulkPdfs] = useState([]);
  const [pdfDragging, setPdfDragging] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  // bulk
  const [bulkBuilding, setBulkBuilding] = useState('');
  const [bulkBuildingNum, setBulkBuildingNum] = useState('');
  const [bulkApts, setBulkApts] = useState([]); // selected apartment numbers
  const [bulkForm, setBulkForm] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkReload, setBulkReload] = useState(0);
  const [bulkResult, setBulkResult] = useState('');
  const [projectComp, setProjectComp] = useState({});

  useEffect(() => {
    api.get(`/portal/buildings-structured/${encodeURIComponent(projectName)}`).then(r => {
      const bStructured = r.data || [];
      setBuildingsStructured(bStructured);
      const blds = [...new Set(bStructured.map(b => b.buildingAddress).filter(Boolean))];
      setBuildings(blds);
    }).catch(() => {});
    api.get(`/portal/tenants-list/${encodeURIComponent(projectName)}`).then(r => {
      setTenants(r.data || []);
    }).catch(() => {});
    api.get(`/portal/project-comp/${encodeURIComponent(projectName)}`).then(r => setProjectComp(r.data || {})).catch(() => {});
  }, [projectName]);

  useEffect(() => {
    if (!projectName) return;
    api.get(`/portal/building-specs/${encodeURIComponent(projectName)}/${specTarget}`).then(r => {
      const data = r.data || {};
      setSpecForm(data);
      if (specTarget === '_project_') setAgreementPdfUrl(data.agreement_pdf_url || '');
    }).catch(() => {});
  }, [projectName, specTarget]);

  async function extractSpecsFromPdf(file) {
    if (!file) return;
    setExtracting(true); setExtractMsg('מנתח את ההסכם...');
    try {
      const fd1 = new FormData(); fd1.append('pdf', file);
      const fd2 = new FormData(); fd2.append('pdf', file);
      // שמור PDF תמיד (לא תלוי בחילוץ)
      api.post(`/portal/agreement-pdf/${encodeURIComponent(projectName)}`, fd2)
        .then(r => setAgreementPdfUrl(r.data.url)).catch(() => {});
      // חלץ מפרט
      const r = await api.post('/portal/extract-specs', fd1);
      setSpecForm(prev => {
        const merged = { ...prev };
        Object.entries(r.data).forEach(([k, v]) => { if (v && v !== 'null') merged[k] = v; });
        return merged;
      });
      setExtractMsg('✓ המפרט חולץ בהצלחה — בדוק ושמור');
      setTimeout(() => setExtractMsg(''), 6000);
    } catch (e) {
      const msg = e?.response?.data?.error || 'שגיאה בניתוח הקובץ';
      setExtractMsg(msg); setTimeout(() => setExtractMsg(''), 5000);
    }
    setExtracting(false);
  }

  async function uploadAgreementPdf(file) {
    if (!file) return;
    setUploadingAgreement(true);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      const r = await api.post(`/portal/agreement-pdf/${encodeURIComponent(projectName)}`, fd);
      setAgreementPdfUrl(r.data.url);
    } catch { alert('שגיאה בהעלאת הקובץ'); }
    setUploadingAgreement(false);
  }

  async function deleteAgreementPdf() {
    if (!window.confirm('למחוק את קובץ ההסכם?')) return;
    await api.delete(`/portal/agreement-pdf/${encodeURIComponent(projectName)}`).catch(() => {});
    setAgreementPdfUrl('');
  }

  const [importSizing, setImportSizing] = useState(false);
  const [excelDragging, setExcelDragging] = useState(false);
  const [storedExcel, setStoredExcel] = useState(null);

  useEffect(() => {
    if (!projectName) return;
    api.get(`/portal/excel-file/${encodeURIComponent(projectName)}`).then(r => setStoredExcel(r.data)).catch(() => {});
  }, [projectName]);

  async function processExcelFile(file) {
    if (!file) return;
    setImportSizing(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post(`/portal/import-sizes/${encodeURIComponent(projectName)}`, fd);
      setStoredExcel({ url: r.data.url, original_name: r.data.original_name });
      alert(`✓ עודכנו ${r.data.updated} דירות בהצלחה`);
      setBulkReload(c => c + 1);
    } catch (err) { alert('שגיאה: ' + (err?.response?.data?.error || err.message)); }
    setImportSizing(false);
  }

  function handleImportSizes(e) { processExcelFile(e.target.files[0]); e.target.value = ''; }

  const [importFloorsBusy, setImportFloorsBusy] = useState(false);
  const [floorsDragging, setFloorsDragging] = useState(false);
  const [floorsResult, setFloorsResult] = useState('');

  async function processFloorsFile(file) {
    if (!file) return;
    setImportFloorsBusy(true); setFloorsResult('');
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post(`/portal/import-floors/${encodeURIComponent(projectName)}`, fd);
      setFloorsResult(`✓ עודכנו ${r.data.updated} דירות (${r.data.skipped} דולגו)`);
      setBulkReload(c => c + 1);
    } catch (err) { setFloorsResult('שגיאה: ' + (err?.response?.data?.error || err.message)); }
    setImportFloorsBusy(false);
  }
  function handleImportFloors(e) { processFloorsFile(e.target.files[0]); e.target.value = ''; }

  const [importNewBldgBusy, setImportNewBldgBusy] = useState(false);
  const [newBldgResult, setNewBldgResult] = useState('');

  async function processNewBuildingFile(file) {
    if (!file) return;
    setImportNewBldgBusy(true); setNewBldgResult('');
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post(`/portal/import-new-building/${encodeURIComponent(projectName)}`, fd);
      setNewBldgResult(`✓ עודכנו ${r.data.updated} דירות (${r.data.skipped} דולגו)`);
      setBulkReload(c => c + 1);
    } catch (err) { setNewBldgResult('שגיאה: ' + (err?.response?.data?.error || err.message)); }
    setImportNewBldgBusy(false);
  }
  function handleImportNewBuilding(e) { processNewBuildingFile(e.target.files[0]); e.target.value = ''; }

  const [showSpec, setShowSpec] = useState(true);
  const [showBulk, setShowBulk] = useState(true);
  const [showPdfs, setShowPdfs] = useState(true);
  const [showFloorplans, setShowFloorplans] = useState(false);
  const [floorplans, setFloorplans] = useState([]);
  const [fpBuildingNumbers, setFpBuildingNumbers] = useState('');
  const [fpUploading, setFpUploading] = useState(false);
  const [editingFloorplan, setEditingFloorplan] = useState(null);

  const loadFloorplans = useCallback(async () => {
    if (!projectName) return;
    const r = await api.get(`/portal/building-floorplans/${encodeURIComponent(projectName)}`).catch(() => ({ data: [] }));
    setFloorplans(r.data || []);
  }, [projectName]);
  useEffect(() => { loadFloorplans(); }, [loadFloorplans]);

  async function uploadFloorplan(file) {
    if (!file) return;
    if (!fpBuildingNumbers.trim()) { alert('הזן מספרי בניינים (מופרדים בפסיק) לפני העלאה'); return; }
    setFpUploading(true);
    const fd = new FormData();
    fd.append('image', file);
    fd.append('building_numbers', fpBuildingNumbers.trim());
    try {
      await api.post(`/portal/building-floorplan/${encodeURIComponent(projectName)}`, fd);
      setFpBuildingNumbers('');
      loadFloorplans();
    } catch (err) { alert('שגיאה: ' + (err?.response?.data?.error || err.message)); }
    setFpUploading(false);
  }

  async function deleteFloorplan(id) {
    if (!window.confirm('למחוק תוכנית זו?')) return;
    await api.delete(`/portal/building-floorplan/${id}`);
    loadFloorplans();
  }

  async function saveFloorplanMarkers(markers) {
    await api.post(`/portal/building-floorplan-markers/${editingFloorplan.id}`, { markers });
    setEditingFloorplan(null);
    loadFloorplans();
  }

  async function deleteStoredExcel() {
    if (!window.confirm('למחוק את קובץ האקסל?')) return;
    await api.delete(`/portal/excel-file/${encodeURIComponent(projectName)}`).catch(() => {});
    setStoredExcel(null);
  }

  async function saveBulk() {
    setBulkSaving(true); setBulkResult('');
    const calced = calcNewValues(bulkForm, projectComp);
    try {
      const r = await api.post(`/portal/apartment-details-bulk/${encodeURIComponent(projectName)}`, {
        current_size: bulkForm.current_size || '',
        current_floor: bulkForm.current_floor || '',
        current_balcony: bulkForm.current_balcony ?? '0',
        current_parking: bulkForm.current_parking ?? '0',
        current_storage: bulkForm.current_storage ?? '0',
        new_size: calced.new_size,
        new_floor: calced.new_floor,
        balcony_size: calced.balcony_size,
        parking_tabu: calced.parking_tabu,
        storage_tabu: calced.storage_tabu,
        building: bulkBuilding, buildingNumber: bulkBuildingNum, apartments: bulkApts
      });
      setBulkResult(r.data.updated > 0 ? `✓ עודכנו ${r.data.updated} דירות` : '⚠️ לא נמצאו דירות מתאימות — בדוק שבחרת בניין ודירות');
      setTimeout(() => setBulkResult(''), 6000);
      setBulkReload(c => c + 1);
    } catch { setBulkResult('שגיאה בשמירה'); }
    setBulkSaving(false);
  }

  async function saveSpec() {
    setSpecSaving(true);
    await api.post(`/portal/building-specs/${encodeURIComponent(projectName)}/${specTarget}`, specForm).catch(() => {});
    setSpecSaving(false); setSpecSaved(true); setTimeout(() => setSpecSaved(false), 2500);
  }

  // Load tenant data when exactly 1 apartment is selected
  useEffect(() => {
    if (bulkApts.length === 0) { setBulkForm({}); return; }
    if (bulkApts.length !== 1 || !bulkBuilding || !bulkBuildingNum) return;
    api.get(`/portal/apartment-details-by-apt/${encodeURIComponent(projectName)}`, {
      params: { building: bulkBuilding, buildingNumber: bulkBuildingNum, apartment: bulkApts[0] }
    }).then(r => {
      const d = r.data || {};
      setBulkForm({
        current_size: d.current_size ?? '',
        current_floor: d.current_floor ?? '',
        current_balcony: d.current_balcony ?? '0',
        current_parking: d.current_parking ?? '0',
        current_storage: d.current_storage ?? '0',
        new_floor: d.new_floor ?? '',
        new_size: d.new_size ?? '',
        balcony_size: d.balcony_size ?? '',
        parking_tabu: d.parking_tabu ?? '',
        storage_tabu: d.storage_tabu ?? '',
      });
    }).catch(() => {});
  }, [bulkApts, bulkBuilding, bulkBuildingNum, bulkReload]);

  // Calculated new values from projectComp + current form values
  function calcNewValues(form, comp) {
    const curSize = parseFloat(form.current_size) || 0;
    const addSize = parseFloat(comp.size_addition) || 0;
    const newSize = curSize + addSize;
    let newFloor = '—';
    if (form.new_floor) {
      const cleaned = String(form.new_floor).replace(/^(קומה\s*)+/g, '').trim();
      // A range like "8-13" means the exact floor isn't decided yet — show the
      // same tenant-facing wording here too, so admin preview matches what the
      // tenant actually sees instead of a raw range they'd have to interpret.
      newFloor = cleaned.includes('-') ? 'בשליש האמצעי של הבניין החדש ובקומה גבוהה יותר' : cleaned;
    }
    else if (comp.floor_type === 'middle_third') newFloor = 'שליש אמצעי של הבניין';
    else if (comp.floor_type === 'pending') newFloor = 'טרם נקבע';
    else if (comp.floor_type === 'addition') {
      const cur = parseFloat(form.current_floor);
      const add = parseFloat(comp.new_floor) || 0;
      newFloor = !isNaN(cur) && add ? String(cur + add) : add ? `+ ${add} קומות` : '—';
    }
    else if (comp.floor_type === 'floor_other') newFloor = comp.new_floor || '—';
    return {
      new_size: addSize || curSize ? String(newSize % 1 === 0 ? newSize : newSize.toFixed(1)) : '0',
      new_floor: newFloor,
      balcony_size: (() => { const n = parseFloat(comp.balcony_addition); return !comp.balcony_addition ? '0' : isNaN(n) ? comp.balcony_addition : String(n % 1 === 0 ? n : n.toFixed(2).replace(/\.?0+$/,'')); })(),
      parking_tabu: (() => { const n = parseFloat(comp.parking_tabu);     const cur = parseFloat(form.current_parking)||0; return !comp.parking_tabu ? '0' : isNaN(n) ? comp.parking_tabu : String((cur+n) % 1 === 0 ? (cur+n) : (cur+n).toFixed(2).replace(/\.?0+$/,'')); })(),
      storage_tabu: (() => { const n = parseFloat(comp.storage_addition);  return !comp.storage_addition ? '0' : isNaN(n) ? comp.storage_addition : String(n % 1 === 0 ? n : n.toFixed(2).replace(/\.?0+$/,'')); })(),
    };
  }

  // bulk helpers
  const bulkBuildingNums = bulkBuilding
    ? buildingsStructured.filter(b => b.buildingAddress === bulkBuilding).map(b => b.buildingNumber).filter(Boolean)
    : [];
  useEffect(() => {
    if (!bulkBuilding || !bulkBuildingNum) { setBulkAptOptions([]); return; }
    api.get(`/portal/apartments-by-building/${encodeURIComponent(bulkBuilding)}/${encodeURIComponent(bulkBuildingNum)}`)
      .then(r => setBulkAptOptions(r.data || [])).catch(() => setBulkAptOptions([]));
  }, [bulkBuilding, bulkBuildingNum]);

  const norm = s => (s || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const q = norm(search);
  const filtered = q.length < 1 ? tenants : tenants.filter(t => {
    const full = norm(`${t.firstName} ${t.lastName}`);
    const rev = norm(`${t.lastName} ${t.firstName}`);
    return full.includes(q) || rev.includes(q) || (t.cellularPhone || '').includes(q);
  });

  async function selectTenant(t) {
    setSelectedTenant(t);
    const phone = (t.cellularPhone || '').replace(/[\s\-]/g, '');
    let r = { data: {} };
    if (t.buildingAddress && t.buildingNumber && t.apartmentNumber) {
      r = await api.get(`/portal/apartment-details-by-apt/${encodeURIComponent(projectName)}`, {
        params: { building: t.buildingAddress, buildingNumber: t.buildingNumber, apartment: t.apartmentNumber }
      }).catch(() => ({ data: {} }));
    }
    if (!r.data || !Object.keys(r.data).length) {
      r = await api.get(`/portal/apartment-details/${encodeURIComponent(phone)}`).catch(() => ({ data: {} }));
    }
    const d = r.data || {};
    const { new_size, ...rest } = d;
    setAptForm(rest); setAptSaved(false);
    api.get(`/portal/apt-pdfs?phone=${encodeURIComponent(phone)}`).then(res => setAptPdfs(res.data || [])).catch(() => {});
  }

  async function uploadAptPdfs(files) {
    if (!selectedTenant || !files.length) return;
    setPdfUploading(true);
    const phone = (selectedTenant.cellularPhone || '').replace(/[\s\-]/g, '');
    const fd = new FormData();
    fd.append('phone', phone);
    fd.append('project_name', projectName);
    Array.from(files).forEach(f => fd.append('pdfs', f));
    const res = await api.post('/portal/apt-pdfs', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).catch(() => ({ data: [] }));
    setAptPdfs(prev => [...(res.data || []), ...prev]);
    setPdfUploading(false);
  }

  async function deleteAptPdf(id) {
    await api.delete(`/portal/apt-pdfs/${id}`).catch(() => {});
    setAptPdfs(prev => prev.filter(p => p.id !== id));
  }

  const adminPdfKey = `admin:${projectName}`;

  useEffect(() => {
    api.get(`/portal/apt-pdfs?phone=${encodeURIComponent(adminPdfKey)}`).then(res => setBulkPdfs(res.data || [])).catch(() => {});
  }, [projectName]);

  async function uploadBulkPdfs(files) {
    if (!files.length) return;
    setPdfUploading(true);
    const fd = new FormData();
    fd.append('phone', adminPdfKey);
    fd.append('project_name', projectName);
    Array.from(files).forEach(f => fd.append('pdfs', f));
    const res = await api.post('/portal/apt-pdfs', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).catch(() => ({ data: [] }));
    setBulkPdfs(prev => [...(res.data || []), ...prev]);
    setPdfUploading(false);
  }

  async function deleteBulkPdf(id) {
    await api.delete(`/portal/apt-pdfs/${id}`).catch(() => {});
    setBulkPdfs(prev => prev.filter(p => p.id !== id));
  }

  async function saveApt() {
    if (!selectedTenant) return;
    setAptSaving(true);
    const phone = (selectedTenant.cellularPhone || '').replace(/[\s\-]/g, '');
    await api.post(`/portal/apartment-details/${encodeURIComponent(phone)}`, aptForm).catch(() => {});
    setAptSaving(false); setAptSaved(true); setTimeout(() => setAptSaved(false), 2500);
  }

  const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: '0.9rem', boxSizing: 'border-box' };
  const labelStyle = { fontSize: '0.82rem', color: '#6b7280', marginBottom: 4, display: 'block', fontWeight: 600 };
  const cardStyle = { background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb', marginBottom: 20 };

  return (
    <div>
      {/* Spec section — per project */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showSpec ? 16 : 0, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937' }}>🔧 עיקרי המפרט הטכני</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {showSpec && projectName === 'ג החדשה' && (
              <div style={{ display: 'flex', border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }}>
                <button onClick={() => setSpecTarget('_project_')} style={{ border: 'none', padding: '6px 14px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', background: specTarget === '_project_' ? '#1B2A4A' : 'white', color: specTarget === '_project_' ? 'white' : '#374151' }}>מפרט כללי</button>
                <button onClick={() => setSpecTarget('_tama_')} style={{ border: 'none', padding: '6px 14px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', background: specTarget === '_tama_' ? '#1B2A4A' : 'white', color: specTarget === '_tama_' ? 'white' : '#374151' }}>מפרט תמ"א (11,20,22,24)</button>
              </div>
            )}
            {extractMsg && <span style={{ fontSize: '0.82rem', color: extractMsg.startsWith('✓') ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{extractMsg}</span>}
            {showSpec && (
              <React.Fragment>
                {/* Navigate to files filtered by הסכם פינוי בינוי */}
                <button
                  onClick={() => onGoToFiles && onGoToFiles('הסכם פינוי בינוי')}
                  style={{ background: '#1d4ed8', color: 'white', border: 'none', padding: '7px 16px', borderRadius: 8, fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  📋 הצגת ההסכם + המפרט הטכני
                </button>
                {/* Extract specs button */}
                <label style={{ cursor: extracting ? 'wait' : 'pointer', background: extracting ? '#9ca3af' : '#7c3aed', color: 'white', padding: '7px 16px', borderRadius: 8, fontSize: '0.83rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {extracting ? '⏳ מנתח...' : '📄 הטמע מפרט מהסכם PDF'}
                  <input type="file" accept=".pdf" style={{ display: 'none' }} disabled={extracting} onChange={e => { if (e.target.files[0]) extractSpecsFromPdf(e.target.files[0]); e.target.value = ''; }} />
                </label>
                {/* The actual agreement PDF the tenant-facing "צפה בהסכם המלא"
                    button opens — without this uploaded, that button never
                    appears at all for the project (this had no UI control
                    until now, only worked for projects seeded via a one-off
                    script). Only applies at the project level, not the TAMA spec. */}
                {specTarget === '_project_' && (
                  agreementPdfUrl ? (
                    <>
                      <a href={agreementPdfUrl} target="_blank" rel="noreferrer" style={{ background: '#16a34a', color: 'white', textDecoration: 'none', padding: '7px 16px', borderRadius: 8, fontSize: '0.83rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                        📄 קובץ ההסכם הועלה — צפה
                      </a>
                      <button onClick={deleteAgreementPdf} style={{ background: 'none', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 8, padding: '7px 12px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                        מחק
                      </button>
                    </>
                  ) : (
                    <label style={{ cursor: uploadingAgreement ? 'wait' : 'pointer', background: uploadingAgreement ? '#9ca3af' : '#b45309', color: 'white', padding: '7px 16px', borderRadius: 8, fontSize: '0.83rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {uploadingAgreement ? '⏳ מעלה...' : '📤 העלה קובץ הסכם (PDF) — יופיע לדייר ב"קבצים"'}
                      <input type="file" accept=".pdf" style={{ display: 'none' }} disabled={uploadingAgreement} onChange={e => { if (e.target.files[0]) uploadAgreementPdf(e.target.files[0]); e.target.value = ''; }} />
                    </label>
                  )
                )}
              </React.Fragment>
            )}
            <button onClick={() => setShowSpec(v => !v)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', color: '#6b7280', cursor: 'pointer' }}>
              {showSpec ? 'הסתר' : 'הצג'}
            </button>
          </div>
        </div>
        {showSpec && (
          <React.Fragment>
            <div className="spec-fields-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              {(specTarget === '_tama_' && projectName === 'ג החדשה' ? SPEC_FIELDS_TAMA : SPEC_FIELDS).map(f => {
                const taId = `spec-ta-${f.key}`;
                function insertBold() {
                  const ta = document.getElementById(taId);
                  if (!ta) return;
                  const s = ta.selectionStart, e = ta.selectionEnd;
                  const val = specForm[f.key] || '';
                  const selected = val.slice(s, e);
                  const newVal = val.slice(0, s) + `**${selected}**` + val.slice(e);
                  setSpecForm(p => ({ ...p, [f.key]: newVal }));
                  setTimeout(() => { ta.focus(); ta.setSelectionRange(s + 2, e + 2); }, 0);
                }
                return (
                  <div key={f.key}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <label style={{ ...labelStyle, marginBottom: 0 }}>{f.label}</label>
                      <button type="button" onClick={insertBold} title="בולד (סמן טקסט ולחץ)" style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4, border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer' }}>B</button>
                    </div>
                    <textarea id={taId} value={specForm[f.key] || ''} onChange={e => setSpecForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={`פרטי ${f.label}`} rows={3} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />

                  </div>
                );
              })}
            </div>
            <button onClick={saveSpec} disabled={specSaving} style={{ background: '#1B2A4A', color: 'white', border: 'none', padding: '9px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
              {specSaved ? '✓ נשמר' : specSaving ? 'שומר...' : 'שמור מפרט'}
            </button>
          </React.Fragment>
        )}
      </div>

      {/* Bulk update */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showBulk ? 14 : 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937' }}>🏗️ עדכון נתוני דירה — לבניין או לכל הפרויקט</div>
          <button onClick={() => setShowBulk(v => !v)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', color: '#6b7280', cursor: 'pointer' }}>
            {showBulk ? 'הסתר' : 'הצג'}
          </button>
        </div>
        {showBulk && (
        <React.Fragment>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12, alignItems: 'flex-end' }}>
          <div>
            <label style={labelStyle}>כתובת בניין</label>
            <select value={bulkBuilding} onChange={e => { setBulkBuilding(e.target.value); setBulkBuildingNum(''); setBulkApts([]); }} style={{ ...inputStyle, minWidth: 180 }}>
              <option value="">בחר כתובת...</option>
              {buildings.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          {bulkBuildingNums.length > 0 && (
            <div>
              <label style={labelStyle}>מספר בניין</label>
              <select value={bulkBuildingNum} onChange={e => { setBulkBuildingNum(e.target.value); setBulkApts([]); }} style={{ ...inputStyle, minWidth: 100 }}>
                <option value="">בחר...</option>
                {bulkBuildingNums.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          )}
        </div>
        {bulkAptOptions.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>דירות — בחר אחת או יותר</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <button type="button" onClick={() => setBulkApts(bulkApts.length === bulkAptOptions.length ? [] : [...bulkAptOptions])}
                style={{ fontSize: '0.78rem', padding: '3px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: bulkApts.length === bulkAptOptions.length ? '#7c3aed' : '#f3f4f6', color: bulkApts.length === bulkAptOptions.length ? 'white' : '#374151', cursor: 'pointer', fontWeight: 600 }}>
                {bulkApts.length === bulkAptOptions.length ? '✓ בטל הכל' : 'בחר הכל'}
              </button>
              {bulkAptOptions.map(apt => (
                <button key={apt} type="button"
                  onClick={() => setBulkApts(p => p.includes(apt) ? p.filter(a => a !== apt) : [...p, apt])}
                  style={{ fontSize: '0.78rem', padding: '3px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: bulkApts.includes(apt) ? '#7c3aed' : '#f3f4f6', color: bulkApts.includes(apt) ? 'white' : '#374151', cursor: 'pointer', fontWeight: 600 }}>
                  דירה {apt}
                </button>
              ))}
            </div>
          </div>
        )}
        {(() => {
          const calced = calcNewValues(bulkForm, projectComp);
          // TAMA buildings at ברוך קרוא 11/20/22/24 don't include parking — hide that row for them.
          const isTamaNoParking = (bulkBuilding || '').replace(/\s+/g, '') === 'ברוךקרוא'
            && ['11', '20', '22', '24'].includes(String(bulkBuildingNum || '').trim());
          const rows = [
            { label: 'שטח (מ"ר)', curKey: 'current_size', newVal: calced.new_size },
            { label: 'קומה', curKey: 'current_floor', newVal: calced.new_floor },
            { label: 'מרפסת שמש (מ"ר)', curKey: 'current_balcony', newVal: calced.balcony_size },
            { label: 'מחסן (מ"ר)', curKey: 'current_storage', newVal: calced.storage_tabu },
            ...(isTamaNoParking ? [] : [{ label: 'חנייה בטאבו', curKey: 'current_parking', newVal: calced.parking_tabu }]),
          ];
          return (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 40px 1fr', gap: 0, marginBottom: 0, borderRadius: 14, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                {/* כותרות */}
                <div style={{ textAlign: 'center', fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '10px 8px', fontSize: '0.9rem', borderBottom: '1px solid #e5e7eb' }}>🏠 הדירה הישנה</div>
                <div style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb' }} />
                <div style={{ textAlign: 'center', fontWeight: 700, color: '#15803d', background: '#f0fdf4', padding: '10px 8px', fontSize: '0.9rem', borderBottom: '1px solid #e5e7eb' }}>✨ הדירה החדשה</div>
                {rows.map((row, i) => (
                  <React.Fragment key={row.curKey}>
                    <div style={{ background: i % 2 === 0 ? '#fffbeb' : '#fefce8', padding: '10px 14px', borderBottom: i < rows.length - 1 ? '1px solid #fde68a' : 'none' }}>
                      <div style={{ fontSize: '0.73rem', color: '#92400e', marginBottom: 4, fontWeight: 600 }}>{row.label}</div>
                      <input value={bulkForm[row.curKey] ?? ''} onChange={e => setBulkForm(p => ({ ...p, [row.curKey]: e.target.value }))} placeholder="0" style={{ ...inputStyle, marginBottom: 0, background: 'white' }} />
                    </div>
                    <div style={{ background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d1d5db', fontSize: '1rem', borderRight: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderBottom: i < rows.length - 1 ? '1px solid #e5e7eb' : 'none' }}>←</div>
                    <div style={{ background: i % 2 === 0 ? '#f0fdf4' : '#ecfdf5', padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center', borderBottom: i < rows.length - 1 ? '1px solid #bbf7d0' : 'none' }}>
                      <div style={{ fontSize: '0.73rem', color: '#6b7280', marginBottom: 4, fontWeight: 600 }}>{row.label}</div>
                      <div style={{ fontWeight: 700, color: '#15803d', fontSize: '1rem' }}>{row.newVal}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })()}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={saveBulk} disabled={bulkSaving} style={{ background: '#7c3aed', color: 'white', border: 'none', padding: '9px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
            {bulkSaving ? 'מעדכן...' : '💾 החל על הנבחרים'}
          </button>
        </div>

        {/* Excel import section */}
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#374151', marginBottom: 8 }}>📊 קובץ שטחים מאקסל</div>

          {/* Stored excel display */}
          {storedExcel && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
              <span style={{ fontSize: '1rem' }}>📊</span>
              <span style={{ flex: 1, fontSize: '0.82rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{storedExcel.original_name}</span>
              <a href={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(storedExcel.url)}`} target="_blank" rel="noopener noreferrer" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, padding: '3px 10px', fontSize: '0.78rem', textDecoration: 'none', whiteSpace: 'nowrap', fontWeight: 600 }}>צפה</a>
              <button onClick={() => downloadCrossOriginFile(storedExcel.url, storedExcel.original_name)} style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, padding: '3px 10px', fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 }}>הורד</button>
              <button onClick={deleteStoredExcel} title="מחק" style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1rem', padding: '2px 4px', lineHeight: 1 }}>🗑</button>
            </div>
          )}

          {/* Drag-drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setExcelDragging(true); }}
            onDragLeave={() => setExcelDragging(false)}
            onDrop={e => { e.preventDefault(); setExcelDragging(false); const f = e.dataTransfer.files[0]; if (f) processExcelFile(f); }}
            style={{ border: `2px dashed ${excelDragging ? '#15803d' : '#d1fae5'}`, borderRadius: 10, padding: '14px 12px', textAlign: 'center', background: excelDragging ? '#f0fdf4' : '#fafff9', cursor: 'pointer', transition: 'all 0.2s' }}
          >
            <label style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: '1.4rem' }}>📊</span>
              <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 600 }}>
                {importSizing ? '⏳ מייבא ושומר...' : 'גרור קובץ אקסל לכאן או לחץ לבחירה'}
              </span>
              <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>xlsx — שיבוצי דירות (כתובת, דירה, שטח דירה קיים, שטח דירה חדשה)</span>
              <input type="file" accept=".xlsx,.xls" hidden onChange={handleImportSizes} disabled={importSizing} />
            </label>
          </div>
          {bulkResult && <span style={{ color: bulkResult.startsWith('✓') ? '#15803d' : '#dc2626', fontWeight: 600, fontSize: '0.9rem' }}>{bulkResult}</span>}
        </div>

        {/* Floors import from lottery sheet */}
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#374151', marginBottom: 8 }}>🎲 קובץ קומות מהגרלה</div>
          <div
            onDragOver={e => { e.preventDefault(); setFloorsDragging(true); }}
            onDragLeave={() => setFloorsDragging(false)}
            onDrop={e => { e.preventDefault(); setFloorsDragging(false); const f = e.dataTransfer.files[0]; if (f) processFloorsFile(f); }}
            style={{ border: `2px dashed ${floorsDragging ? '#7c3aed' : '#e9d5ff'}`, borderRadius: 10, padding: '14px 12px', textAlign: 'center', background: floorsDragging ? '#f5f3ff' : '#fdfaff', cursor: 'pointer', transition: 'all 0.2s' }}
          >
            <label style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: '1.4rem' }}>🎲</span>
              <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 600 }}>
                {importFloorsBusy ? '⏳ מייבא ושומר...' : 'גרור קובץ אקסל לכאן או לחץ לבחירה'}
              </span>
              <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>xlsx — טבלת הגרלות (כתובת, מס' דירה, קומה נבחרת)</span>
              <input type="file" accept=".xlsx,.xls" hidden onChange={handleImportFloors} disabled={importFloorsBusy} />
            </label>
          </div>
          {floorsResult && <span style={{ color: floorsResult.startsWith('✓') ? '#15803d' : '#dc2626', fontWeight: 600, fontSize: '0.9rem' }}>{floorsResult}</span>}
        </div>

        {/* New-building assignment import — final apartment-to-new-building mapping */}
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#374151', marginBottom: 8 }}>📍 שיבוץ בניין ואות דירה חדשים (להדמיה)</div>
          <div
            style={{ border: '2px dashed #fde68a', borderRadius: 10, padding: '14px 12px', textAlign: 'center', background: '#fffbeb', cursor: 'pointer', transition: 'all 0.2s' }}
          >
            <label style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: '1.4rem' }}>📍</span>
              <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 600 }}>
                {importNewBldgBusy ? '⏳ מייבא ושומר...' : 'לחץ לבחירת קובץ אקסל'}
              </span>
              <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>xlsx — גיליון "שיבוץ דירות סופי" (כתובת, דירה, מספר בניין חדש, אות דירה — אופציונלי)</span>
              <input type="file" accept=".xlsx,.xls" hidden onChange={handleImportNewBuilding} disabled={importNewBldgBusy} />
            </label>
          </div>
          {newBldgResult && <span style={{ color: newBldgResult.startsWith('✓') ? '#15803d' : '#dc2626', fontWeight: 600, fontSize: '0.9rem' }}>{newBldgResult}</span>}
        </div>

        </React.Fragment>
        )}
      </div>

      {/* PDF Measurements — admin only */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showPdfs ? 12 : 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937' }}>📄 קבצי PDF מדידות — לניהול בלבד</div>
          <button onClick={() => setShowPdfs(v => !v)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', color: '#6b7280', cursor: 'pointer' }}>
            {showPdfs ? 'הסתר' : 'הצג'}
          </button>
        </div>
        {showPdfs && (
        <React.Fragment>
        <div
          onDragOver={e => { e.preventDefault(); setPdfDragging(true); }}
          onDragLeave={() => setPdfDragging(false)}
          onDrop={e => { e.preventDefault(); setPdfDragging(false); uploadBulkPdfs(e.dataTransfer.files); }}
          style={{ border: `2px dashed ${pdfDragging ? '#7c3aed' : '#d1d5db'}`, borderRadius: 10, padding: '20px 16px', textAlign: 'center', background: pdfDragging ? '#f5f3ff' : '#f9fafb', transition: 'all 0.2s', cursor: 'pointer', marginBottom: 12 }}
          onClick={() => document.getElementById('admin-pdf-input').click()}
        >
          <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>📂</div>
          <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>{pdfUploading ? 'מעלה...' : 'גרור קבצי PDF לכאן או לחץ לבחירה'}</div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 4 }}>ניתן לבחור מספר קבצים</div>
          <input id="admin-pdf-input" type="file" accept=".pdf,application/pdf" multiple style={{ display: 'none' }}
            onChange={e => { uploadBulkPdfs(e.target.files); e.target.value = ''; }} />
        </div>
        {bulkPdfs.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {bulkPdfs.map(pdf => (
              <div key={pdf.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px' }}>
                <span style={{ fontSize: '1rem' }}>📄</span>
                <span style={{ flex: 1, fontSize: '0.82rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pdf.original_name}</span>
                <a href={pdf.url} target="_blank" rel="noopener noreferrer" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, padding: '3px 10px', fontSize: '0.78rem', textDecoration: 'none', whiteSpace: 'nowrap', fontWeight: 600 }}>צפה</a>
                <button onClick={() => downloadCrossOriginFile(pdf.url, pdf.original_name)} style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, padding: '3px 10px', fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 }}>הורד</button>
                <button onClick={() => deleteBulkPdf(pdf.id)} title="מחק" style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1rem', padding: '2px 4px', borderRadius: 4, flexShrink: 0, lineHeight: 1 }}>🗑</button>
              </div>
            ))}
          </div>
        )}
        </React.Fragment>
        )}
      </div>

      {/* Building floorplans — used to draw the tenant's "your apartment plan" arrow */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showFloorplans ? 12 : 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937' }}>🗺️ ניהול תוכניות בניין (לחץ "התוכנית של הדירה שלך")</div>
          <button onClick={() => setShowFloorplans(v => !v)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', color: '#6b7280', cursor: 'pointer' }}>
            {showFloorplans ? 'הסתר' : 'הצג'}
          </button>
        </div>
        {showFloorplans && (
          <>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
              <input value={fpBuildingNumbers} onChange={e => setFpBuildingNumbers(e.target.value)} placeholder="מספרי בניינים, מופרדים בפסיק (למשל 1,2)" style={{ ...inputStyle, width: 260 }} />
              <label style={{ cursor: 'pointer', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, padding: '7px 14px', fontSize: '0.82rem', fontWeight: 600 }}>
                {fpUploading ? 'מעלה...' : '📤 העלה תוכנית'}
                <input type="file" accept="image/*" hidden disabled={fpUploading} onChange={e => { uploadFloorplan(e.target.files[0]); e.target.value = ''; }} />
              </label>
            </div>
            {floorplans.length === 0 && <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>אין תוכניות עדיין</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {floorplans.map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px' }}>
                  <img src={p.image_url} alt="" style={{ width: 56, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1f2937' }}>בניינים: {p.building_numbers}</div>
                    <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>{p.markers?.length || 0} אותיות מסומנות</div>
                  </div>
                  <button onClick={() => setEditingFloorplan(p)} style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, padding: '5px 12px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>🎯 סמן אותיות</button>
                  <button onClick={() => deleteFloorplan(p.id)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1rem' }}>🗑</button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {editingFloorplan && (
        <Modal title={`סימון אותיות — בניינים ${editingFloorplan.building_numbers}`} onClose={() => setEditingFloorplan(null)}>
          <FloorplanMarkerEditor plan={editingFloorplan} onSave={saveFloorplanMarkers} onCancel={() => setEditingFloorplan(null)} />
        </Modal>
      )}

      {/* Per-tenant apartment details */}
      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20, alignItems: 'start' }}>
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937', marginBottom: 10 }}>🔍 בחר דייר</div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="חיפוש לפי שם או טלפון..." style={{ ...inputStyle, marginBottom: 10 }} />
          <div style={{ maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {filtered.map(t => (
              <button key={`${t.id}-${t.buildingAddress}-${t.buildingNumber}-${t.apartmentNumber}`} onClick={() => selectTenant(t)}
                style={{ textAlign: 'right', background: selectedTenant?.id === t.id ? '#f5f3ff' : '#f9fafb', border: selectedTenant?.id === t.id ? '1px solid #c4b5fd' : '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}>
                <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1f2937' }}>{t.firstName} {t.lastName}</div>
                <div style={{ fontSize: '0.76rem', color: '#6b7280', marginTop: 2 }}>{t.buildingAddress} {t.buildingNumber} | דירה {t.apartmentNumber}{t.cellularPhone ? ` | ${t.cellularPhone}` : ''}</div>
              </button>
            ))}
            {filtered.length === 0 && <div style={{ color: '#9ca3af', textAlign: 'center', padding: 16 }}>אין תוצאות</div>}
          </div>
        </div>

        <div style={cardStyle}>
          {selectedTenant ? (
            <>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937', marginBottom: 4 }}>🏡 נתוני הדירה — {selectedTenant.firstName} {selectedTenant.lastName}</div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 16 }}>{selectedTenant.cellularPhone} | {selectedTenant.buildingAddress} דירה {selectedTenant.apartmentNumber}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                {APT_FIELDS.filter(f => {
                  // TAMA buildings at ברוך קרוא 11/20/22/24 don't include parking — hide those fields for them.
                  const isTamaNoParking = (selectedTenant.buildingAddress || '').replace(/\s+/g, '') === 'ברוךקרוא'
                    && ['11', '20', '22', '24'].includes(String(selectedTenant.buildingNumber || '').trim());
                  return !(isTamaNoParking && (f.key === 'current_parking' || f.key === 'parking_tabu'));
                }).map(f => (
                  <div key={f.key} style={f.col === 'both' ? { gridColumn: '1 / -1' } : {}}>
                    <label style={labelStyle}>{f.label}</label>
                    <input value={aptForm[f.key] || ''} onChange={e => setAptForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={compPlaceholder(f.key, aptForm, projectComp)} style={inputStyle} />
                  </div>
                ))}
              </div>
              <button onClick={saveApt} disabled={aptSaving} style={{ background: '#1B2A4A', color: 'white', border: 'none', padding: '9px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                {aptSaved ? '✓ נשמר' : aptSaving ? 'שומר...' : 'שמור נתוני דירה'}
              </button>
            </>
          ) : (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>בחר דייר מהרשימה לעריכת נתוני דירתו</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tasks Tab ────────────────────────────────────────────────────────────────

function TasksTab({ projectName }) {
  const { role, name: workerName } = getWorkerInfo();
  const [tasks, setTasks] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [form, setForm] = useState({ title: '', description: '', assignee_type: 'worker', assignee_name: '', assignee_phone: '', selectedTenants: [] });
  const [completeModal, setCompleteModal] = useState(null); // task id
  const [completedBy, setCompletedBy] = useState('');
  const [saving, setSaving] = useState(false);
  const [editTask, setEditTask] = useState(null); // { id, title, description }
  const [editTaskForm, setEditTaskForm] = useState({ title: '', description: '' });

  const enc = encodeURIComponent(projectName);

  useEffect(() => {
    api.get(`/portal/tasks/${enc}`).then(r => setTasks(r.data || [])).catch(() => {});
    api.get('/portal/workers').then(r => setWorkers(r.data || [])).catch(() => {});
    api.get(`/portal/tenants-list/${enc}`).then(r => setTenants(r.data || [])).catch(() => {});
  }, [projectName]);

  const filteredWorkers = workers.filter(w => role === 'admin' ? true : w.role !== 'admin');

  const searchWords = tenantSearch.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const filteredTenants = searchWords.length > 0
    ? tenants.filter(t => {
        const full = `${t.firstName||''} ${t.lastName||''}`.toLowerCase();
        return searchWords.every(w => full.includes(w));
      }).slice(0, 8)
    : [];

  async function addTask() {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (form.assignee_type === 'tenant' && form.selectedTenants?.length > 0) {
        const newTasks = [];
        for (const t of form.selectedTenants) {
          const res = await api.post(`/portal/tasks/${enc}`, {
            ...form, assignee_name: t.name, assignee_phone: t.phone, assignee_address: t.address, created_by: workerName,
          });
          newTasks.push(res.data);
        }
        setTasks(p => [...newTasks, ...p]);
      } else {
        const res = await api.post(`/portal/tasks/${enc}`, { ...form, created_by: workerName });
        setTasks(p => [res.data, ...p]);
      }
      setForm({ title: '', description: '', assignee_type: 'worker', assignee_name: '', assignee_phone: '', selectedTenants: [] });
      setTenantSearch('');
    } catch {}
    setSaving(false);
  }

  async function completeTask(id) {
    await api.patch(`/portal/tasks/${id}/complete`, { completed_by: completedBy || workerName });
    setTasks(p => p.map(t => t.id === id ? { ...t, status: 'done', completed_by: completedBy || workerName, completed_at: new Date().toISOString() } : t));
    setCompleteModal(null);
    setCompletedBy('');
  }

  async function reopenTask(id) {
    await api.patch(`/portal/tasks/${id}/reopen`, {});
    setTasks(p => p.map(t => t.id === id ? { ...t, status: 'pending', completed_by: null, completed_at: null } : t));
  }

  async function deleteTask(id) {
    if (!window.confirm('למחוק משימה?')) return;
    await api.delete(`/portal/tasks/${id}`);
    setTasks(p => p.filter(t => t.id !== id));
  }

  async function saveEditTask() {
    if (!editTask || !editTaskForm.title.trim()) return;
    await api.patch(`/portal/tasks/${editTask.id}/edit`, { title: editTaskForm.title, description: editTaskForm.description });
    setTasks(p => p.map(t => t.id === editTask.id ? { ...t, title: editTaskForm.title, description: editTaskForm.description } : t));
    setEditTask(null);
  }

  const pending = tasks.filter(t => t.status === 'pending');
  const done = tasks.filter(t => t.status === 'done');

  const card = { background: 'white', borderRadius: 12, padding: '14px 18px', border: '1px solid #e5e7eb', marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
  const inp = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box', fontFamily: 'inherit', direction: 'rtl' };

  return (
    <div style={{ direction: 'rtl' }}>
      {/* Add task form */}
      <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '1px solid #e5e7eb', marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 14, color: '#1B2A4A' }}>➕ הוספת משימה</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>כותרת *</label>
            <input style={inp} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="כותרת המשימה" />
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>תיאור (לא חובה)</label>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 60 }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="תיאור המשימה..." />
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 8 }}>הקצה ל</label>
            <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
              {['worker', 'tenant'].map(type => (
                <label key={type} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: form.assignee_type === type ? 700 : 400 }}>
                  <input type="radio" checked={form.assignee_type === type} onChange={() => setForm(p => ({ ...p, assignee_type: type, assignee_name: '', assignee_phone: '' }))} />
                  {type === 'worker' ? 'עובד / מנהל' : 'דייר'}
                </label>
              ))}
            </div>
            {form.assignee_type === 'worker' && (
              <select style={inp} value={form.assignee_name} onChange={e => setForm(p => ({ ...p, assignee_name: e.target.value }))}>
                <option value="">בחר עובד...</option>
                {filteredWorkers.map(w => (
                  <option key={w.userName} value={`${w.workerFirstName} ${w.workerLastName}`}>
                    {w.workerFirstName} {w.workerLastName}{w.role === 'admin' ? ' (אדמין)' : ''}
                  </option>
                ))}
              </select>
            )}
            {form.assignee_type === 'tenant' && (
              <TenantMultiSearch
                projectName={projectName}
                selectedTenants={form.selectedTenants || []}
                onAdd={t => setForm(p => ({ ...p, selectedTenants: [...(p.selectedTenants || []), t] }))}
                onRemove={phone => setForm(p => ({ ...p, selectedTenants: (p.selectedTenants || []).filter(t => t.phone !== phone) }))}
              />
            )}
          </div>
        </div>
        <button onClick={addTask} disabled={saving || !form.title.trim() || (form.assignee_type === 'tenant' && (form.selectedTenants||[]).length === 0)} style={{ marginTop: 14, background: '#1B2A4A', color: 'white', border: 'none', padding: '9px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, opacity: (!form.title.trim() || saving || (form.assignee_type === 'tenant' && (form.selectedTenants||[]).length === 0)) ? 0.6 : 1 }}>
          {saving ? 'שומר...' : 'הוסף משימה'}
        </button>
      </div>

      {/* Pending tasks */}
      <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1B2A4A', marginBottom: 10 }}>⏳ ממתינות ({pending.length})</div>
      {pending.length === 0 && <div style={{ color: '#9ca3af', marginBottom: 20, fontSize: '0.9rem' }}>אין משימות ממתינות</div>}
      {pending.map(t => (
        <div key={t.id} style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: 2 }}>{t.title}</div>
              {t.description && <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 4 }}>{t.description}</div>}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                {t.assignee_name && <span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 99, padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600 }}>👤 {t.assignee_name}</span>}
                <span style={{ color: '#9ca3af', fontSize: '0.78rem' }}>{new Date(t.created_at).toLocaleDateString('he-IL')}</span>
                {t.created_by && <span style={{ color: '#9ca3af', fontSize: '0.78rem' }}>נוצר ע"י {t.created_by}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button onClick={() => { setEditTask(t); setEditTaskForm({ title: t.title, description: t.description || '' }); }} style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '5px 8px', cursor: 'pointer', fontSize: '0.82rem' }}>✏️</button>
              <button onClick={() => { setCompleteModal(t.id); setCompletedBy(workerName); }} style={{ background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>✓ בוצע</button>
              <button onClick={() => deleteTask(t.id)} style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 8, padding: '5px 8px', cursor: 'pointer', fontSize: '0.82rem' }}>🗑</button>
            </div>
          </div>
        </div>
      ))}

      {/* Done tasks */}
      {done.length > 0 && (
        <>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1B2A4A', margin: '20px 0 10px' }}>✅ בוצעו ({done.length})</div>
          {done.map(t => (
            <div key={t.id} style={{ ...card, opacity: 0.85 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#374151', textDecoration: 'line-through', marginBottom: 2 }}>{t.title}</div>
                  {t.description && <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: 4 }}>{t.description}</div>}
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                    {t.assignee_name && <span style={{ background: '#f3f4f6', color: '#6b7280', borderRadius: 99, padding: '2px 10px', fontSize: '0.78rem' }}>👤 {t.assignee_name}</span>}
                    {t.completed_by && <span style={{ background: '#dcfce7', color: '#15803d', borderRadius: 99, padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600 }}>בוצע ע"י {t.completed_by}</span>}
                    {t.completed_at && <span style={{ color: '#9ca3af', fontSize: '0.78rem' }}>{new Date(t.completed_at).toLocaleDateString('he-IL')}</span>}
                  </div>
                </div>
                <button onClick={() => reopenTask(t.id)} style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: '0.82rem', flexShrink: 0 }}>↩ פתח</button>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Complete modal */}
      {completeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: 360, direction: 'rtl' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 16 }}>מי אישר את המשימה?</div>
            <input style={{ ...inp, marginBottom: 16 }} value={completedBy} onChange={e => setCompletedBy(e.target.value)} placeholder="שם המאשר" />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => completeTask(completeModal)} style={{ flex: 1, background: '#15803d', color: 'white', border: 'none', padding: '9px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>אשר ביצוע</button>
              <button onClick={() => setCompleteModal(null)} style={{ flex: 1, background: '#f3f4f6', color: '#374151', border: 'none', padding: '9px 0', borderRadius: 8, cursor: 'pointer' }}>ביטול</button>
            </div>
          </div>
        </div>
      )}
      {editTask && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: 400, direction: 'rtl' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 16 }}>✏️ עריכת משימה</div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>כותרת *</label>
              <input style={inp} value={editTaskForm.title} onChange={e => setEditTaskForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>תיאור</label>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: 60 }} value={editTaskForm.description} onChange={e => setEditTaskForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={saveEditTask} style={{ flex: 1, background: '#1B2A4A', color: 'white', border: 'none', padding: '9px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>שמור</button>
              <button onClick={() => setEditTask(null)} style={{ flex: 1, background: '#f3f4f6', color: '#374151', border: 'none', padding: '9px 0', borderRadius: 8, cursor: 'pointer' }}>ביטול</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Dashboard Tab ────────────────────────────────────────────────────────────

const ACTION_LABELS = {
  tenant_login: '🚪 כניסה לפורטל',
  // The eye emoji (👁) rendered as a broken "?" glyph in some environments —
  // swapped for the magnifying glass, which is already used and confirmed
  // to render fine elsewhere in this same panel (e.g. the tenant-lookup tab icon).
  tenant_tab_view: '🔎 צפייה בטאב',
  worker_tab_view: '🔎 צפייה בטאב (עובד)',
  tenant_file_view: '📄 פתיחת קובץ',
  tenant_file_download: '⬇ הורדת קובץ',
  worker_login: '👤 כניסה לפאנל',
  announcement_created: '📢 הודעה נוצרה',
  announcement_deleted: '🗑 הודעה נמחקה',
  file_uploaded: '📁 קובץ הועלה',
  file_deleted: '🗑 קובץ נמחק',
  task_created: '✅ משימה נוצרה',
  task_completed: '✅ משימה הושלמה',
  task_reopened: '↩ משימה נפתחה',
  stage_created: '📋 שלב נוצר',
  stage_updated: '✏️ שלב עודכן',
  gallery_uploaded: '📷 תמונה הועלתה',
  contact_created: '📞 איש קשר נוסף',
};

// ── New Compensations Tab ────────────────────────────────────────────────────
const FLOOR_OPTIONS = [
  { value: 'middle_third', label: 'שליש אמצעי של הבניין' },
  { value: 'addition',     label: 'תוספת קומות על הקומה הנוכחית' },
  { value: 'pending',      label: 'טרם נקבע' },
  { value: 'agreement',    label: 'בהתאם לתנאי ההסכם' },
  { value: 'floor_other',  label: 'אחר' },
];

function NewCompTab({ projectName }) {
  const [form, setForm] = useState({ size_addition:'', floor_type:'', new_floor:'', balcony_addition:'', parking_tabu:'', storage_addition:'' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get(`/portal/project-comp/${encodeURIComponent(projectName)}`).then(r => {
      const d = r.data || {};
      setForm({ size_addition: d.size_addition||'', floor_type: d.floor_type||'', new_floor: d.new_floor||'', balcony_addition: d.balcony_addition||'', parking_tabu: d.parking_tabu||'', storage_addition: d.storage_addition||'' });
    }).catch(() => {});
  }, [projectName]);

  const save = async () => {
    setSaving(true);
    await api.post(`/portal/project-comp/${encodeURIComponent(projectName)}`, form).catch(() => {});
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const lblStyle = { fontSize:'0.78rem', fontWeight:600, color:'#6b7280', display:'block', marginBottom:4 };
  const inpStyle = { width:'100%', border:'1px solid #e5e7eb', borderRadius:8, padding:'8px 12px', fontSize:'0.9rem', boxSizing:'border-box' };
  const selStyle = { width:'100%', border:'1px solid #e5e7eb', borderRadius:8, padding:'7px 10px', fontSize:'0.83rem', boxSizing:'border-box', marginBottom:6 };

  const numField = (key, label, noteText) => {
    const val = form[key] ?? '';
    const mode = val === 'בהתאם לתנאי ההסכם' ? 'agreement' : val === '__other__' || (val !== '' && isNaN(Number(val)) && val !== 'בהתאם לתנאי ההסכם') ? 'other' : 'number';
    const otherVal = mode === 'other' && val !== '__other__' ? val : '';
    return (
      <div>
        <label style={lblStyle}>{label}</label>
        <select value={mode} onChange={e => {
          const m = e.target.value;
          setForm(p => ({...p, [key]: m === 'agreement' ? 'בהתאם לתנאי ההסכם' : m === 'other' ? '__other__' : ''}));
        }} style={selStyle}>
          <option value="number">ערך מספרי</option>
          <option value="agreement">בהתאם לתנאי ההסכם</option>
          <option value="other">אחר</option>
        </select>
        {mode === 'number' && <>
          <input type="number" step="1" value={val === '' ? '' : parseInt(val) || ''} onChange={e => setForm(p => ({...p, [key]: e.target.value ? String(parseInt(e.target.value)) : ''}))} placeholder={noteText} style={inpStyle} />
          <div style={{ fontSize:'0.72rem', color:'#d1d5db', marginTop:3 }}>{noteText}</div>
        </>}
        {mode === 'other' &&
          <input value={otherVal} onChange={e => setForm(p => ({...p, [key]: e.target.value || '__other__'}))} placeholder="הכנס ערך..." style={{ ...inpStyle, border:'1px solid #7c3aed' }} />
        }
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 620 }}>
      <div style={{ background:'white', borderRadius:14, padding:28 }}>
        <div style={{ fontWeight:700, fontSize:'1.05rem', marginBottom:6, color:'#1f2937' }}>✨ תמורה לדירה החדשה — {displayProjectName(projectName)}</div>
        <div style={{ fontSize:'0.8rem', color:'#9ca3af', marginBottom:24 }}>הערכים שיוגדרו כאן יוצגו לכל דייר לפי הנתונים הנוכחיים שלו</div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:18 }}>
          {numField('size_addition', 'תוספת שטח דירה (מ"ר)', 'יחובר לשטח הנוכחי של כל דייר')}

          {/* קומה */}
          <div>
            <label style={lblStyle}>קומה חדשה</label>
            <select value={form.floor_type} onChange={e => setForm(p => ({...p, floor_type: e.target.value, new_floor: ''}))}
              style={{ width:'100%', border:'1px solid #e5e7eb', borderRadius:8, padding:'8px 12px', fontSize:'0.85rem', boxSizing:'border-box', color: form.floor_type ? '#1f2937' : '#9ca3af' }}>
              <option value="">בחר אפשרות...</option>
              {FLOOR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {form.floor_type === 'addition' && (
              <div style={{ marginTop:8 }}>
                <input type="number" value={form.new_floor} onChange={e => setForm(p => ({...p, new_floor: e.target.value}))}
                  placeholder="מספר הקומות להוסיף לקומה הנוכחית"
                  style={{ width:'100%', border:'1px solid #7c3aed', borderRadius:8, padding:'8px 12px', fontSize:'0.88rem', boxSizing:'border-box' }} />
                <div style={{ fontSize:'0.72rem', color:'#7c3aed', marginTop:3 }}>לדוגמה: קומה נוכחית 3 + 2 = קומה 5</div>
              </div>
            )}
            {form.floor_type === 'floor_other' && (
              <input value={form.new_floor} onChange={e => setForm(p => ({...p, new_floor: e.target.value}))}
                placeholder="הכנס ערך חופשי..."
                style={{ width:'100%', border:'1px solid #7c3aed', borderRadius:8, padding:'8px 12px', fontSize:'0.88rem', boxSizing:'border-box', marginTop:8 }} />
            )}
          </div>

          {numField('balcony_addition', 'שטח מרפסת (מ"ר)', 'ערך קבוע לכל הדיירים')}
          {numField('parking_tabu', 'חנייה בטאבו (חדש)', 'יחובר לחנייה הנוכחית של כל דייר')}
          {numField('storage_addition', 'שטח מחסן בטאבו (מ"ר)', 'ערך קבוע לכל הדיירים')}
        </div>

        <button onClick={save} disabled={saving}
          style={{ marginTop:24, background: saved ? '#15803d' : '#7c3aed', color:'white', border:'none', borderRadius:10, padding:'10px 32px', fontWeight:700, fontSize:'0.9rem', cursor:'pointer' }}>
          {saved ? '✓ נשמר' : saving ? 'שומר...' : 'שמור תמורות'}
        </button>
      </div>
    </div>
  );
}

function DashboardTab({ projectName }) {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [activitySearch, setActivitySearch] = useState('');
  const [tenantMsgs, setTenantMsgs] = useState([]);
  const [showMsgs, setShowMsgs] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const todayStr = new Date().toISOString().slice(0, 10);
  const [statsDate, setStatsDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);
  const [aiQuestions, setAiQuestions] = useState([]);
  const fmtDay = d => new Date(d).toLocaleDateString('he-IL');
  const rangeText = statsDate === toDate
    ? (statsDate === todayStr ? 'היום' : `ב-${fmtDay(statsDate)}`)
    : `מ-${fmtDay(statsDate)} עד ${fmtDay(toDate)}`;
  const dayAgo = n => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
  const pickRange = (from, to) => { setStatsDate(from); setToDate(to); };
  const rangeBtn = active => ({ padding: '5px 12px', borderRadius: 8, border: '1px solid ' + (active ? '#1a4a8a' : '#d1d5db'), background: active ? '#1a4a8a' : 'white', color: active ? 'white' : '#374151', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' });

  const load = useCallback(async () => {
    const enc = encodeURIComponent(projectName);
    const range = `from=${statsDate}&to=${toDate}`;
    const [s, a, msgs, ai] = await Promise.all([
      api.get(`/portal/dashboard-stats/${enc}?${range}`).then(r => r.data).catch(() => null),
      api.get(`/portal/activity/${enc}?${range}`).then(r => r.data).catch(() => []),
      api.get(`/portal/tenant-messages/${enc}`).then(r => r.data).catch(() => []),
      api.get(`/portal/ai-questions/${enc}?${range}`).then(r => r.data).catch(() => []),
    ]);
    setAiQuestions(ai);
    setStats(s);
    setActivity(a);
    setTenantMsgs(msgs);
  }, [projectName, statsDate, toDate]);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const kpi = [
    { label: `כניסות דיירים ${rangeText}`, value: stats?.loginsToday ?? '—', emoji: '🚪' },
    { label: 'משימות פתוחות', value: stats?.openTasks ?? '—', emoji: '✅' },
    { label: 'הודעות', value: stats?.announcements ?? '—', emoji: '📢' },
    { label: 'קבצים במערכת', value: stats?.files ?? '—', emoji: '📁' },
  ];

  const card = { background: 'white', borderRadius: 14, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };

  // Messages marked "נקרא" (done) or "בוצע" (completed) move out of the active
  // list entirely and into the archive — only "new"/"review" stay in view.
  const activeMsgs = tenantMsgs.filter(m => !['done', 'completed'].includes(m.msg_status || 'new'));
  const archivedMsgs = tenantMsgs.filter(m => ['done', 'completed'].includes(m.msg_status || 'new'));
  const unreadMsgs = activeMsgs;

  async function setMsgStatus(id, status) {
    await api.patch(`/portal/tenant-messages/${id}/status`, { status }).catch(() => {});
    setTenantMsgs(prev => prev.map(m => m.id === id ? { ...m, msg_status: status, is_read: status === 'new' ? 0 : 1 } : m));
  }

  // answer a tenant message: saved, and the tenant gets an SMS with a link to read it after login
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  async function sendReply(m) {
    if (!replyText.trim() || replySending) return;
    setReplySending(true);
    try {
      const r = await api.post(`/portal/tenant-messages/${m.id}/reply`, { reply: replyText.trim() });
      setTenantMsgs(prev => prev.map(x => x.id === m.id ? { ...x, reply_text: replyText.trim(), reply_by: r.data.reply_by, replied_at: new Date().toISOString().slice(0, 19).replace('T', ' '), reply_seen_at: null } : x));
      setReplyOpen(null); setReplyText('');
      alert(r.data.smsSent ? 'התשובה נשמרה ונשלח SMS לדייר' : 'התשובה נשמרה, אבל לא הצלחנו לשלוח SMS לדייר');
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בשליחת התשובה');
    } finally { setReplySending(false); }
  }

  async function deleteMsg(id) {
    if (!window.confirm('למחוק את ההודעה?')) return;
    await api.delete(`/portal/tenant-messages/${id}`).catch(() => {});
    setTenantMsgs(prev => prev.filter(m => m.id !== id));
  }

  const STATUS_META = {
    new:       { label: 'טרם בוצע', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
    review:    { label: 'בבדיקה',   color: '#92400e', bg: '#fffbeb', border: '#fcd34d' },
    done:      { label: 'נקרא',     color: '#15803d', bg: '#f0fdf4', border: '#86efac' },
    completed: { label: 'בוצע',     color: '#1d4ed8', bg: '#eff6ff', border: '#93c5fd' },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Badge הודעות מדיירים */}
      {tenantMsgs.length > 0 && (
        <div style={{ background: unreadMsgs.length > 0 ? '#eff6ff' : 'white', borderRadius: 14, padding: '14px 20px', border: unreadMsgs.length > 0 ? '2px solid #1a4a8a' : '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <span style={{ fontWeight: 700, color: '#1a4a8a' }}>✉️ הודעות מדיירים</span>
            {unreadMsgs.length > 0 && (
              <span style={{ marginRight: 10, background: '#1a4a8a', color: 'white', borderRadius: 12, padding: '2px 10px', fontSize: '0.85rem', fontWeight: 700 }}>
                {unreadMsgs.length} חדשות
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowMsgs(true)} style={{ background: '#1a4a8a', color: 'white', border: 'none', borderRadius: 10, padding: '7px 16px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
              צפה בהודעות ({activeMsgs.length})
            </button>
            <button onClick={() => setShowArchive(true)} style={{ background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 10, padding: '7px 16px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
              🗄 ארכיון ({archivedMsgs.length})
            </button>
          </div>
        </div>
      )}

      {/* Modal הודעות */}
      {showMsgs && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '80vh', overflowY: 'auto', direction: 'rtl' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1a4a8a' }}>✉️ הודעות מדיירים</div>
              <button onClick={() => setShowMsgs(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            {activeMsgs.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#9ca3af' }}>אין הודעות פעילות</p>
            ) : activeMsgs.map(m => {
              const st = m.msg_status || (m.is_read ? 'done' : 'new');
              const meta = STATUS_META[st] || STATUS_META.new;
              return (
                <div key={m.id} style={{ background: meta.bg, borderRadius: 12, padding: '14px 16px', marginBottom: 10, border: `1.5px solid ${meta.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, color: '#1a4a8a' }}>{m.from_name || m.from_phone}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ background: meta.color, color: 'white', borderRadius: 20, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700 }}>{meta.label}</span>
                      <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>{new Date(m.created_at + 'Z').toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jerusalem' })}</span>
                    </div>
                  </div>
                  {m.to_worker && <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 4 }}>אל: {m.to_worker}</div>}
                  {m.parent_id && (() => { const par = tenantMsgs.find(x => x.id === m.parent_id); return par ? (
                    <div style={{ background: 'rgba(255,255,255,.7)', borderRadius: 8, padding: '6px 10px', marginBottom: 8, fontSize: '0.8rem', color: '#6b7280', borderInlineStart: '3px solid #93c5fd' }}>
                      <div>↪ תגובה להמשך שיחה. ההודעה הקודמת: {par.message}</div>
                      {par.reply_text && <div style={{ marginTop: 2 }}>התשובה שלך: {par.reply_text}</div>}
                    </div>) : null; })()}
                  <div style={{ color: '#374151', fontSize: '0.95rem', lineHeight: 1.5, marginBottom: 10 }}>{m.message}</div>
                  {m.reply_text && (
                    <div style={{ background: 'white', border: '1px solid #bfdbfe', borderRadius: 10, padding: '8px 12px', marginBottom: 10 }}>
                      <div style={{ fontSize: '0.75rem', color: '#1d4ed8', fontWeight: 700, marginBottom: 3 }}>
                        התשובה שלך{m.reply_by ? ' (' + m.reply_by + ')' : ''} · {m.reply_seen_at ? 'הדייר קרא' : 'נשלח SMS, טרם נקרא'}
                      </div>
                      <div style={{ color: '#111827', fontSize: '0.92rem', whiteSpace: 'pre-wrap' }}>{m.reply_text}</div>
                    </div>
                  )}
                  {replyOpen === m.id && (
                    <div style={{ marginBottom: 10 }}>
                      <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={3} placeholder="כתוב תשובה לדייר..."
                        style={{ width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 10, border: '1.5px solid #93c5fd', fontFamily: 'inherit', fontSize: '0.92rem', resize: 'vertical' }} />
                      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <button onClick={() => sendReply(m)} disabled={replySending || !replyText.trim()}
                          style={{ background: '#1d4ed8', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontWeight: 700, cursor: 'pointer', opacity: (replySending || !replyText.trim()) ? 0.6 : 1 }}>
                          {replySending ? 'שולח...' : 'שלח תשובה + SMS לדייר'}
                        </button>
                        <button onClick={() => { setReplyOpen(null); setReplyText(''); }}
                          style={{ background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 14px', cursor: 'pointer' }}>ביטול</button>
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button onClick={() => { setReplyOpen(m.id); setReplyText(m.reply_text || ''); }}
                      style={{ border: '1.5px solid #1d4ed8', borderRadius: 8, padding: '3px 11px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, background: '#1d4ed8', color: 'white' }}>
                      {m.reply_text ? '💬 ערוך תשובה' : '💬 הגב'}
                    </button>
                    {[
                      { s: 'done',      label: '✅ נקרא',     bg: '#15803d' },
                      { s: 'review',    label: '🔍 בבדיקה',   bg: '#92400e' },
                      { s: 'completed', label: '☑️ בוצע',     bg: '#1d4ed8' },
                      { s: 'new',       label: '🔴 טרם בוצע', bg: '#dc2626' },
                    ].map(btn => (
                      <button key={btn.s} onClick={() => setMsgStatus(m.id, btn.s)}
                        style={{ border: `1.5px solid ${btn.bg}`, borderRadius: 8, padding: '3px 11px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                          background: st === btn.s ? btn.bg : 'white',
                          color: st === btn.s ? 'white' : btn.bg,
                          opacity: st === btn.s ? 1 : 0.75 }}>
                        {btn.label}
                      </button>
                    ))}
                    <button onClick={() => deleteMsg(m.id)}
                      style={{ border: '1.5px solid #9ca3af', borderRadius: 8, padding: '3px 11px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                        background: 'white', color: '#6b7280', marginRight: 'auto' }}>
                      🗑 מחק
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal ארכיון — הודעות שסומנו "נקרא" או "בוצע" */}
      {showArchive && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '80vh', overflowY: 'auto', direction: 'rtl' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1a4a8a' }}>🗄 ארכיון הודעות</div>
              <button onClick={() => setShowArchive(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            {archivedMsgs.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#9ca3af' }}>הארכיון ריק</p>
            ) : archivedMsgs.map(m => {
              const st = m.msg_status || 'done';
              const meta = STATUS_META[st] || STATUS_META.done;
              return (
                <div key={m.id} style={{ background: meta.bg, borderRadius: 12, padding: '14px 16px', marginBottom: 10, border: `1.5px solid ${meta.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, color: '#1a4a8a' }}>{m.from_name || m.from_phone}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ background: meta.color, color: 'white', borderRadius: 20, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700 }}>{meta.label}</span>
                      <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>{new Date(m.created_at + 'Z').toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jerusalem' })}</span>
                    </div>
                  </div>
                  {m.to_worker && <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 4 }}>אל: {m.to_worker}</div>}
                  <div style={{ color: '#374151', fontSize: '0.95rem', lineHeight: 1.5, marginBottom: 10 }}>{m.message}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button onClick={() => setMsgStatus(m.id, 'new')}
                      style={{ border: '1.5px solid #dc2626', borderRadius: 8, padding: '3px 11px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, background: 'white', color: '#dc2626' }}>
                      ↩ שחזר לפעיל
                    </button>
                    <button onClick={() => deleteMsg(m.id)}
                      style={{ border: '1.5px solid #9ca3af', borderRadius: 8, padding: '3px 11px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                        background: 'white', color: '#6b7280', marginRight: 'auto' }}>
                      🗑 מחק
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Date range filter — everything below reflects from–to (inclusive) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.82rem', color: '#6b7280', fontWeight: 600 }}>📅 הצג מתאריך:</span>
        <input type="date" value={statsDate} max={toDate} onChange={e => e.target.value && setStatsDate(e.target.value)} style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.82rem' }} />
        <span style={{ fontSize: '0.82rem', color: '#6b7280', fontWeight: 600 }}>עד תאריך:</span>
        <input type="date" value={toDate} min={statsDate} max={todayStr} onChange={e => e.target.value && setToDate(e.target.value)} style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.82rem' }} />
        <button onClick={() => pickRange(todayStr, todayStr)} style={rangeBtn(statsDate === todayStr && toDate === todayStr)}>היום</button>
        <button onClick={() => pickRange(dayAgo(1), dayAgo(1))} style={rangeBtn(statsDate === dayAgo(1) && toDate === dayAgo(1))}>אתמול</button>
        <button onClick={() => pickRange(dayAgo(6), todayStr)} style={rangeBtn(statsDate === dayAgo(6) && toDate === todayStr)}>7 ימים אחרונים</button>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        {kpi.map(k => (
          <div key={k.label} style={{ ...card, textAlign: 'center' }}>
            <div style={{ fontSize: '1.8rem' }}>{k.emoji}</div>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#1a4a8a' }}>{k.value}</div>
            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 4 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* שאלות AI של דיירים */}
      <div style={card}>
        <div style={{ fontWeight: 700, marginBottom: 12 }}>🤖 שאלות AI של דיירים {rangeText} ({aiQuestions.length})</div>
        {aiQuestions.length === 0 ? (
          <div style={{ color: '#9ca3af', textAlign: 'center', padding: 14 }}>אין שאלות בטווח שנבחר</div>
        ) : aiQuestions.map(q => (
          <div key={q.id} style={{ padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: '0.78rem', color: '#6b7280', marginBottom: 4 }}>
              <span style={{ fontWeight: 600, color: '#1a4a8a' }}>{q.name || q.phone}{q.building ? ` · ${q.building}` : ''}</span>
              <span>{new Date(q.created_at + 'Z').toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jerusalem' })}</span>
            </div>
            <div style={{ color: '#111827', fontWeight: 600 }}>{q.question}</div>
            {q.answer && (
              <details style={{ marginTop: 4 }}>
                <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: '#6b7280' }}>תשובת העוזר</summary>
                <div style={{ marginTop: 4, color: '#374151', fontSize: '0.88rem', whiteSpace: 'pre-wrap' }}>{q.answer}</div>
              </details>
            )}
          </div>
        ))}
      </div>

      {/* Worker logins today */}
      {stats?.workerLoginsToday?.length > 0 && (
        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>👤 כניסות עובדים {rangeText}</div>
          {stats.workerLoginsToday.map((w, i) => {
            const d = (() => { try { return JSON.parse(w.details); } catch { return {}; } })();
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < stats.workerLoginsToday.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                <span style={{ fontWeight: 600 }}>{w.actor_name}</span>
                <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>{d.role === 'admin' ? 'אדמין' : 'מנהל פרויקט'}</span>
                <span style={{ color: '#9ca3af', fontSize: '0.82rem' }}>{new Date(w.created_at + 'Z').toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jerusalem' })}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Activity log */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontWeight: 700 }}>📋 פעולות {statsDate === todayStr && toDate === todayStr ? 'אחרונות' : rangeText}</div>
          <input
            type="text"
            value={activitySearch}
            onChange={e => setActivitySearch(e.target.value)}
            placeholder="חיפוש לפי שם דייר או בניין..."
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '0.85rem', minWidth: 220 }}
          />
        </div>
        {(() => {
          const normalize = s => (s || '').replace(/\s+/g, '').toLowerCase();
          // Match each typed word independently (not as one contiguous phrase) — e.g.
          // "מייזל 7" should find building "מייזלזלמן 7" even though "זלמן" sits
          // between them once spaces are stripped from the stored building string.
          const queryWords = activitySearch.trim().split(/\s+/).filter(Boolean).map(normalize);
          const filtered = queryWords.length
            ? activity.filter(a => {
                const haystack = normalize(a.actor_name) + normalize(a.building);
                return queryWords.every(w => haystack.includes(w));
              })
            : activity;
          if (filtered.length === 0) {
            return <div style={{ color: '#9ca3af', textAlign: 'center', padding: 20 }}>{queryWords.length ? 'לא נמצאו תוצאות' : 'אין פעולות עדיין'}</div>;
          }
          // Two explicit lines (label+time+badge, then detail+building) instead
          // of one flex row with 5 loosely-wrapping items — on a narrow phone
          // that row wrapped each item onto its own line in an order that read
          // as a jumble; this stays readable at any width.
          return filtered.map((a, i) => {
            const detail = (() => {
              try {
                const d = JSON.parse(a.details);
                const extra = (d.tab || d.name || d.title || '').replace(/^\S+\s+(?=\S)/, m => /[a-zA-Zא-ת0-9]/.test(m) ? m : '');
                const actorLabel = d.phone && a.actor_name !== d.phone ? `${a.actor_name} (${d.phone})` : a.actor_name;
                return extra ? `${actorLabel} - ${extra}` : actorLabel;
              } catch { return a.actor_name; }
            })() || '—';
            return (
            <div key={a.id} style={{ padding: '9px 0', borderBottom: i < filtered.length - 1 ? '1px solid #f9fafb' : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>{ACTION_LABELS[a.action_type] || a.action_type}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span style={{ color: '#9ca3af', fontSize: '0.78rem' }}>{new Date(a.created_at + 'Z').toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jerusalem' })}</span>
                  <span style={{ background: a.actor_type === 'tenant' ? '#dbeafe' : '#f3f4f6', color: a.actor_type === 'tenant' ? '#1d4ed8' : '#6b7280', borderRadius: 99, padding: '1px 8px', fontSize: '0.72rem' }}>{a.actor_type === 'tenant' ? 'דייר' : 'עובד'}</span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
                <span style={{ color: '#374151', fontSize: '0.82rem' }}>{detail}</span>
                {a.building && <span style={{ color: '#6b7280', fontSize: '0.78rem', flexShrink: 0 }}>🏢 {a.building}</span>}
              </div>
            </div>
            );
          });
        })()}
      </div>

      {/* Open tasks by assignee */}
      {stats?.openTasksByAssignee?.length > 0 && (
        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>📌 משימות פתוחות לפי נציג</div>
          {stats.openTasksByAssignee.map((row, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < stats.openTasksByAssignee.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
              <span>{row.assignee_name || 'לא מוקצה'}</span>
              <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 99, padding: '2px 12px', fontWeight: 700 }}>{row.c}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Portal Page ──────────────────────────────────────────────────────────────

export default function Portal() {
  const { projectName } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const decodedName = decodeURIComponent(projectName);
  const initialTab = TABS.find(t => t.id === searchParams.get('tab'))?.id || 'stages';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [filesInitCat, setFilesInitCat] = useState('all');

  // Draggable floating AI button — was fixed at bottom-left with no way to
  // move it, so on a phone browser it could sit on top of content with no
  // way to nudge it aside. Same drag mechanism as the tenant portal's own
  // floating AI button (position persisted per-device via localStorage).
  const [aiBtnPos, setAiBtnPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('adminAiChatBtnPos')) || { bottom: 24, left: 24 }; }
    catch { return { bottom: 24, left: 24 }; }
  });
  const aiDragRef = useRef(null);
  const aiWasDragRef = useRef(false);
  const aiBtnRef = useRef(null);

  function onAiBtnPointerDown(e) {
    aiDragRef.current = { startX: e.clientX, startY: e.clientY, startBottom: aiBtnPos.bottom, startLeft: aiBtnPos.left, moved: false, curLeft: aiBtnPos.left, curBottom: aiBtnPos.bottom };
    e.currentTarget.setPointerCapture(e.pointerId);
    window.addEventListener('pointerup', onAiBtnPointerUp);
    window.addEventListener('pointercancel', onAiBtnPointerUp);
  }
  function onAiBtnPointerMove(e) {
    if (!aiDragRef.current) return;
    e.preventDefault();
    const dx = e.clientX - aiDragRef.current.startX;
    const dy = e.clientY - aiDragRef.current.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) aiDragRef.current.moved = true;
    if (!aiDragRef.current.moved) return;
    const newLeft = Math.max(4, Math.min(window.innerWidth - 60, aiDragRef.current.startLeft + dx));
    const newBottom = Math.max(4, Math.min(window.innerHeight - 60, aiDragRef.current.startBottom - dy));
    aiDragRef.current.curLeft = newLeft;
    aiDragRef.current.curBottom = newBottom;
    if (aiBtnRef.current) {
      aiBtnRef.current.style.left = `${newLeft}px`;
      aiBtnRef.current.style.bottom = `${newBottom}px`;
    }
  }
  function onAiBtnPointerUp() {
    if (!aiDragRef.current) return;
    aiWasDragRef.current = !!aiDragRef.current.moved;
    if (aiWasDragRef.current) {
      const p = { left: aiDragRef.current.curLeft, bottom: aiDragRef.current.curBottom };
      localStorage.setItem('adminAiChatBtnPos', JSON.stringify(p));
      setAiBtnPos(p);
    }
    aiDragRef.current = null;
    window.removeEventListener('pointerup', onAiBtnPointerUp);
    window.removeEventListener('pointercancel', onAiBtnPointerUp);
  }

  function changeTab(id) {
    setActiveTab(id);
    navigate(`/admin/${projectName}?tab=${id}`, { replace: true });
    // TABS labels carry a leading emoji (e.g. "📊 דשבורד פעילות") for the tab
    // strip itself — that emoji renders as a broken "?" glyph in some fonts
    // once it lands in the activity feed as plain text, so strip the leading
    // non-letter icon+space before logging, not just the letters after it.
    const tabLabel = (TABS.find(t => t.id === id)?.label || id).replace(/^\S+\s+/, '');
    api.post('/portal/log-worker-activity', {
      workerName: getWorkerInfo().name,
      projectName: decodedName,
      actionType: 'worker_tab_view',
      details: { tab: tabLabel },
    }).catch(() => {});
  }

  function goToFilesWithCat(cat) {
    setFilesInitCat(cat);
    changeTab('files');
  }

  return (
    <div>
      <Navbar title={`פורטל דיירים — ${displayProjectName(decodedName)}`} />
      <div className="container">
        {/* Back button */}
        <div style={{ display: 'flex', marginBottom: 16 }}>
          <button
            onClick={() => navigate(`/project-hub/${projectName}`)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '0.9rem', padding: 0, marginRight: 'auto' }}
          >
            ← חזרה
          </button>
        </div>

        {/* Tabs — a loose flex-wrap of variable-width pills looked ragged on a
            narrow phone (uneven row breaks, no alignment); a fixed 2-column
            grid at mobile widths keeps every pill the same size and aligned. */}
        <div className="admin-tabs-row" style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {TABS.filter(tab => (!tab.adminOnly || getWorkerInfo().role === 'admin')).map(tab => (
            <button
              key={tab.id}
              onClick={() => changeTab(tab.id)}
              className="admin-tab-pill"
              style={{
                padding: '8px 18px', borderRadius: 99, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem',
                background: activeTab === tab.id ? 'var(--primary, #1B2A4A)' : '#f3f4f6',
                color: activeTab === tab.id ? '#fff' : '#374151',
                transition: 'background .2s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'dashboard' && <DashboardTab projectName={decodedName} />}
        {activeTab === 'home' && <HomeTab projectName={decodedName} />}
        {activeTab === 'apartment' && <ApartmentTab projectName={decodedName} onGoToFiles={goToFilesWithCat} />}
        {activeTab === 'new-comp' && <NewCompTab projectName={decodedName} />}
        {activeTab === 'stages' && <StagesTab projectName={decodedName} logo={PROJECT_LOGOS[decodedName]} />}
        {activeTab === 'announcements' && <AnnouncementsTab projectName={decodedName} />}
        {activeTab === 'gallery' && <GalleryTab projectName={decodedName} />}
        {activeTab === 'contacts' && <ContactsTab projectName={decodedName} />}
        {activeTab === 'files' && <FilesTab projectName={decodedName} initialFilterCat={filesInitCat} />}
        {activeTab === 'tenant-lookup' && <TenantLookupTab projectName={decodedName} />}
        {activeTab === 'tasks' && <TasksTab projectName={decodedName} />}
        {activeTab === 'about-manhelet' && <AboutManheletTab projectName={decodedName} />}
        {activeTab === 'about' && <AboutTab projectName={decodedName} />}
        {activeTab === 'ai-chat' && <AIChatTab projectName={decodedName} />}
      </div>

      {activeTab !== 'ai-chat' && (
        <button
          ref={aiBtnRef}
          onClick={() => { if (!aiWasDragRef.current) changeTab('ai-chat'); }}
          onPointerDown={onAiBtnPointerDown}
          onPointerMove={onAiBtnPointerMove}
          onPointerUp={onAiBtnPointerUp}
          title="עוזר AI"
          style={{
            position: 'fixed', bottom: aiBtnPos.bottom, left: aiBtnPos.left, zIndex: 9999,
            width: 56, height: 56, borderRadius: '50%', border: 'none', cursor: 'pointer',
            padding: 0, overflow: 'hidden',
            boxShadow: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'transform .2s',
            touchAction: 'none',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <img
            src="https://aura-tenant-portal-files.s3.us-east-1.amazonaws.com/home-images/1788079441278-iakp5i51gfe.png"
            alt=""
            draggable={false}
            onDragStart={e => e.preventDefault()}
            style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
          />
        </button>
      )}
    </div>
  );
}

function RagDocsPanel({ projectName }) {
  const [docs, setDocs] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  React.useEffect(() => {
    api.get(`/portal/admin/rag-docs/${encodeURIComponent(projectName)}`)
      .then(r => setDocs(r.data))
      .catch(() => {});
  }, [projectName]);

  async function uploadDoc(file, docType) {
    if (!file) return;
    setUploading(true);
    setMsg('מעלה קובץ...');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('projectName', projectName);
      fd.append('docType', docType);
      fd.append('docName', file.name);
      const r0 = await api.post('/portal/admin/ingest-pdf', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { jobId } = r0.data;
      // Poll until done
      let attempts = 0;
      while (attempts < 120) {
        await new Promise(res => setTimeout(res, 3000));
        attempts++;
        try {
          const st = await api.get(`/portal/admin/ingest-status/${jobId}`);
          const job = st.data;
          if (job.status === 'done') {
            setMsg(`✅ הועלה בהצלחה — ${job.chunks} צ'אנקים`);
            break;
          } else if (job.status === 'error') {
            setMsg('❌ שגיאה: ' + job.error);
            break;
          } else {
            const pct = job.total > 0 ? Math.round((job.progress / job.total) * 100) : 0;
            setMsg(`⏳ מעבד... ${job.progress}/${job.total} צ'אנקים (${pct}%)`);
          }
        } catch {}
      }
      if (attempts >= 120) setMsg('❌ פג הזמן — בדוק את השרת');
      const r2 = await api.get(`/portal/admin/rag-docs/${encodeURIComponent(projectName)}`);
      setDocs(r2.data);
    } catch (e) {
      setMsg('❌ שגיאה: ' + (e.response?.data?.error || e.message));
    }
    setUploading(false);
  }

  const DOC_TYPES = [
    { type: 'מפרט_טכני', label: '📋 מפרט טכני' },
    { type: 'הסכם_פינוי_בינוי', label: '📄 הסכם פינוי בינוי' },
    // TAMA reinforcement-and-additions agreement is specific to ג' החדשה's
    // ברוך קרוא 11/20/22/24 buildings — showing it in every project's doc
    // list was misleading admins elsewhere into thinking it applied to them.
    ...(projectName === 'ג החדשה' ? [{ type: 'הסכם_פינוי_בינוי_חיזוק', label: '📄 הסכם חיזוק ותוספות (ברוך קרוא 11, 20, 22, 24)' }] : []),
  ];

  return (
    <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, marginBottom: 16, border: '1px solid #e5e7eb' }}>
      <div style={{ fontWeight: 700, marginBottom: 12, color: '#1f2937' }}>📚 מסמכי בסיס ידע לצ'אט</div>
      {DOC_TYPES.map(({ type, label }) => {
        const indexed = docs.find(d => d.doc_type === type);
        return (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ minWidth: 180, fontSize: '0.9rem', color: '#374151' }}>{label}</span>
            {indexed ? (
              <span style={{ fontSize: '0.8rem', color: '#15803d', background: '#f0fdf4', borderRadius: 6, padding: '2px 8px' }}>
                ✅ {indexed.chunks} צ'אנקים | {new Date(indexed.updated_at).toLocaleDateString('he-IL')}
              </span>
            ) : (
              <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>לא הועלה</span>
            )}
            <label style={{ cursor: uploading ? 'not-allowed' : 'pointer', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 12px', fontSize: '0.8rem', fontWeight: 600 }}>
              {uploading ? 'מעבד...' : '⬆ העלה PDF'}
              <input type="file" accept=".pdf" hidden disabled={uploading} onChange={e => { if (e.target.files[0]) uploadDoc(e.target.files[0], type); e.target.value = ''; }} />
            </label>
          </div>
        );
      })}
      {msg && <div style={{ fontSize: '0.85rem', marginTop: 8, color: msg.startsWith('✅') ? '#15803d' : '#dc2626' }}>{msg}</div>}
    </div>
  );
}

function AIChatTab({ projectName }) {
  const { role } = getWorkerInfo();
  const storageKey = `ai-chat-${projectName}`;
  const [messages, setMessages] = React.useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : [{ role: 'assistant', text: `שלום! אני העוזר החכם לפרויקט "${displayProjectName(projectName)}". אני מחובר לנתונים בזמן אמת.\nאפשר לשאול אותי על דיירים, שטחי דירות, חתימות, הודעות, שלבים, משימות ועוד.` }];
    } catch { return [{ role: 'assistant', text: `שלום! אני העוזר החכם לפרויקט "${displayProjectName(projectName)}".` }]; }
  });
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [showRag, setShowRag] = React.useState(false);
  const bottomRef = React.useRef(null);

  React.useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(messages.slice(-50))); } catch {}
  }, [messages, storageKey]);

  React.useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  async function send() {
    const q = input.trim();
    if (!q || loading) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', text: q }]);
    setLoading(true);
    try {
      const r = await api.post('/portal/ai-chat', { question: q, projectName, history: messages.slice(-10) });
      setMessages(m => [...m, { role: 'assistant', text: r.data.answer }]);
    } catch {
      setMessages(m => [...m, { role: 'assistant', text: 'שגיאה — נסה שוב.' }]);
    }
    setLoading(false);
  }

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)' }}>
      {role === 'admin' && (
        <div style={{ marginBottom: 8 }}>
          <button onClick={() => setShowRag(v => !v)}
            style={{ background: 'white', border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 14px', fontSize: '0.82rem', cursor: 'pointer', color: '#374151', fontWeight: 600 }}>
            {showRag ? '▲ הסתר מסמכים' : '📚 ניהול מסמכי בסיס ידע'}
          </button>
          {showRag && <RagDocsPanel projectName={projectName} />}
        </div>
      )}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-start' : 'flex-end' }}>
            <div style={{
              maxWidth: '80%', padding: '10px 14px', borderRadius: 14, fontSize: '0.9rem', lineHeight: 1.6, whiteSpace: 'pre-wrap',
              background: m.role === 'user' ? '#1B2A4A' : 'white',
              color: m.role === 'user' ? 'white' : '#1f2937',
              border: m.role === 'assistant' ? '1px solid #e5e7eb' : 'none',
              borderBottomRightRadius: m.role === 'user' ? 4 : 14,
              borderBottomLeftRadius: m.role === 'assistant' ? 4 : 14,
            }}>{m.text.split(/(\*\*[^*]+\*\*)/).map((seg, k) =>
                  seg.startsWith('**') && seg.endsWith('**')
                    ? <strong key={k}>{seg.slice(2, -2)}</strong>
                    : seg
                )}</div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 14, padding: '10px 16px', color: '#9ca3af', fontSize: '0.85rem' }}>⏳ חושב...</div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <div style={{ display: 'flex', gap: 8, padding: '12px 0', borderTop: '1px solid #e5e7eb' }}>
        <input
          value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
          placeholder="שאל שאלה על הפרויקט..."
          style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid #d1d5db', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit' }}
          disabled={loading}
        />
        <button onClick={send} disabled={loading || !input.trim()}
          style={{ background: '#1B2A4A', color: 'white', border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>
          שלח
        </button>
        <button onClick={() => { const init = [{ role: 'assistant', text: `שלום! אני העוזר החכם לפרויקט "${displayProjectName(projectName)}".` }]; setMessages(init); localStorage.setItem(storageKey, JSON.stringify(init)); }}
          title="נקה שיחה"
          style={{ background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 10, padding: '10px 12px', cursor: 'pointer', fontSize: '1rem' }}>
          🗑
        </button>
      </div>
    </div>
  );
}
